import { GoalConference } from '../../../components/coaching/GoalConference';
import { useGoalDraftRecovery } from '../../../hooks/useGoalDraftRecovery';
import { useOptionalAuth } from '../../../hooks/useAuth';
import { previewLocation, previewNotice } from '../../../services/studentPreview';
import { reviewRecoveryKey } from '../../../utils/reviewDraftRecovery';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WorkEvidencePanel } from '../../../components/coaching/WorkEvidencePanel';
import { Link, useLocation } from 'react-router-dom';
import { assignmentsApi, exportApi, goalsApi, hooksV2Api, lessonRunsApi, nsrApi, performanceReviewApi, sectionsApi, studentActionQueueApi } from '../../../services/apiV2';
import {
  FiveWeekPerformanceReviewPayload,
  GoalFinalReflection,
  GoalEvidencePayload,
  GoalMilestone,
  InstructionalCycle,
  InstructionalCheckIn,
  GoalPolicy,
  GoalAiSuggestion,
  PrimaryGoalAiReview,
  DailyGoalAiNudge,
  DailyGoalCycle,
  CurrentFocus,
  ExternalWorkBlock,
  ExternalWorkEvent,
  Hook,
  LessonRun,
  NSRCyclePayload,
  Section,
  StudentLongerTermDirection,
  StudentPrimaryGoal,
  WeeklyGoalCheckpoint,
  WeeklyGoalCheckpointAvailability,
} from '../../../types/v2';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { OptionalAppShell } from '../../../components/layout/AppShell';
import { PageHeader } from '../../../components/layout/PageHeader';
import { SectionCard } from '../../../components/layout/SectionCard';
import { studentV2NavItems } from '../../../components/layout/v2NavItems';
import { HelpTab } from '../../../components/v2/student/HelpTab';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { cn } from '../../../lib/cn';

const STUDENT_DASHBOARD_ASSIGNMENTS_REFRESH_MS = 15000;

type CatalogStandard = {
  id: number; official_code: string; official_title: string; official_text: string;
  pathway_key: string; pathway_title: string; source_page: number;
  content_code?: string; content_title?: string; performance_code?: string; performance_title?: string;
};

type CatalogContentGroup = {
  pathway_key: string; pathway_title: string; content_code: string; content_title: string;
  performance_standards: Array<{ performance_code: string; performance_title: string; standards: CatalogStandard[] }>;
};

const PracticeStandardsExplorer: React.FC<{
  goal: StudentPrimaryGoal;
  onChange: (standards: NonNullable<StudentPrimaryGoal['practice_standards']>) => void;
}> = ({ goal, onChange }) => {
  const [pathways, setPathways] = useState<Array<{ key: string; title: string; standard_count: number }>>([]);
  const [pathway, setPathway] = useState('');
  const [query, setQuery] = useState('');
  const [hierarchy, setHierarchy] = useState<CatalogContentGroup[]>([]);
  const [suggestions, setSuggestions] = useState<CatalogStandard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const selected = goal.practice_standards || [];

  useEffect(() => {
    Promise.all([goalsApi.primaryGoal.standardsCatalog(), goalsApi.primaryGoal.standardSuggestions(goal.id)])
      .then(([catalog, suggested]) => {
        setPathways(catalog?.pathways || []);
        setSuggestions((suggested?.suggestions || []).map((row: any) => ({ ...row, id: row.standard_id })));
      }).catch(() => setError('Standards are temporarily unavailable.'));
  }, [goal.id]);

  const search = async () => {
    try {
      setLoading(true); setError('');
      const payload = await goalsApi.primaryGoal.standardsCatalog({ pathway: pathway || undefined, q: query.trim() || undefined });
      setHierarchy(payload?.hierarchy || []);
    } catch { setError('Could not search standards. Try again.'); }
    finally { setLoading(false); }
  };

  const add = async (standardId: number) => {
    try {
      setError('');
      const focus = await goalsApi.primaryGoal.practiceStandards.add(goal.id, standardId);
      onChange([...selected.filter((row) => row.id !== focus.id), focus]);
    } catch (err: any) { setError(err?.response?.data?.error === 'practice_standard_limit_reached' ? 'Choose up to three practice standards.' : 'Could not add that standard.'); }
  };

  const remove = async (focusId: number) => {
    try {
      setError('');
      await goalsApi.primaryGoal.practiceStandards.remove(goal.id, focusId);
      onChange(selected.filter((row) => row.id !== focusId));
    } catch { setError('Could not remove that standard.'); }
  };

  return <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50/60 p-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div><p className="text-sm font-semibold text-text">Standards I’m practicing</p><p className="mt-1 text-sm text-muted">Explore Idaho CTE standards for awareness and evidence planning. Official assessment remains in HeadRush.</p></div>
      <span className="rounded-full bg-white px-2 py-1 text-xs text-sky-800">{selected.length}/3 selected</span>
    </div>
    {selected.length > 0 && <div className="mt-3 space-y-2">{selected.map((row) => <div key={row.id} className="rounded-lg border border-sky-200 bg-white p-3 text-sm"><div className="flex items-start justify-between gap-3"><div><p className="text-xs font-semibold text-sky-800">{row.pathway_title} · Content Standard {row.content_code}: {row.content_title}</p><p className="mt-1 font-semibold text-sky-950">{row.official_code} · {row.performance_title}</p><p className="mt-1 text-slate-700">{row.official_text}</p><p className="mt-1 text-xs text-slate-500">Practicing — not an official grade or mastery decision</p></div><button type="button" className="text-xs font-semibold text-rose-700" onClick={() => void remove(row.id)}>Remove</button></div></div>)}</div>}
    {suggestions.length > 0 && <div className="mt-4"><p className="text-xs font-semibold uppercase tracking-wide text-sky-900">Suggested for this goal</p><div className="mt-2 grid gap-2 lg:grid-cols-3">{suggestions.map((row) => { const chosen = selected.some((item) => item.standard_id === row.id); return <div key={row.id} className="rounded-lg border border-sky-200 bg-white p-3 text-sm"><p className="text-xs font-semibold text-sky-800">{row.content_code}: {row.content_title}</p><p className="mt-1 font-semibold">{row.official_code} · {row.official_title}</p><p className="mt-1 text-slate-700">{row.official_text}</p><button type="button" disabled={chosen || selected.length >= 3} className="mt-2 rounded border border-sky-300 px-3 py-1 text-xs font-semibold text-sky-800 disabled:opacity-50" onClick={() => void add(row.id)}>{chosen ? 'Selected' : 'Practice this'}</button></div>; })}</div></div>}
    <p className="mt-4 text-xs text-slate-600">Browse by pathway, open a main Content Standard, then open its supporting Performance Standards.</p>
    <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
      <select aria-label="Standards pathway" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" value={pathway} onChange={(event) => setPathway(event.target.value)}><option value="">All pathways</option>{pathways.map((row) => <option key={row.key} value={row.key}>{row.title} ({row.standard_count})</option>)}</select>
      <input aria-label="Search standards" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search hardware, networking, Python…" onKeyDown={(event) => { if (event.key === 'Enter') void search(); }} />
      <button type="button" className="rounded-lg bg-sky-700 px-4 py-2 text-sm font-semibold text-white" onClick={() => void search()} disabled={loading}>{loading ? 'Searching…' : 'Browse standards'}</button>
    </div>
    {error && <p role="alert" className="mt-2 text-sm font-medium text-rose-700">{error}</p>}
    {hierarchy.length > 0 && <div className="mt-3 max-h-[32rem] space-y-2 overflow-y-auto">{hierarchy.map((content) => <details key={`${content.pathway_key}:${content.content_code}`} className="rounded-lg border border-slate-200 bg-white p-3"><summary className="cursor-pointer font-semibold text-slate-900">Content Standard {content.content_code}: {content.content_title}</summary><div className="mt-2 space-y-2 pl-2">{content.performance_standards.map((performance) => <details key={performance.performance_code} className="rounded border border-slate-200 bg-slate-50 p-2"><summary className="cursor-pointer text-sm font-semibold">Performance Standard {performance.performance_code}: {performance.performance_title}</summary><div className="mt-2 space-y-2">{performance.standards.map((row) => { const chosen = selected.some((item) => item.standard_id === row.id); return <div key={row.id} className="rounded border border-slate-200 bg-white p-2 text-sm"><div className="flex items-start justify-between gap-3"><p><span className="font-semibold">{row.official_code}</span> {row.official_text}</p><button type="button" disabled={chosen || selected.length >= 3} className="shrink-0 rounded border border-sky-300 px-2 py-1 text-xs font-semibold text-sky-800 disabled:opacity-50" onClick={() => void add(row.id)}>{chosen ? 'Selected' : 'Practice this'}</button></div></div>; })}</div></details>)}</div></details>)}</div>}
  </div>;
};

type AssignmentSummary = {
  assignment_id: number;
  lesson_plan_id: number;
  lesson_plan_title: string;
  section_id: number;
  section_name: string;
  assigned_at: string;
  due_at: string | null;
  student_status: 'not_started' | 'in_progress' | 'completed' | 'excused';
  makeup_review?: { status: 'pending' | 'needs_revision' | 'approved'; note: string } | null;
  purpose?: 'makeup' | 'assessment' | 'practice';
  assessment_stage?: string;
  hook_level?: string;
  target_summary?: string;
};

const HOOK_LEVEL_ORDER: Record<string, number> = {
  define: 0,
  do: 1,
  explain: 2,
  apply: 3,
};

const assignmentHookLevel = (assignment: AssignmentSummary) => {
  const value = String(assignment.assessment_stage || assignment.hook_level || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(HOOK_LEVEL_ORDER, value) ? value : '';
};

const compareAssignmentsByHookLevel = (left: AssignmentSummary, right: AssignmentSummary) => {
  const leftLevel = assignmentHookLevel(left);
  const rightLevel = assignmentHookLevel(right);
  const leftOrder = leftLevel ? HOOK_LEVEL_ORDER[leftLevel] : Number.MAX_SAFE_INTEGER;
  const rightOrder = rightLevel ? HOOK_LEVEL_ORDER[rightLevel] : Number.MAX_SAFE_INTEGER;
  if (leftOrder !== rightOrder) return leftOrder - rightOrder;

  const leftAssignedAt = new Date(left.assigned_at || 0).getTime();
  const rightAssignedAt = new Date(right.assigned_at || 0).getTime();
  if (leftAssignedAt !== rightAssignedAt) return rightAssignedAt - leftAssignedAt;
  return right.assignment_id - left.assignment_id;
};

type LearningGoalBuilder = {
  skill: string;
  action: string;
  proof: string;
  first_step: string;
};

type PlanTrack = '' | 'python' | 'comptia' | 'web_development' | 'main_project' | 'secondary_project' | 'missing_work' | 'other';

type CompletionWorkItem = {
  key: string;
  kind: 'internal_assignment' | 'assignment' | 'external_task' | 'lesson_task' | 'live_lesson';
  id: number | null;
  title: string;
  source: string;
  trackKey?: string;
  detail: string;
  status?: string;
};

type ProjectRole = '' | 'main_project' | 'secondary_project';

type StudentDashboardTab = 'today' | 'goals' | 'work' | 'portfolio' | 'help' | 'past';

const PROJECT_ROLE_OPTIONS: Array<{ value: ProjectRole; label: string }> = [
  { value: '', label: 'Not project work' },
  { value: 'main_project', label: 'Main project' },
  { value: 'secondary_project', label: 'Secondary project' },
];

const PLAN_TRACK_OPTIONS: Array<{ value: PlanTrack; label: string }> = [
  { value: '', label: 'Choose track' },
  { value: 'python', label: 'Python' },
  { value: 'comptia', label: 'CompTIA' },
  { value: 'web_development', label: 'Web Development' },
  { value: 'main_project', label: 'Main Project' },
  { value: 'secondary_project', label: 'Secondary Project' },
  { value: 'missing_work', label: 'Missing Work' },
  { value: 'other', label: 'Other / Custom' },
];

const parseTags = (value: string): string[] => (
  value
    .split(',')
    .map((item) => item.trim())
    .filter((item, idx, arr) => item.length > 0 && arr.indexOf(item) === idx)
);

const composeLearningGoalText = (
  skill: string,
  action: string,
  proof: string,
  firstStep: string,
): string => {
  const skillText = skill.trim();
  const actionText = action.trim();
  const proofText = proof.trim();
  const firstStepText = firstStep.trim();
  if (!skillText || !actionText || !proofText) return '';
  const sentence = `I will improve ${skillText} by ${actionText}. I will know it worked when ${proofText}.`;
  return firstStepText ? `${sentence} First step: ${firstStepText}.` : sentence;
};

const trackLabel = (track?: string): string => (
  PLAN_TRACK_OPTIONS.find((option) => option.value === track)?.label
  || String(track || '').replace(/_/g, ' ')
  || 'Daily work'
);

const usesSeniorPriorityWorkflow = (policy?: GoalPolicy | null): boolean => {
  if (policy?.workflow) return policy.workflow.mode === 'goal_led';
  const gradeLevel = String(policy?.grade_level || '').trim().toLowerCase();
  return Boolean(
    policy?.daily_priority_enabled
    && (policy?.preset_key === 'grades_11_12' || ['11', '11th', '12', '12th'].includes(gradeLevel)),
  );
};

// Map plan tracks to work types for the Start Work flow
const getWorkType = (track?: string): string => {
  switch (track) {
    case 'python':
    case 'comptia':
      return 'certification';
    case 'web_development':
      return 'course_work';
    case 'main_project':
    case 'secondary_project':
      return track === 'main_project' ? 'main_project' : 'secondary_project';
    case 'missing_work':
      return 'missing_work';
    default:
      return 'other';
  }
};

// Generate focus label from track and target
const getFocusLabel = (track?: string, target?: string): string => {
  if (!target) return 'Daily Work';

  // For certification tracks, use the track name as focus
  if (['python', 'comptia'].includes(track || '')) {
    return trackLabel(track);
  }

  // For course work, use the track name
  if (track === 'web_development') {
    return trackLabel(track);
  }

  // For projects, try to extract a meaningful name from target
  if (['main_project', 'secondary_project'].includes(track || '')) {
    // Extract first few words of target as project name
    const words = target.split(' ').slice(0, 3).join(' ');
    return words.length > 20 ? words.substring(0, 20) + '...' : words;
  }

  // For missing work, use a generic label
  if (track === 'missing_work') {
    return 'Missing Assignment';
  }

  // Default to target itself (truncated if too long)
  return target.length > 30 ? target.substring(0, 30) + '...' : target;
};

const workStatusLabel = (status?: string): string => {
  const value = String(status || 'not_started');
  if (value === 'in_progress' || value === 'active') return 'In Progress';
  if (value === 'not_started') return 'Not Started';
  if (value === 'checked_in') return 'Checked In';
  if (value === 'checked_out') return 'Checked Out';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const inferPlanWorkSourceApp = (track?: string, workItem?: Record<string, any> | null): string => {
  const source = String(workItem?.source || workItem?.source_app || '').trim();
  if (source) return source;
  switch (track) {
    case 'python':
      return 'Python Essentials';
    case 'comptia':
      return 'CompTIA';
    case 'web_development':
      return 'Web Development';
    case 'main_project':
    case 'secondary_project':
      return 'Project';
    case 'missing_work':
      return 'Elevate';
    default:
      return '';
  }
};

const composeLearningGoalTextFromPlan = (
  track: PlanTrack,
  target: string,
  successCriteria: string,
): string => {
  const targetText = target.trim();
  if (!track || !targetText) return '';
  const successText = successCriteria.trim();
  const sentence = `I am working on ${trackLabel(track)}: ${targetText}.`;
  return successText ? `${sentence} I will know I am done when ${successText}.` : sentence;
};

const composeCompletionGoalText = (
  manualText: string,
  selectedWorkItem?: CompletionWorkItem | null,
  projectRole: ProjectRole = '',
  firstStep = '',
  successCriteria = '',
): string => {
  const roleLabel = PROJECT_ROLE_OPTIONS.find((option) => option.value === projectRole)?.label || '';
  const roleSuffix = projectRole && roleLabel ? ` Project focus: ${roleLabel}.` : '';
  const firstStepSuffix = firstStep.trim() ? ` First step: ${firstStep.trim()}.` : '';
  const successSuffix = successCriteria.trim() ? ` Success check: ${successCriteria.trim()}.` : '';
  if (selectedWorkItem) {
    const sourceText = selectedWorkItem.source ? ` in ${selectedWorkItem.source}` : '';
    return `I will complete ${manualText.trim() || selectedWorkItem.title}${sourceText} today.${firstStepSuffix}${successSuffix}${roleSuffix}`;
  }
  const trimmedManualText = manualText.trim();
  return trimmedManualText ? `I will complete ${trimmedManualText} today.${firstStepSuffix}${successSuffix}${roleSuffix}` : '';
};

const formatDuration = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const assignmentStatusBadgeVariant = (
  status: AssignmentSummary['student_status'],
): 'default' | 'success' | 'warning' | 'muted' => {
  if (status === 'completed') return 'success';
  if (status === 'in_progress') return 'warning';
  return 'muted';
};

const cycleStatusBadgeVariant = (statusValue?: string): 'success' | 'warning' | 'muted' => {
  if (statusValue === 'checked_out') return 'success';
  if (statusValue === 'checked_in') return 'warning';
  return 'muted';
};

const GoalsStat: React.FC<{
  label: string;
  value: React.ReactNode;
  detail: React.ReactNode;
}> = ({ label, value, detail }) => (
  <Card className="border-border/80 bg-surface-alt/50 shadow-none">
    <CardContent className="space-y-1 pt-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">{label}</p>
      <p className="text-2xl font-semibold tracking-tight text-text">{value}</p>
      <p className="text-sm text-muted">{detail}</p>
    </CardContent>
  </Card>
);

const FieldLabel: React.FC<{
  htmlFor?: string;
  children: React.ReactNode;
}> = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">
    {children}
  </label>
);

const externalTaskStatusBadgeVariant = (statusValue?: string): 'success' | 'warning' | 'muted' => {
  if (statusValue === 'completed') return 'success';
  if (statusValue === 'in_progress') return 'warning';
  return 'muted';
};

const workBlockStatusBadgeVariant = (statusValue?: string): 'success' | 'warning' | 'muted' => {
  if (statusValue === 'completed') return 'success';
  if (statusValue === 'active') return 'warning';
  return 'muted';
};

const inferTrackKey = (title?: string, source?: string): string => {
  const token = `${title || ''} ${source || ''}`.toLowerCase();
  if (token.includes('comptia') || token.includes('comp tia')) return 'comptia';
  if (token.includes('python')) return 'python_essentials';
  if (token.includes('project')) return 'project';
  return 'custom';
};

const structuredPlanFromCycle = (cycle?: DailyGoalCycle | null) => {
  const learningGoal = (cycle?.goals || []).find((goal) => goal.goal_type === 'learning');
  const completionGoal = (cycle?.goals || []).find((goal) => goal.goal_type === 'completion');
  const learningBuilder = (
    learningGoal?.builder_data && typeof learningGoal.builder_data === 'object'
      ? learningGoal.builder_data
      : {}
  ) as Record<string, any>;
  const completionBuilder = (
    completionGoal?.builder_data && typeof completionGoal.builder_data === 'object'
      ? completionGoal.builder_data
      : {}
  ) as Record<string, any>;
  const cycleWorkItem = cycle?.plan_work_item && typeof cycle.plan_work_item === 'object' && Object.keys(cycle.plan_work_item).length > 0
    ? cycle.plan_work_item
    : null;
  const selectedWorkItem = cycleWorkItem || (
    completionBuilder.selected_work_item && typeof completionBuilder.selected_work_item === 'object'
      ? completionBuilder.selected_work_item
      : null
  );
  return {
    track: String(cycle?.plan_track || completionBuilder.plan_track || ''),
    target: String(cycle?.plan_target || completionBuilder.manual_text || selectedWorkItem?.title || completionGoal?.smart_text || ''),
    firstStep: String(cycle?.plan_first_step || completionBuilder.first_step || learningBuilder.first_step || ''),
    successCriteria: String(cycle?.plan_success_criteria || completionBuilder.success_criteria || learningBuilder.proof || ''),
    workItemTitle: String(selectedWorkItem?.title || ''),
    hasStructuredPlan: Boolean(
      cycle?.plan_track
      || cycle?.plan_target
      || cycle?.plan_first_step
      || cycle?.plan_success_criteria
    ),
    legacyLearningText: String(learningGoal?.smart_text || ''),
    legacyCompletionText: String(completionGoal?.smart_text || ''),
  };
};

export const V2StudentDashboard: React.FC = () => {
  const auth = useOptionalAuth();
  const location = useLocation();
  const redesignEnabled = isUiRedesignPageEnabled('v2_student_dashboard');
  const gateFromNavigation = (location.state as any)?.gate;
  const [allHooks, setHooks] = useState<Hook[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [joinGrade, setJoinGrade] = useState('');
  const [joinGradeRequired, setJoinGradeRequired] = useState(false);
  const [joiningSection, setJoiningSection] = useState(false);
  const [joinSectionError, setJoinSectionError] = useState('');
  const [joinSectionSuccess, setJoinSectionSuccess] = useState('');
  const [availableActiveRun, setActiveRun] = useState<LessonRun | null>(null);
  const activeRun = availableActiveRun && Number(availableActiveRun.section) === activeSection ? availableActiveRun : null;
  const hooks = useMemo(() => allHooks.filter(hook => hook.assignments?.some(
    assignment => Number(assignment.section) === activeSection && (hook.is_archived || assignment.is_active),
  )), [allHooks, activeSection]);
  const [fixQueuePreview, setFixQueuePreview] = useState<{
    unresolved: number;
    items: Array<{
      id: number;
      lesson_step_title?: string;
      status?: string;
      suggestions?: string[];
    }>;
  } | null>(null);
  const [runHistory, setRunHistory] = useState<LessonRun[]>([]);
  const [allAssignments, setAssignments] = useState<AssignmentSummary[]>([]);
  const assignments = useMemo(() => allAssignments
    .filter(assignment => Number(assignment.section_id) === activeSection)
    .sort(compareAssignmentsByHookLevel), [allAssignments, activeSection]);
  const [goalCycle, setGoalCycle] = useState<DailyGoalCycle | null>(null);
  const [sectionSubgroup, setSectionSubgroup] = useState('');
  const [suggestedPlanTrack, setSuggestedPlanTrack] = useState<PlanTrack>('');
  const [goalHistory, setGoalHistory] = useState<DailyGoalCycle[]>([]);
  const [goalsError, setGoalsError] = useState('');
  const [focusStatusMessage, setFocusStatusMessage] = useState('');
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [loadingGoalHistory, setLoadingGoalHistory] = useState(false);
  const [primaryGoal, setPrimaryGoal] = useState<StudentPrimaryGoal | null>(null);
  const [primaryGoals, setPrimaryGoals] = useState<StudentPrimaryGoal[]>([]);
  const [selectedGoalTrack, setSelectedGoalTrack] = useState<'required_pathway' | 'personal_project'>('required_pathway');
  const [primaryGoalPurpose, setPrimaryGoalPurpose] = useState<'technical_cte' | 'personal_professional' | ''>('');
  const [primaryGoalEditing, setPrimaryGoalEditing] = useState(false);
  const [primaryGoalSaving, setPrimaryGoalSaving] = useState(false);
  const [primaryGoalError, setPrimaryGoalError] = useState('');
  const [primaryGoalTitle, setPrimaryGoalTitle] = useState('');
  const [primaryGoalWhy, setPrimaryGoalWhy] = useState('');
  const [primaryGoalMetric, setPrimaryGoalMetric] = useState('');
  const [primaryGoalStartDate, setPrimaryGoalStartDate] = useState('');
  const [primaryGoalEndDate, setPrimaryGoalEndDate] = useState('');
  const primaryGoalDraftDirtyRef = useRef(false);
  const [personalProjectTask, setPersonalProjectTask] = useState('');
  const [personalProjectProof, setPersonalProjectProof] = useState('');
  const [activePerformanceCycle, setActivePerformanceCycle] = useState<InstructionalCycle | null>(null);
  const goalDraft = useGoalDraftRecovery(
    !loadingGoals && activeSection && activePerformanceCycle ? reviewRecoveryKey(auth?.user?.id, `student-goal:${activeSection}:${activePerformanceCycle.id}:${selectedGoalTrack}`, 'goal') : '',
    { title: primaryGoalTitle, why: primaryGoalWhy, metric: primaryGoalMetric, purpose: primaryGoalPurpose, start: primaryGoalStartDate, end: primaryGoalEndDate },
    primaryGoalDraftDirtyRef.current,
    draft => {
      primaryGoalDraftDirtyRef.current = true;
      setPrimaryGoalTitle(draft.title); setPrimaryGoalWhy(draft.why); setPrimaryGoalMetric(draft.metric);
      setPrimaryGoalPurpose(draft.purpose); setPrimaryGoalStartDate(draft.start); setPrimaryGoalEndDate(draft.end);
      setPrimaryGoalEditing(true);
    });
  const [longerTermDirection, setLongerTermDirection] = useState<StudentLongerTermDirection | null>(null);
  const [directionEditing, setDirectionEditing] = useState(false);
  const [directionSaving, setDirectionSaving] = useState(false);
  const [directionError, setDirectionError] = useState('');
  const [directionTitle, setDirectionTitle] = useState('');
  const [directionWhy, setDirectionWhy] = useState('');
  const [directionMetric, setDirectionMetric] = useState('');
  const directionDraftDirtyRef = useRef(false);
  const planningFormActiveRef = useRef(false);
  const [milestones, setMilestones] = useState<GoalMilestone[]>([]);
  const [milestoneHookStage, setMilestoneHookStage] = useState<GoalMilestone['hook_stage']>('define');
  const [milestoneOutcome, setMilestoneOutcome] = useState('');
  const [milestoneDueDate, setMilestoneDueDate] = useState('');
  const [milestoneEvidenceExpected, setMilestoneEvidenceExpected] = useState(false);
  const [milestoneSaving, setMilestoneSaving] = useState(false);
  const [milestoneError, setMilestoneError] = useState('');
  const [goalEvidence, setGoalEvidence] = useState<GoalEvidencePayload | null>(null);
  const [goalEvidenceSource, setGoalEvidenceSource] = useState('');
  const [goalEvidenceMilestone, setGoalEvidenceMilestone] = useState('');
  const [goalEvidenceSaving, setGoalEvidenceSaving] = useState(false);
  const [goalEvidenceError, setGoalEvidenceError] = useState('');
  const [goalEvidenceExporting, setGoalEvidenceExporting] = useState(false);
  const [nsrCycle, setNsrCycle] = useState<NSRCyclePayload | null>(null);
  const [nsrPrimaryItem, setNsrPrimaryItem] = useState('');
  const [nsrSecondaryItem, setNsrSecondaryItem] = useState('');
  const [nsrReflection, setNsrReflection] = useState('');
  const [nsrSaving, setNsrSaving] = useState(false);
  const [nsrError, setNsrError] = useState('');
  const [instructionalCheckIns, setInstructionalCheckIns] = useState<InstructionalCheckIn[]>([]);
  const [primaryGoalReview, setPrimaryGoalReview] = useState<PrimaryGoalAiReview | null>(null);
  const [primaryGoalReviewEventId, setPrimaryGoalReviewEventId] = useState<number | null>(null);
  const [primaryGoalReviewLoading, setPrimaryGoalReviewLoading] = useState(false);
  const [primaryGoalReviewError, setPrimaryGoalReviewError] = useState('');
  const [goalExamplesOpen, setGoalExamplesOpen] = useState(false);
  const [goalExampleSaving, setGoalExampleSaving] = useState(false);
  const [goalExampleNotice, setGoalExampleNotice] = useState('');
  const goalExampleSourceRef = useRef<StudentPrimaryGoal | null>(null);
  const selectedGoalRef = useRef(primaryGoal);
  selectedGoalRef.current = primaryGoal;
  const [dailyNudge, setDailyNudge] = useState<DailyGoalAiNudge | null>(null);
  const [dailyNudgeEventId, setDailyNudgeEventId] = useState<number | null>(null);
  const [dailyNudgeLoading, setDailyNudgeLoading] = useState(false);
  const [dailyNudgeError, setDailyNudgeError] = useState('');
  const [activeTab, setActiveTab] = useState<StudentDashboardTab>('today');
  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (['today', 'goals', 'work', 'portfolio', 'help', 'past'].includes(requestedTab || '')) {
      setActiveTab(requestedTab as StudentDashboardTab);
    } else {
      setActiveTab('today');
    }
  }, [location.search]);
  const [goalPolicy, setGoalPolicy] = useState<GoalPolicy | null>(null);
  const [goalFinalReflection, setGoalFinalReflection] = useState<GoalFinalReflection | null>(null);
  const [reviewGoals, setReviewGoals] = useState<StudentPrimaryGoal[]>([]);
  const [reviewingGoalId, setReviewingGoalId] = useState<number | null>(null);
  const [goalClosureError, setGoalClosureError] = useState('');
  const [goalClosureSaving, setGoalClosureSaving] = useState(false);
  const [goalClosureOpen, setGoalClosureOpen] = useState(false);
  const [goalOutcomeStatus, setGoalOutcomeStatus] = useState<'met' | 'partially_met' | 'not_met'>('partially_met');
  const [goalEvidenceText, setGoalEvidenceText] = useState('');
  const [goalWhatWorkedText, setGoalWhatWorkedText] = useState('');
  const [goalObstacleText, setGoalObstacleText] = useState('');
  const [goalNextTimeChangeText, setGoalNextTimeChangeText] = useState('');
  const [goalNsrFocusReflectionText, setGoalNsrFocusReflectionText] = useState('');
  const [goalNextDirectionText, setGoalNextDirectionText] = useState('');
  const [performanceReview, setPerformanceReview] = useState<FiveWeekPerformanceReviewPayload | null>(null);
  const [weeklyCheckpoint, setWeeklyCheckpoint] = useState<WeeklyGoalCheckpoint | null>(null);
  const [weeklyCheckpointHistory, setWeeklyCheckpointHistory] = useState<WeeklyGoalCheckpoint[]>([]);
  const [weeklyCheckpointAvailability, setWeeklyCheckpointAvailability] = useState<WeeklyGoalCheckpointAvailability | null>(null);
  const [weeklyCheckpointWeekStart, setWeeklyCheckpointWeekStart] = useState('');
  const [weeklyCheckpointError, setWeeklyCheckpointError] = useState('');
  const [weeklyCheckpointSaving, setWeeklyCheckpointSaving] = useState(false);
  const [weeklyProgressText, setWeeklyProgressText] = useState('');
  const [weeklyEvidenceText, setWeeklyEvidenceText] = useState('');
  const [weeklyObstacleText, setWeeklyObstacleText] = useState('');
  const [weeklyAdjustmentText, setWeeklyAdjustmentText] = useState('');
  const [weeklyConfidenceRating, setWeeklyConfidenceRating] = useState(3);

  const [learningSkill, setLearningSkill] = useState('');
  const [learningAction, setLearningAction] = useState('');
  const [learningProof, setLearningProof] = useState('');
  const [firstStepText, setFirstStepText] = useState('');
  const [aiRefinedLearningGoalText, setAiRefinedLearningGoalText] = useState('');
  const [planTrack, setPlanTrack] = useState<PlanTrack>('');
  const [planTarget, setPlanTarget] = useState('');
  const [planSuccessCriteria, setPlanSuccessCriteria] = useState('');
  const [protectedGoalMinutes, setProtectedGoalMinutes] = useState('');
  const [plannedSecondaryWork, setPlannedSecondaryWork] = useState('');
  const [plannedWorkMode, setPlannedWorkMode] = useState('');
  const [selectedCompletionWorkKey, setSelectedCompletionWorkKey] = useState('');
  const [completionProjectRole, setCompletionProjectRole] = useState<ProjectRole>('');
  const [learningStageTag, setLearningStageTag] = useState('');
  const [learningTags, setLearningTags] = useState('');
  const [completionTags, setCompletionTags] = useState('');
  const [submittingCheckIn, setSubmittingCheckIn] = useState(false);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<GoalAiSuggestion | null>(null);
  const [aiSuggestionEventId, setAiSuggestionEventId] = useState<number | null>(null);
  const [aiRationale, setAiRationale] = useState('');
  const [aiApplied, setAiApplied] = useState(false);
  const [dailyGoalOptional, setDailyGoalOptional] = useState(false);

  // Start Work flow state
  const [showStartWorkTransition, setShowStartWorkTransition] = useState(false);
  const [workStatus, setWorkStatus] = useState<'not_started' | 'in_progress' | 'paused' | 'completed'>('not_started');
  const [focusEditing, setFocusEditing] = useState(false);
  const [focusKey, setFocusKey] = useState('individual_project');
  const [focusTitle, setFocusTitle] = useState('');
  const [focusSaving, setFocusSaving] = useState(false);

  const [externalTitle, setExternalTitle] = useState('');
  const [externalSource, setExternalSource] = useState('');
  const [externalCreating, setExternalCreating] = useState(false);
  const [externalWorkBlocks, setExternalWorkBlocks] = useState<ExternalWorkBlock[]>([]);
  const [externalWorkEvents, setExternalWorkEvents] = useState<ExternalWorkEvent[]>([]);
  const [switchCountToday, setSwitchCountToday] = useState(0);
  const [loadingExternalWork, setLoadingExternalWork] = useState(false);
  const [externalWorkActionLoading, setExternalWorkActionLoading] = useState(false);
  const [startWorkTaskId, setStartWorkTaskId] = useState('');
  const [startWorkTitle, setStartWorkTitle] = useState('');
  const [startWorkSource, setStartWorkSource] = useState('');
  const [startWorkPlannedMinutes, setStartWorkPlannedMinutes] = useState(30);
  const [blockEvidenceNote, setBlockEvidenceNote] = useState('');
  const [blockEvidenceLink, setBlockEvidenceLink] = useState('');
  const [switchTaskId, setSwitchTaskId] = useState('');
  const [switchTitle, setSwitchTitle] = useState('');
  const [switchSource, setSwitchSource] = useState('');
  const [switchPlannedMinutes, setSwitchPlannedMinutes] = useState(30);
  const [minutesDelta, setMinutesDelta] = useState(5);
  const [timerNow, setTimerNow] = useState(Date.now());
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0);
  const [countdownHidden, setCountdownHidden] = useState(() => window.localStorage.getItem('elevate-focus-countdown-hidden') === 'true');

  const [checkoutReflection, setCheckoutReflection] = useState('');
  const [checkoutGoalStatus, setCheckoutGoalStatus] = useState<'yes' | 'partial' | 'no' | ''>('');
  const [priorityResult, setPriorityResult] = useState<'met' | 'partially_met' | 'blocked' | 'changed_authentic_work' | 'ready_for_review' | ''>('');
  const [pathwayEvidenceText, setPathwayEvidenceText] = useState('');
  const [pathwayEvidenceUrl, setPathwayEvidenceUrl] = useState('');
  const [projectResult, setProjectResult] = useState<'met' | 'partially_met' | 'blocked' | 'changed_authentic_work' | 'ready_for_review' | ''>('');
  const [projectEvidenceText, setProjectEvidenceText] = useState('');
  const [projectEvidenceUrl, setProjectEvidenceUrl] = useState('');
  const [projectPortfolioCandidate, setProjectPortfolioCandidate] = useState(false);
  const [checkoutNextStep, setCheckoutNextStep] = useState('');
  const [planChangeReason, setPlanChangeReason] = useState('');
  const [planChangeNote, setPlanChangeNote] = useState('');
  const [movedTowardPrimaryGoal, setMovedTowardPrimaryGoal] = useState<'yes' | 'no' | ''>('');
  const [completionRating, setCompletionRating] = useState(3);
  const [confidenceRating, setConfidenceRating] = useState(3);
  const [submittingCheckOut, setSubmittingCheckOut] = useState(false);
  const sectionLoadRequestRef = useRef(0);

  const normalizeArray = (data: any) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  const activeWorkBlock = useMemo(
    () => externalWorkBlocks.find((block) => block.status === 'active') || null,
    [externalWorkBlocks],
  );
  const pausedWorkBlock = useMemo(
    () => externalWorkBlocks.find((block) => block.status === 'paused') || null,
    [externalWorkBlocks],
  );
  const currentWorkBlock = activeWorkBlock || pausedWorkBlock;
  const focusSupportStatusMessage = focusStatusMessage || (
    currentWorkBlock?.support_status === 'acknowledged'
      ? 'Your teacher has seen your help request and will follow up.'
      : currentWorkBlock?.support_status === 'requested'
        ? 'Help requested. Your teacher can see the request on the Goals roster.'
        : ''
  );
  const beginSectionLoadRequest = () => {
    sectionLoadRequestRef.current += 1;
    return sectionLoadRequestRef.current;
  };
  const isCurrentSectionLoadRequest = (requestId?: number) => (
    requestId == null || sectionLoadRequestRef.current === requestId
  );
  const activeWorkElapsedSeconds = useMemo(() => {
    if (!currentWorkBlock) return 0;
    const baseSeconds = Math.max(0, Math.floor(Number(currentWorkBlock.accumulated_seconds ?? Number(currentWorkBlock.actual_minutes || 0) * 60)));
    if (currentWorkBlock.status === 'paused') return baseSeconds;
    const startedAt = new Date(currentWorkBlock.started_at).getTime();
    if (Number.isNaN(startedAt)) return baseSeconds;
    const runtimeSeconds = Math.max(0, Math.floor((timerNow + serverClockOffsetMs - startedAt) / 1000));
    return baseSeconds + runtimeSeconds;
  }, [currentWorkBlock, serverClockOffsetMs, timerNow]);
  const activeWorkRemainingSeconds = useMemo(() => {
    const plannedSeconds = Number(currentWorkBlock?.planned_minutes || 0) * 60;
    return plannedSeconds > 0 ? Math.max(0, plannedSeconds - activeWorkElapsedSeconds) : null;
  }, [currentWorkBlock, activeWorkElapsedSeconds]);
  const loadExternalWork = async (
    sectionId: number,
    requestId?: number,
  ) => {
    if (!isCurrentSectionLoadRequest(requestId)) return;
    setLoadingExternalWork(true);
    try {
      const blockPayload = await goalsApi.externalWorkBlocks.list({ section_id: sectionId });
      if (!isCurrentSectionLoadRequest(requestId)) return;
      const blocks = Array.isArray(blockPayload?.blocks) ? blockPayload.blocks : [];
      if (blockPayload?.server_now) {
        const serverNowMs = new Date(blockPayload.server_now).getTime();
        if (!Number.isNaN(serverNowMs)) setServerClockOffsetMs(serverNowMs - Date.now());
      }
      const activeBlockId = Number(
        blockPayload?.active_block_id || blocks.find((block: ExternalWorkBlock) => block.status === 'active')?.id || 0,
      ) || null;
      setExternalWorkBlocks(blocks);
      setSwitchCountToday(Number(blockPayload?.switch_count_today || 0));
      if (activeBlockId) {
        const eventPayload = await goalsApi.externalWorkEvents.list({
          section_id: sectionId,
          block_id: activeBlockId,
          limit: 50,
        });
        if (!isCurrentSectionLoadRequest(requestId)) return;
        setExternalWorkEvents(Array.isArray(eventPayload?.events) ? eventPayload.events : []);
      } else {
        const eventPayload = await goalsApi.externalWorkEvents.list({
          section_id: sectionId,
          limit: 30,
        });
        if (!isCurrentSectionLoadRequest(requestId)) return;
        setExternalWorkEvents(Array.isArray(eventPayload?.events) ? eventPayload.events : []);
      }
    } catch (err: any) {
      if (!isCurrentSectionLoadRequest(requestId)) return;
      if (err?.response?.status === 409 && err?.response?.data?.error === 'goals_not_enabled_for_section_or_grade') {
        setExternalWorkBlocks([]);
        setExternalWorkEvents([]);
        setSwitchCountToday(0);
      } else {
        setGoalsError(err?.response?.data?.error || 'Failed to load external work timeline.');
      }
    } finally {
      if (isCurrentSectionLoadRequest(requestId)) {
        setLoadingExternalWork(false);
      }
    }
  };

  const loadGoalHistory = async (sectionId: number, limit = 14, requestId?: number) => {
    if (!isCurrentSectionLoadRequest(requestId)) return;
    setLoadingGoalHistory(true);
    try {
      const payload = await goalsApi.myHistory({ section_id: sectionId, limit });
      if (!isCurrentSectionLoadRequest(requestId)) return;
      setGoalHistory(Array.isArray(payload?.history) ? payload.history : []);
    } catch (err: any) {
      if (!isCurrentSectionLoadRequest(requestId)) return;
      if (err?.response?.status === 409 && err?.response?.data?.error === 'goals_not_enabled_for_section_or_grade') {
        setGoalHistory([]);
      } else {
        setGoalsError(err?.response?.data?.error || 'Failed to load goal history.');
      }
    } finally {
      if (isCurrentSectionLoadRequest(requestId)) {
        setLoadingGoalHistory(false);
      }
    }
  };

  const applyPrimaryGoalToForm = (goal: StudentPrimaryGoal | null) => {
    primaryGoalDraftDirtyRef.current = false;
    setPrimaryGoal(goal);
    setMilestones(Array.isArray(goal?.milestones) ? (goal?.milestones || []) : []);
    setPrimaryGoalTitle(String(goal?.title || ''));
    setPrimaryGoalWhy(String(goal?.why_text || ''));
    setPrimaryGoalMetric(String(goal?.target_metric_text || ''));
    setPrimaryGoalPurpose(goal?.goal_purpose || '');
    setPrimaryGoalStartDate(String(goal?.start_date || ''));
    setPrimaryGoalEndDate(String(goal?.end_date || ''));
    setPrimaryGoalEditing(!goal);
    setPrimaryGoalReview(goal?.quality_workflow?.evaluation?.status === 'completed'
      ? goal.quality_workflow.evaluation.review || null : null);
    setPrimaryGoalReviewEventId(null);
    setPrimaryGoalReviewError('');
    setGoalExamplesOpen(false);
    setGoalExampleNotice('');
    goalExampleSourceRef.current = null;
    setGoalEvidence(null);
    setGoalEvidenceSource('');
    setGoalEvidenceMilestone('');
    setGoalEvidenceError('');
    setNsrCycle(null);
    setNsrPrimaryItem('');
    setNsrSecondaryItem('');
    setNsrReflection('');
    setNsrError('');
    setPerformanceReview(null);
    setDailyNudge(null);
    setDailyNudgeEventId(null);
    setDailyNudgeError('');
  };

  const applyGoalFinalReflection = (reflection: GoalFinalReflection | null) => {
    setGoalFinalReflection(reflection);
    setGoalOutcomeStatus(reflection?.outcome_status || 'partially_met');
    setGoalEvidenceText(String(reflection?.evidence_text || ''));
    setGoalWhatWorkedText(String(reflection?.what_worked_text || ''));
    setGoalObstacleText(String(reflection?.obstacle_text || ''));
    setGoalNextTimeChangeText(String(reflection?.next_time_change_text || ''));
    setGoalNsrFocusReflectionText(String(reflection?.nsr_focus_reflection_text || ''));
    setGoalNextDirectionText(String(reflection?.next_goal_direction_text || ''));
    setGoalClosureOpen(false);
    setGoalClosureError('');
  };

  const clearGoalFinalReflection = () => {
    applyGoalFinalReflection(null);
  };

  const applyWeeklyCheckpointPayload = (payload: any) => {
    const checkpoint = (payload?.current_checkpoint || null) as WeeklyGoalCheckpoint | null;
    setWeeklyCheckpoint(checkpoint);
    setWeeklyCheckpointHistory(Array.isArray(payload?.history) ? payload.history : []);
    setWeeklyCheckpointAvailability((payload?.availability || null) as WeeklyGoalCheckpointAvailability | null);
    setWeeklyCheckpointWeekStart(String(payload?.week_start_date || ''));
    setWeeklyProgressText(String(checkpoint?.progress_text || ''));
    setWeeklyEvidenceText(String(checkpoint?.evidence_text || ''));
    setWeeklyObstacleText(String(checkpoint?.obstacle_text || ''));
    setWeeklyAdjustmentText(String(checkpoint?.adjustment_text || ''));
    setWeeklyConfidenceRating(Number(checkpoint?.confidence_rating || 3));
  };

  const clearWeeklyCheckpoint = () => {
    setWeeklyCheckpoint(null);
    setWeeklyCheckpointHistory([]);
    setWeeklyCheckpointAvailability(null);
    setWeeklyCheckpointWeekStart('');
    setWeeklyProgressText('');
    setWeeklyEvidenceText('');
    setWeeklyObstacleText('');
    setWeeklyAdjustmentText('');
    setWeeklyConfidenceRating(3);
  };

  const loadWeeklyCheckpoint = async (sectionId: number, requestId?: number) => {
    if (!isCurrentSectionLoadRequest(requestId)) return;
    setWeeklyCheckpointError('');
    try {
      const payload = await goalsApi.weeklyCheckpoint.current({ section_id: sectionId, limit: 6 });
      if (!isCurrentSectionLoadRequest(requestId)) return;
      applyWeeklyCheckpointPayload(payload);
    } catch (err: any) {
      if (!isCurrentSectionLoadRequest(requestId)) return;
      clearWeeklyCheckpoint();
      if (err?.response?.status === 409 && err?.response?.data?.error === 'goals_not_enabled_for_section_or_grade') {
        return;
      }
      setWeeklyCheckpointError(err?.response?.data?.error || 'Failed to load weekly checkpoint.');
    }
  };

  const loadPrimaryGoal = async (sectionId: number, requestId?: number) => {
    if (!isCurrentSectionLoadRequest(requestId)) return;
    setPrimaryGoalError('');
    try {
      const payload = await goalsApi.primaryGoal.active({ section_id: sectionId });
      if (!isCurrentSectionLoadRequest(requestId)) return;
      const policy = (payload?.goal_policy || null) as GoalPolicy | null;
      setGoalPolicy(policy);
      const cycle = (payload?.active_instructional_cycle || null) as InstructionalCycle | null;
      const direction = (payload?.longer_term_direction || null) as StudentLongerTermDirection | null;
      setActivePerformanceCycle(cycle);
      setLongerTermDirection(direction);
      if (!directionDraftDirtyRef.current) {
        setDirectionTitle(String(direction?.title || ''));
        setDirectionWhy(String(direction?.why_text || ''));
        setDirectionMetric(String(direction?.target_metric_text || ''));
        setDirectionEditing(false);
        setDirectionError('');
      }
      const activeGoals = (
        Array.isArray(payload?.primary_goals)
          ? payload.primary_goals
          : payload?.primary_goal ? [payload.primary_goal] : []
      ).map((row: StudentPrimaryGoal) => ({
        ...row,
        goal_track: row.goal_track || 'required_pathway',
      })) as StudentPrimaryGoal[];
      setPrimaryGoals(activeGoals);
      setReviewGoals(Array.isArray(payload?.review_goals) ? payload.review_goals : []);
      setReviewingGoalId(null);
      const requiredGoal = activeGoals.find((row) => row.goal_track === 'required_pathway');
      const goal = (requiredGoal || payload?.primary_goal || payload?.review_goal || null) as StudentPrimaryGoal | null;
      if (primaryGoalDraftDirtyRef.current) {
        setPrimaryGoal(goal);
        setMilestones(Array.isArray(goal?.milestones) ? (goal?.milestones || []) : []);
      } else {
        setSelectedGoalTrack(goal?.goal_track || 'required_pathway');
        applyPrimaryGoalToForm(goal);
        if (!goal && usesSeniorPriorityWorkflow(policy)) {
          setPrimaryGoalEditing(Boolean(cycle));
        }
      }
      try {
        const checkInPayload = await studentActionQueueApi.myCheckIns(sectionId);
        if (!isCurrentSectionLoadRequest(requestId)) return;
        setInstructionalCheckIns(Array.isArray(checkInPayload.data?.check_ins) ? checkInPayload.data.check_ins : []);
      } catch {
        if (!isCurrentSectionLoadRequest(requestId)) return;
        setInstructionalCheckIns([]);
      }
      if (goal) {
        if (goal.instructional_cycle) {
          const evidencePayload = await goalsApi.primaryGoal.evidenceLinks.list(goal.id);
          if (!isCurrentSectionLoadRequest(requestId)) return;
          setGoalEvidence(evidencePayload as GoalEvidencePayload);
          try {
            const nsrPayload = await nsrApi.myCycle(goal.instructional_cycle);
            if (!isCurrentSectionLoadRequest(requestId)) return;
            const typedNsrPayload = nsrPayload as NSRCyclePayload;
            setNsrCycle(typedNsrPayload);
            setNsrPrimaryItem(typedNsrPayload.focus?.primary_item_key || '');
            setNsrSecondaryItem(typedNsrPayload.focus?.secondary_item_key || '');
            setNsrReflection(typedNsrPayload.focus?.student_reflection || '');
          } catch (nsrLoadError: any) {
            if (!isCurrentSectionLoadRequest(requestId)) return;
            if (![404, 409].includes(Number(nsrLoadError?.response?.status))) {
              setNsrError(nsrLoadError?.response?.data?.detail || nsrLoadError?.response?.data?.error || 'Failed to load Next Step Ready focus.');
            }
          }
          try {
            const reviewPayload = await performanceReviewApi.my(goal.id);
            if (!isCurrentSectionLoadRequest(requestId)) return;
            setPerformanceReview(reviewPayload as FiveWeekPerformanceReviewPayload);
          } catch (reviewLoadError: any) {
            if (!isCurrentSectionLoadRequest(requestId)) return;
            if (Number(reviewLoadError?.response?.status) !== 404) {
              setGoalClosureError(reviewLoadError?.response?.data?.detail || 'Failed to load the five-week review.');
            }
          }
        }
        if (policy?.final_reflection_enabled) {
          const reflectionPayload = await goalsApi.primaryGoal.finalReflection.get(goal.id);
          if (!isCurrentSectionLoadRequest(requestId)) return;
          applyGoalFinalReflection((reflectionPayload?.final_reflection || null) as GoalFinalReflection | null);
        } else {
          clearGoalFinalReflection();
        }
        if (policy?.weekly_checkpoint_enabled) {
          await loadWeeklyCheckpoint(sectionId, requestId);
        } else {
          clearWeeklyCheckpoint();
        }
      } else {
        clearGoalFinalReflection();
        clearWeeklyCheckpoint();
      }
    } catch (err: any) {
      if (!isCurrentSectionLoadRequest(requestId)) return;
      setInstructionalCheckIns([]);
      if (!primaryGoalDraftDirtyRef.current) {
        applyPrimaryGoalToForm(null);
        setPrimaryGoals([]);
        setActivePerformanceCycle(null);
        setGoalPolicy(null);
        setReviewGoals([]);
        setReviewingGoalId(null);
      }
      if (!directionDraftDirtyRef.current) {
        setLongerTermDirection(null);
      }
      clearGoalFinalReflection();
      clearWeeklyCheckpoint();
      setPrimaryGoalError(err?.response?.data?.error || 'Failed to load long-term goal.');
    }
  };

  const loadGoals = async (sectionId: number) => {
    const requestId = beginSectionLoadRequest();
    setLoadingGoals(true);
    setGoalsError('');
    try {
      const payload = await goalsApi.myToday({ section_id: sectionId });
      if (!isCurrentSectionLoadRequest(requestId)) return;
      const nextIsSchoolDay = Boolean(payload?.gate_status?.is_school_day ?? true);
      if (nextIsSchoolDay || !['not_checked_in', undefined, null].includes(payload?.status)) {
        setDailyGoalOptional(false);
      }
      setGoalPolicy((payload?.gate_status?.goal_policy || payload?.section?.goal_policy || null) as GoalPolicy | null);
      await loadPrimaryGoal(sectionId, requestId);
      if (!isCurrentSectionLoadRequest(requestId)) return;
      setGoalCycle(payload || null);
      setSectionSubgroup(String(payload?.section_subgroup || payload?.section?.student_group_name || ''));
      const learningGoal = (payload?.goals || []).find((goal: any) => goal.goal_type === 'learning');
      const completionGoal = (payload?.goals || []).find((goal: any) => goal.goal_type === 'completion');
      const loadedPlanTrack = String(payload?.plan_track || '');
      const loadedSuggestedTrack = String(payload?.suggested_plan_track || '');
      const nextLoadedTrack = PLAN_TRACK_OPTIONS.some((option) => option.value === loadedPlanTrack && option.value !== '')
        ? loadedPlanTrack as PlanTrack
        : '';
      const nextSuggestedTrack = PLAN_TRACK_OPTIONS.some((option) => option.value === loadedSuggestedTrack)
        ? loadedSuggestedTrack as PlanTrack
        : '';
      setSuggestedPlanTrack(nextSuggestedTrack);
      setPlanTrack(nextLoadedTrack || nextSuggestedTrack);
      setPlanTarget(String(payload?.plan_target || ''));
      setProtectedGoalMinutes(payload?.protected_goal_minutes ? String(payload.protected_goal_minutes) : '');
      if (payload?.protected_goal_minutes) setStartWorkPlannedMinutes(Number(payload.protected_goal_minutes));
      setPlannedSecondaryWork(String(payload?.planned_secondary_work || ''));
      setPlannedWorkMode(String(payload?.planned_work_mode || ''));
      setPlanSuccessCriteria(String(payload?.plan_success_criteria || ''));
      const loadedFocus = (payload?.current_focus || {}) as Partial<CurrentFocus>;
      setFocusKey(String(loadedFocus.key || 'individual_project'));
      setFocusTitle(String(loadedFocus.title || loadedFocus.label || ''));
      setPersonalProjectTask(String(payload?.project_plan_target || loadedFocus.secondary_title || ''));
      setPersonalProjectProof(String(payload?.project_plan_success_criteria || ''));
      setFocusEditing(false);

      // Restore work state from backend
      if (payload?.work_status) {
        setWorkStatus(payload.work_status);
        // Don't show transition card if work has already started
        if (payload.work_status !== 'not_started') {
          setShowStartWorkTransition(false);
        }
      }
      const learningBuilder = (
        learningGoal?.builder_data && typeof learningGoal.builder_data === 'object'
          ? learningGoal.builder_data
          : {}
      ) as Partial<LearningGoalBuilder>;
      setLearningSkill(String(learningBuilder.skill || (payload?.plan_track ? trackLabel(payload.plan_track) : '')));
      setLearningAction(String(learningBuilder.action || payload?.plan_target || ''));
      setLearningProof(String(learningBuilder.proof || payload?.plan_success_criteria || ''));
      setFirstStepText(String(payload?.plan_first_step || learningBuilder.first_step || ''));
      setAiRefinedLearningGoalText(Boolean(learningGoal?.ai_assisted) ? String(learningGoal?.smart_text || '') : '');
      const completionBuilder = (
        completionGoal?.builder_data && typeof completionGoal.builder_data === 'object'
          ? completionGoal.builder_data
          : {}
      ) as Record<string, any>;
      const selectedWorkItem = payload?.plan_work_item && typeof payload.plan_work_item === 'object' && Object.keys(payload.plan_work_item).length > 0
        ? payload.plan_work_item
        : completionBuilder.selected_work_item && typeof completionBuilder.selected_work_item === 'object'
          ? completionBuilder.selected_work_item
          : null;
      if (!payload?.plan_target) {
        setPlanTarget(String(completionBuilder.manual_text || (!selectedWorkItem ? completionGoal?.smart_text || '' : selectedWorkItem?.title || '')));
      }
      if (!payload?.plan_success_criteria && completionBuilder.success_criteria) {
        setPlanSuccessCriteria(String(completionBuilder.success_criteria || ''));
      }
      if (!payload?.plan_first_step && completionBuilder.first_step) {
        setFirstStepText(String(completionBuilder.first_step || ''));
      }
      const selectedWorkKey = selectedWorkItem?.kind && selectedWorkItem?.id
        ? `${selectedWorkItem.kind}:${selectedWorkItem.id}`
        : null;
      setSelectedCompletionWorkKey(selectedWorkKey || '');
      const loadedProjectRole = String(
        completionBuilder.project_role
        || selectedWorkItem?.project_role
        || (payload?.plan_track === 'main_project' || payload?.plan_track === 'secondary_project' ? payload.plan_track : '')
        || '',
      );
      setCompletionProjectRole(
        loadedProjectRole === 'main_project' || loadedProjectRole === 'secondary_project'
          ? loadedProjectRole
          : '',
      );
      setLearningStageTag(String(learningGoal?.stage_tag || ''));
      setLearningTags(Array.isArray(learningGoal?.readiness_tags) ? learningGoal.readiness_tags.join(', ') : '');
      setCompletionTags(Array.isArray(completionGoal?.readiness_tags) ? completionGoal.readiness_tags.join(', ') : '');
      setAiSuggestion(null);
      setAiSuggestionEventId(null);

      setAiApplied(Boolean(learningGoal?.ai_assisted));
      setAiRationale(String(learningGoal?.ai_rationale || ''));
      setCheckoutReflection(String(payload?.reflection_text || ''));
      const nextReflectionStatus = String(payload?.reflection_status || '') as 'yes' | 'partial' | 'no' | '';
      setCheckoutGoalStatus(['yes', 'partial', 'no'].includes(nextReflectionStatus) ? nextReflectionStatus : '');
      setCheckoutNextStep(String(payload?.reflection_next_step || ''));
      setPlanChangeReason(String(payload?.plan_change_reason || ''));
      setPlanChangeNote(String(payload?.plan_change_note || ''));
      const loadedPriorityResult = String(payload?.priority_result || '') as typeof priorityResult;
      setPriorityResult(['met', 'partially_met', 'blocked', 'changed_authentic_work', 'ready_for_review'].includes(loadedPriorityResult) ? loadedPriorityResult : '');
      setPathwayEvidenceText(String(payload?.pathway_evidence?.description || ''));
      setPathwayEvidenceUrl(String(payload?.pathway_evidence?.url || ''));
      const loadedProjectResult = String(payload?.project_result || '') as typeof projectResult;
      setProjectResult(['met', 'partially_met', 'blocked', 'changed_authentic_work', 'ready_for_review'].includes(loadedProjectResult) ? loadedProjectResult : '');
      setProjectEvidenceText(String(payload?.project_evidence?.description || ''));
      setProjectEvidenceUrl(String(payload?.project_evidence?.url || ''));
      setProjectPortfolioCandidate(Boolean(payload?.project_evidence?.portfolio_candidate));
      setMovedTowardPrimaryGoal(
        payload?.moved_toward_primary_goal === true
          ? 'yes'
          : payload?.moved_toward_primary_goal === false
            ? 'no'
            : '',
      );
      setCompletionRating(Number(payload?.completion_rating || 3));
      setConfidenceRating(Number(payload?.confidence_rating || 3));
      const goalsEnabledForStudent = Boolean(
        payload?.gate_status?.goals_feature_enabled ?? payload?.section?.goals_feature_enabled,
      );
      if (goalsEnabledForStudent) {
        await Promise.all([
          loadExternalWork(sectionId, requestId),
          loadGoalHistory(sectionId, 14, requestId),
        ]);
      } else {
        setExternalWorkBlocks([]);
        setExternalWorkEvents([]);
        setSwitchCountToday(0);
        setGoalHistory([]);
      }
    } catch (err: any) {
      if (!isCurrentSectionLoadRequest(requestId)) return;
      setGoalsError(err?.response?.data?.error || 'Failed to load daily goals.');
      setGoalCycle(null);
      setSectionSubgroup('');
      setSuggestedPlanTrack('');
      setGoalPolicy(null);
      applyPrimaryGoalToForm(null);
      clearGoalFinalReflection();
      setExternalWorkBlocks([]);
      setExternalWorkEvents([]);
      setSwitchCountToday(0);
      setGoalHistory([]);
    } finally {
      if (isCurrentSectionLoadRequest(requestId)) {
        setLoadingGoals(false);
      }
    }
  };

  const loadWorkspace = async (preferredSectionId?: number) => {
    const [hooksRes, sectionsRes] = await Promise.all([
      hooksV2Api.list(),
      sectionsApi.list(),
    ]);
    const hooksData = normalizeArray(hooksRes.data);
    const sectionsData = normalizeArray(sectionsRes.data);
    setHooks(hooksData);
    setSections(sectionsData);
    if (sectionsData.length) {
      const requestedSectionId = preferredSectionId || Number(new URLSearchParams(location.search).get('section'));
      const preferredSection = requestedSectionId
        ? sectionsData.find((section: Section) => section.id === requestedSectionId)
        : null;
      const nextSectionId = preferredSection?.id || sectionsData[0].id;
      setActiveSection(nextSectionId);
      await loadGoals(nextSectionId);
    } else {
      beginSectionLoadRequest();
      setActiveSection(null);
      setGoalCycle(null);
      setSectionSubgroup('');
      setSuggestedPlanTrack('');
      setGoalPolicy(null);
      applyPrimaryGoalToForm(null);
      clearGoalFinalReflection();
      clearWeeklyCheckpoint();
      setExternalWorkBlocks([]);
      setExternalWorkEvents([]);
      setGoalHistory([]);
    }
  };

  useEffect(() => {
    void loadWorkspace(Number(new URLSearchParams(location.search).get('section')) || undefined);
  }, []);

  useEffect(() => {
    const loadActiveRun = async () => {
      try {
        const response = await lessonRunsApi.myActive();
        setActiveRun(response || null);
      } catch (err: any) {
        if (err?.response?.status === 428) {
          setActiveRun(null);
        }
      }
    };
    void loadActiveRun();
    const intervalId = window.setInterval(() => {
      void loadActiveRun();
    }, STUDENT_DASHBOARD_ASSIGNMENTS_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [goalCycle?.status, activeSection]);

  useEffect(() => {
    const loadFixQueue = async () => {
      if (!activeRun?.id) {
        setFixQueuePreview(null);
        return;
      }
      try {
        const payload = await lessonRunsApi.submissionQualityQueue(activeRun.id, {
          statuses: 'provisional,needs_teacher_review',
        });
        const unresolved = Number(payload?.provisional || 0) + Number(payload?.needs_teacher_review || 0);
        const items = Array.isArray(payload?.items) ? payload.items.slice(0, 3) : [];
        setFixQueuePreview({ unresolved, items });
      } catch {
        setFixQueuePreview(null);
      }
    };
    loadFixQueue();
  }, [activeRun?.id, location.key]);

  const loadRunHistory = async () => {
    try {
      const response = await lessonRunsApi.list();
      const rows = normalizeArray(response.data) as LessonRun[];
      rows.sort((a, b) => {
        const aTime = new Date(a.started_at || 0).getTime();
        const bTime = new Date(b.started_at || 0).getTime();
        return bTime - aTime;
      });
      setRunHistory(rows);
    } catch (err: any) {
      if (err?.response?.status === 428) {
        setRunHistory([]);
      }
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await assignmentsApi.my();
      setAssignments(Array.isArray(response) ? response : []);
    } catch (err: any) {
      if (err?.response?.status === 428) {
        setAssignments([]);
      }
    }
  };

  useEffect(() => {
    loadAssignments();
    loadRunHistory();
  }, [location.key, goalCycle?.status]);

  useEffect(() => {
    const handleFocus = () => {
      loadAssignments();
      loadRunHistory();
      if (activeSection && !planningFormActiveRef.current) {
        loadGoals(activeSection);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeSection]);

  useEffect(() => {
    const refreshStudentQueue = () => {
      loadAssignments();
      loadRunHistory();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      refreshStudentQueue();
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      refreshStudentQueue();
    }, STUDENT_DASHBOARD_ASSIGNMENTS_REFRESH_MS);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [goalCycle?.status, location.key]);

  useEffect(() => {
    if (!activeWorkBlock) return undefined;
    const intervalId = window.setInterval(() => {
      setTimerNow(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [activeWorkBlock?.id]);

  const toggleCountdownHidden = () => {
    setCountdownHidden((current) => {
      const next = !current;
      window.localStorage.setItem('elevate-focus-countdown-hidden', String(next));
      return next;
    });
  };

  useEffect(() => {
    if (!activeWorkBlock) {
      setBlockEvidenceNote('');
      setBlockEvidenceLink('');
      return;
    }
    setBlockEvidenceNote(String(activeWorkBlock.evidence_note || ''));
    setBlockEvidenceLink(String(activeWorkBlock.evidence_link || ''));
    setTimerNow(Date.now());
  }, [activeWorkBlock?.id]);

  useEffect(() => {
    if (!startWorkTaskId) return;
    const task = (goalCycle?.external_tasks || []).find((entry) => String(entry.id) === String(startWorkTaskId));
    if (!task) return;
    setStartWorkTitle(String(task.title || ''));
    setStartWorkSource(String(task.source_app || ''));
  }, [startWorkTaskId, goalCycle?.external_tasks]);

  useEffect(() => {
    if (!switchTaskId) return;
    const task = (goalCycle?.external_tasks || []).find((entry) => String(entry.id) === String(switchTaskId));
    if (!task) return;
    setSwitchTitle(String(task.title || ''));
    setSwitchSource(String(task.source_app || ''));
  }, [switchTaskId, goalCycle?.external_tasks]);

  const reviewRuns = useMemo(() => {
    const completedRuns = runHistory.filter((run) => run.status !== 'active');
    const latestRunByLesson = new Map<string, LessonRun>();

    for (const run of completedRuns) {
      const dedupeKey = `${run.section}-${run.lesson_plan}`;
      const existing = latestRunByLesson.get(dedupeKey);
      if (!existing) {
        latestRunByLesson.set(dedupeKey, run);
        continue;
      }

      const runTime = new Date(run.started_at || 0).getTime();
      const existingTime = new Date(existing.started_at || 0).getTime();
      if (runTime > existingTime || (runTime === existingTime && run.id > existing.id)) {
        latestRunByLesson.set(dedupeKey, run);
      }
    }

    return Array.from(latestRunByLesson.values())
      .sort((a, b) => {
        const aTime = new Date(a.started_at || 0).getTime();
        const bTime = new Date(b.started_at || 0).getTime();
        if (bTime !== aTime) return bTime - aTime;
        return b.id - a.id;
      })
      .slice(0, 8);
  }, [runHistory]);

  const statusStyles = useMemo(() => ({
    not_started: 'bg-slate-100 text-slate-700',
    in_progress: 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700',
    excused: 'bg-purple-100 text-purple-700',
  }), []);
  const openExternalTasks = useMemo(
    () => (goalCycle?.external_tasks || []).filter((entry) => entry.status !== 'completed'),
    [goalCycle?.external_tasks],
  );

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const externalEventLabel = (event: ExternalWorkEvent) => {
    const type = String(event.event_type || '');
    if (type === 'start') return 'Session started';
    if (type === 'stop') return `Session ${String(event.payload?.status || 'stopped')}`;
    if (type === 'switch') return 'Switched to next session';
    if (type === 'minutes_adjusted') return `+${event.payload?.minutes_delta || 0} minutes`;
    if (type === 'evidence_note') return 'Evidence note updated';
    if (type === 'evidence_link') return 'Evidence link updated';
    return type;
  };

  const smartDeficiencyLabel = (value: string) => {
    if (value === 'missing_specific_scope') return 'Needs specific scope';
    if (value === 'missing_measurable_criterion') return 'Needs measurable criterion';
    if (value === 'goal_scope_unrealistic') return 'Scope looks unrealistic';
    if (value === 'missing_relevance_link') return 'Needs relevance link';
    if (value === 'missing_time_bound') return 'Needs time boundary';
    return value.replace(/_/g, ' ');
  };

  const cycleStatusClass = (statusValue: string) => {
    if (statusValue === 'checked_out') return 'bg-emerald-100 text-emerald-700';
    if (statusValue === 'checked_in') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  const currentSection = useMemo(
    () => sections.find((section) => section.id === activeSection) || null,
    [sections, activeSection],
  );
  const gateRequired = Boolean(goalCycle?.gate_status?.gate_required);
  const dailyPriorityMissing = Boolean(
    goalCycle?.gate_status?.expectation_missing
    && goalCycle?.gate_status?.soft_signal_enabled
    && !gateRequired,
  );
  const isSchoolDay = Boolean(goalCycle?.gate_status?.is_school_day ?? true);
  const gateReason = String(goalCycle?.gate_status?.reason || '');
  const goalsFeatureEnabled = Boolean(
    goalCycle?.gate_status?.goals_feature_enabled ?? currentSection?.goals_feature_enabled,
  );
  const effectiveGoalMode = String(
    goalPolicy?.effective_goal_mode
    || goalCycle?.gate_status?.effective_goal_mode
    || currentSection?.goal_mode
    || (goalsFeatureEnabled ? 'full' : 'off'),
  );
  const primaryGoalFeatureEnabled = Boolean(goalPolicy?.primary_goal_enabled ?? goalsFeatureEnabled);
  const weeklyCheckpointFeatureEnabled = Boolean(goalPolicy?.weekly_checkpoint_enabled ?? goalsFeatureEnabled);
  const finalReflectionFeatureEnabled = Boolean(goalPolicy?.final_reflection_enabled ?? goalsFeatureEnabled);
  const aiGoalAssistFeatureEnabled = Boolean(goalPolicy?.ai_goal_assist_enabled ?? goalsFeatureEnabled);
  const seniorPriorityWorkflow = usesSeniorPriorityWorkflow(goalPolicy);
  const showGoalTools = Boolean((goalPolicy?.workflow?.goal_tools_enabled ?? primaryGoalFeatureEnabled) || primaryGoals.length || primaryGoal);
  // Goal conferences run alongside daily work; proposals do not block check-ins.
  const seniorGoalSetupRequired = false;
  const activeHooks = useMemo(
    () => hooks.filter((hook) => !hook.is_archived),
    [hooks],
  );
  const archivedHooks = useMemo(
    () => hooks.filter((hook) => hook.is_archived),
    [hooks],
  );
  const activeHookCount = useMemo(
    () => activeHooks.length,
    [activeHooks],
  );
  const activeAssignmentCount = useMemo(
    () => assignments.filter((assignment) => !['completed', 'excused'].includes(assignment.student_status)).length,
    [assignments],
  );
  const currentFocusLabel = activeRun ? 'Live lesson' : gateRequired ? 'Check-in gate' : 'Independent work';
  const canSubmitDailyCheckIn = !['checked_in', 'checked_out'].includes(String(goalCycle?.status || ''));
  const dailyGoalLocked = Boolean(goalsFeatureEnabled && gateRequired);
  const workUnlocked = Boolean(goalsFeatureEnabled && !gateRequired && goalCycle?.status === 'checked_in');

const dashboardNavItems = useMemo(() => {
  const missingWorkCount = goalCycle?.missing_work?.counts?.total || 0;
  const activeNav = activeTab === 'work'
    ? 'work'
    : activeTab === 'goals'
      ? 'coaching'
      : activeTab === 'portfolio'
        ? 'review'
        : activeTab === 'today' ? 'daily-plan' : 'dashboard';
  return studentV2NavItems(activeNav, currentSection?.id).map(item => ({
    ...item,
    badge: item.id === 'today' && gateRequired
      ? 1
      : item.id === 'work' && missingWorkCount > 0
        ? missingWorkCount
        : null,
  }));
}, [activeTab, currentSection?.id, gateRequired, goalCycle]);
  const showNonSchoolFocusView = Boolean(!isSchoolDay && !dailyGoalOptional && canSubmitDailyCheckIn);
  const showDailyCheckInForm = Boolean(
    canSubmitDailyCheckIn
    && !seniorGoalSetupRequired
    && (gateRequired || goalCycle?.status === 'not_checked_in' || (!isSchoolDay && dailyGoalOptional)),
  );
  planningFormActiveRef.current = primaryGoalEditing
    || directionEditing
    || showDailyCheckInForm
    || (activeTab === 'today' && goalCycle?.status === 'checked_in');
  const dayTypeLabel = !isSchoolDay
    ? seniorPriorityWorkflow ? 'Optional work day' : 'Optional goal day'
    : gateReason === 'non_school_day'
      ? seniorPriorityWorkflow ? 'No morning plan required' : 'No goal gate'
      : seniorPriorityWorkflow ? 'School work day' : 'School goal day';
  const dayTypeDetail = !isSchoolDay
    ? seniorPriorityWorkflow
      ? 'A morning plan is optional today. Choose useful work or set a direction if it helps.'
      : 'Daily goals are optional today. Use a light focus view or set a plan if it helps.'
    : gateRequired
      ? 'A daily plan is required before the rest of the workspace opens.'
      : 'Daily goal tracking is open for this school day.';
  const goalStatusLabel = String(goalCycle?.status || 'not_checked_in').replace(/_/g, ' ');
  const currentLearningGoal = useMemo(
    () => (goalCycle?.goals || []).find((goal) => goal.goal_type === 'learning') || null,
    [goalCycle?.goals],
  );
  const currentCompletionGoal = useMemo(
    () => (goalCycle?.goals || []).find((goal) => goal.goal_type === 'completion') || null,
    [goalCycle?.goals],
  );
  const linkedPrimaryGoal = (goalCycle?.primary_goal_detail || null) as StudentPrimaryGoal | null;
  const dailyGoalAnchor = linkedPrimaryGoal || primaryGoal;
  const dailyPlanConnectsToPrimaryGoal = Boolean(
    primaryGoal && (
      !goalCycle
      || goalCycle.status === 'not_checked_in'
      || gateRequired
      || Number(goalCycle.primary_goal) === Number(primaryGoal.id)
    ),
  );
  const totalLoggedExternalMinutes = useMemo(
    () => externalWorkBlocks.reduce((sum, block) => sum + Number(block.actual_minutes || 0), 0),
    [externalWorkBlocks],
  );
  const learningGoalBuilder = useMemo<LearningGoalBuilder>(() => ({
    skill: learningSkill.trim() || trackLabel(planTrack),
    action: learningAction.trim() || planTarget.trim(),
    proof: learningProof.trim() || planSuccessCriteria.trim(),
    first_step: firstStepText.trim(),
  }), [firstStepText, learningAction, learningProof, learningSkill, planSuccessCriteria, planTarget, planTrack]);
  const composedLearningGoalText = useMemo(
    () => composeLearningGoalTextFromPlan(planTrack, planTarget, planSuccessCriteria)
      || composeLearningGoalText(
        learningGoalBuilder.skill,
        learningGoalBuilder.action,
        learningGoalBuilder.proof,
        learningGoalBuilder.first_step,
      ),
    [learningGoalBuilder, planSuccessCriteria, planTarget, planTrack],
  );
  const finalLearningGoalText = aiApplied && aiRefinedLearningGoalText.trim()
    ? aiRefinedLearningGoalText.trim()
    : composedLearningGoalText;
  const availableCompletionWorkItems = useMemo<CompletionWorkItem[]>(() => {
    const items = new Map<string, CompletionWorkItem>();
    const addItem = (item: CompletionWorkItem) => {
      if (!item.title || items.has(item.key)) return;
      items.set(item.key, item);
    };

    for (const item of goalCycle?.missing_work?.items || []) {
      const kind = String(item.kind || '') as CompletionWorkItem['kind'];
      const id = Number(item.id || 0);
      if (!id || !['internal_assignment', 'external_task'].includes(kind)) continue;
      const title = String(item.title || '').trim();
      const dueValue = String(item.due_local_date || item.due_date || item.due_at || '').trim();
      addItem({
        key: `${kind}:${id}`,
        kind,
        id,
        title,
        source: kind === 'external_task' ? String(item.source_app || 'Outside work') : 'Elevate assignment',
        trackKey: kind === 'external_task' ? inferTrackKey(title, String(item.source_app || '')) : 'custom',
        detail: [
          item.overdue ? 'Overdue' : item.due_today ? 'Due today' : dueValue ? `Due ${dueValue}` : 'No due date',
          item.status ? `Status: ${String(item.status).replace(/_/g, ' ')}` : '',
        ].filter(Boolean).join(' | '),
        status: String(item.status || ''),
      });
    }

    for (const assignment of assignments) {
      if (['completed', 'excused'].includes(assignment.student_status)) continue;
      const id = Number(assignment.assignment_id || 0);
      addItem({
        key: `internal_assignment:${id}`,
        kind: 'internal_assignment',
        id,
        title: assignment.lesson_plan_title || `Assignment ${id}`,
        source: assignment.section_name || 'Elevate assignment',
        trackKey: 'custom',
        detail: assignment.due_at ? `Due ${new Date(assignment.due_at).toLocaleDateString()}` : 'Active assignment',
        status: assignment.student_status,
      });
    }

    for (const entry of openExternalTasks) {
      const id = Number(entry.id || 0);
      addItem({
        key: `external_task:${id}`,
        kind: 'external_task',
        id,
        title: entry.title,
        source: entry.source_app || 'Outside work',
        trackKey: inferTrackKey(entry.title, entry.source_app),
        detail: entry.due_date ? `Due ${entry.due_date}` : `Status: ${entry.status.replace(/_/g, ' ')}`,
        status: entry.status,
      });
    }

    if (activeRun) {
      addItem({
        key: `live_lesson:${activeRun.id}`,
        kind: 'live_lesson',
        id: activeRun.id,
        title: activeRun.lesson_plan_title || 'Live lesson',
        source: activeRun.section_name || 'Live lesson',
        trackKey: 'custom',
        detail: 'Teacher-led work available now',
        status: activeRun.status,
      });
    }

    return Array.from(items.values()).slice(0, 8);
  }, [activeRun, assignments, goalCycle?.missing_work?.items, openExternalTasks]);
  const selectedCompletionWorkItem = useMemo(
    () => availableCompletionWorkItems.find((item) => item.key === selectedCompletionWorkKey) || null,
    [availableCompletionWorkItems, selectedCompletionWorkKey],
  );
  const composedCompletionGoalText = useMemo(
    () => composeCompletionGoalText(
      planTarget,
      selectedCompletionWorkItem,
      completionProjectRole,
      firstStepText,
      planSuccessCriteria,
    ),
    [completionProjectRole, firstStepText, planSuccessCriteria, planTarget, selectedCompletionWorkItem],
  );
  const dailyPlan = useMemo(() => ({
    track: planTrack,
    target: planTarget.trim() || selectedCompletionWorkItem?.title || '',
    first_step: firstStepText.trim(),
    success_criteria: planSuccessCriteria.trim(),
    work_item: selectedCompletionWorkItem
      ? {
          kind: selectedCompletionWorkItem.kind,
          id: selectedCompletionWorkItem.id,
          title: selectedCompletionWorkItem.title,
          source: selectedCompletionWorkItem.source,
          track_key: selectedCompletionWorkItem.trackKey,
          project_role: completionProjectRole || undefined,
        }
      : null,
  }), [completionProjectRole, firstStepText, planSuccessCriteria, planTarget, planTrack, selectedCompletionWorkItem]);
  const completionGoalProgress = useMemo(() => {
    const builderData = (
      currentCompletionGoal?.builder_data && typeof currentCompletionGoal.builder_data === 'object'
        ? currentCompletionGoal.builder_data
        : {}
    ) as Record<string, any>;
    const selectedItem = goalCycle?.plan_work_item && typeof goalCycle.plan_work_item === 'object' && Object.keys(goalCycle.plan_work_item).length > 0
      ? goalCycle.plan_work_item
      : builderData.selected_work_item && typeof builderData.selected_work_item === 'object'
        ? builderData.selected_work_item
        : null;
    const sessionStatus = workStatus !== 'not_started' ? workStatus : '';
    const fallbackStatus = sessionStatus || String(currentCompletionGoal?.status || 'not_started');
    if (!selectedItem) {
      return {
        label: String(goalCycle?.plan_target || currentCompletionGoal?.smart_text || 'Daily plan'),
        status: fallbackStatus,
      };
    }
    const kind = String(selectedItem.kind || '');
    const selectedId = Number(selectedItem.id || 0);
    let status = fallbackStatus;
    if (!sessionStatus && kind === 'external_task') {
      status = String((goalCycle?.external_tasks || []).find((entry) => Number(entry.id) === selectedId)?.status || status);
    }
    if (!sessionStatus && (kind === 'internal_assignment' || kind === 'assignment')) {
      status = String(assignments.find((assignment) => Number(assignment.assignment_id) === selectedId)?.student_status || status);
    }
    if (!sessionStatus && kind === 'live_lesson') {
      status = activeRun?.id === selectedId ? 'in_progress' : fallbackStatus;
    }
    return {
      label: String(selectedItem.title || 'Selected work'),
      status,
    };
  }, [activeRun, assignments, currentCompletionGoal, goalCycle?.external_tasks, goalCycle?.plan_target, goalCycle?.plan_work_item, workStatus]);
  const currentDailyPlan = useMemo(() => structuredPlanFromCycle(goalCycle), [goalCycle]);
  const currentPlanWorkItem = useMemo(() => {
    const raw = goalCycle?.plan_work_item;
    return raw && typeof raw === 'object' && Object.keys(raw).length > 0
      ? raw as Record<string, any>
      : null;
  }, [goalCycle?.plan_work_item]);
  const planWorkSession = useMemo(() => {
    const focus = getFocusLabel(currentDailyPlan.track, currentDailyPlan.target);
    const title = String(
      currentDailyPlan.target
      || currentDailyPlan.legacyCompletionText
      || currentDailyPlan.legacyLearningText
      || focus
      || 'Daily work',
    ).trim();
    const workItemKind = String(currentPlanWorkItem?.kind || '');
    const workItemId = Number(currentPlanWorkItem?.id || 0);
    return {
      title: title || 'Daily work',
      focus,
      sourceApp: inferPlanWorkSourceApp(currentDailyPlan.track, currentPlanWorkItem),
      externalTaskEntryId: workItemKind === 'external_task' && workItemId > 0 ? workItemId : undefined,
    };
  }, [
    currentDailyPlan.legacyCompletionText,
    currentDailyPlan.legacyLearningText,
    currentDailyPlan.target,
    currentDailyPlan.track,
    currentPlanWorkItem,
  ]);
  const storedCurrentFocus = (goalCycle?.current_focus || null) as CurrentFocus | null;
  const seniorFocusPreview = useMemo<CurrentFocus>(() => {
    const requiredGoal = primaryGoals.find((row) => row.goal_track === 'required_pathway');
    const personalGoal = primaryGoals.find((row) => row.goal_track === 'personal_project');
    return {
      key: 'required_pathway',
      label: 'Main Goal',
      title: selectedCompletionWorkItem?.title || planTarget.trim(),
      origin: 'student',
      planned_goal_ids: [requiredGoal?.id, personalProjectTask.trim() ? personalGoal?.id : undefined]
        .filter((value): value is number => typeof value === 'number'),
      secondary_title: personalProjectTask.trim() || undefined,
    };
  }, [personalProjectTask, planTarget, primaryGoals, selectedCompletionWorkItem?.title]);
  const displayedCurrentFocus = storedCurrentFocus || seniorFocusPreview;
  const currentFocusText = [displayedCurrentFocus.label, displayedCurrentFocus.title]
    .filter((value, index, values) => Boolean(value) && values.indexOf(value) === index)
    .join(' — ');
  const missingWorkItems = useMemo(
    () => {
      const items = goalCycle?.missing_work?.items;
      return Array.isArray(items) ? items : [];
    },
    [goalCycle?.missing_work?.items],
  );
  const missingWorkCount = Number(goalCycle?.missing_work?.counts?.total || 0);
  const missingWorkPreview = String(missingWorkItems[0]?.title || '').trim();
  const missingWorkDetail = missingWorkCount <= 0
    ? 'No make-up assignments right now.'
    : missingWorkPreview
      ? `${missingWorkCount} missing: ${missingWorkPreview}${missingWorkCount > 1 ? ` +${missingWorkCount - 1} more` : ''}`
      : `${missingWorkCount} missing item${missingWorkCount === 1 ? '' : 's'} to review.`;

  const handleSectionChange = async (value: string) => {
    const nextSection = Number(value);
    primaryGoalDraftDirtyRef.current = false;
    directionDraftDirtyRef.current = false;
    setActiveSection(nextSection);
    await loadGoals(nextSection);
  };

  const handleJoinSectionByCode = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedCode = joinCode.trim().toUpperCase();
    if (!normalizedCode) {
      setJoinSectionError('Enter the class code from your teacher.');
      return;
    }
    setJoiningSection(true);
    setJoinSectionError('');
    setJoinSectionSuccess('');
    try {
      const response = joinGradeRequired
        ? await sectionsApi.joinByCode(normalizedCode, joinGrade)
        : await sectionsApi.joinByCode(normalizedCode);
      const joinedSection = response.data?.section;
      setJoinCode('');
      setJoinGrade('');
      setJoinGradeRequired(false);
      setJoinSectionSuccess(`Joined ${joinedSection?.name || 'section'}.`);
      await loadWorkspace(Number(joinedSection?.id || 0) || undefined);
    } catch (err: any) {
      if (err?.response?.data?.code === 'grade_required') {
        setJoinGradeRequired(true);
      }
      setJoinSectionError(err?.response?.data?.error || 'Could not join that class.');
    } finally {
      setJoiningSection(false);
    }
  };

  const clearAiRefinement = () => {
    setAiApplied(false);
    setAiRefinedLearningGoalText('');
    setAiSuggestion(null);
    setAiSuggestionEventId(null);
  };

  // Start Work flow handlers
  const persistWorkStatus = async (
    nextStatus: 'not_started' | 'in_progress' | 'paused' | 'completed',
    startedAt?: string,
  ) => {
    if (!activeSection || !goalCycle) return;
    try {
      await goalsApi.updateWorkStatus(activeSection, {
        work_status: nextStatus,
        work_started_at: startedAt,
      });
    } catch (err) {
      console.error('Failed to update work status:', err);
    }
  };

  const startPlanWorkSession = async () => {
    if (!activeSection) return false;
    if (activeWorkBlock) return true;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      const startedPayload = await goalsApi.externalWorkBlocks.start({
        section_id: activeSection,
        external_task_entry_id: planWorkSession.externalTaskEntryId,
        title: planWorkSession.externalTaskEntryId ? undefined : planWorkSession.title,
        source_app: planWorkSession.externalTaskEntryId ? undefined : planWorkSession.sourceApp || undefined,
        planned_minutes: Number.isFinite(startWorkPlannedMinutes) ? Number(startWorkPlannedMinutes) : undefined,
      });
      if (startedPayload?.block) {
        setExternalWorkBlocks((current) => [startedPayload.block, ...current.filter((block) => block.id !== startedPayload.block.id)]);
      }
      await loadGoals(activeSection);
      return true;
    } catch (err: any) {
      if (err?.response?.data?.error === 'active_block_exists') {
        await loadGoals(activeSection);
        setGoalsError('Your focus block is already running. The timer has been refreshed.');
      } else {
        setGoalsError(err?.response?.data?.error || 'Failed to start work session.');
      }
      return false;
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const handleStartWork = async () => {
    const started = await startPlanWorkSession();
    if (!started) return;
    const startedAt = goalCycle?.work_started_at || new Date().toISOString();
    setShowStartWorkTransition(false);
    setWorkStatus('in_progress');
    await persistWorkStatus('in_progress', startedAt);
  };

  const handleEditPlan = () => {
    setShowStartWorkTransition(false);
    // Reset form to current plan for editing
    // (Form already has current values, just hide the transition)
  };

  const handleStartWorkBlock = async () => {
    await handleStartWork();
  };

  const saveCurrentFocus = async () => {
    if (!activeSection || !focusTitle.trim()) return;
    try {
      setFocusSaving(true);
      setGoalsError('');
      const label = focusKey === 'comptia'
        ? 'CompTIA'
        : focusKey === 'individual_project'
          ? primaryGoal?.title || 'Individual Project'
          : focusKey === 'authentic_work'
            ? 'Authentic Work'
            : 'Other';
      const payload = await goalsApi.updateWorkStatus(activeSection, {
        current_focus: {
          key: focusKey,
          label,
          title: focusTitle.trim(),
          origin: 'student',
        },
      });
      setGoalCycle(payload);
      setFocusEditing(false);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to update current focus.');
    } finally {
      setFocusSaving(false);
    }
  };

  const savePrimaryGoal = async () => {
    if (!activeSection) return;
    const title = primaryGoalTitle.trim();
    const whyText = primaryGoalWhy.trim();
    const targetMetricText = primaryGoalMetric.trim();
    const cycleStartDate = activePerformanceCycle?.start_date || primaryGoalStartDate;
    const cycleEndDate = activePerformanceCycle?.end_date || primaryGoalEndDate;
    if (!primaryGoalPurpose || !title || !whyText || !targetMetricText || !cycleStartDate || !cycleEndDate) {
      setPrimaryGoalError('Choose a goal purpose and fill in the title, why, target metric, and date range.');
      return;
    }
    if (cycleEndDate < cycleStartDate) {
      setPrimaryGoalError('End date must be on or after start date.');
      return;
    }
    setPrimaryGoalSaving(true);
    setPrimaryGoalError('');
    try {
      const payload = {
        section_id: activeSection,
        title,
        why_text: whyText,
        target_metric_text: targetMetricText,
        ...(seniorPriorityWorkflow ? { goal_track: selectedGoalTrack } : {}),
        goal_purpose: primaryGoalPurpose,
        start_date: cycleStartDate,
        end_date: cycleEndDate,
        ...(activePerformanceCycle ? { instructional_cycle: activePerformanceCycle.id } : {}),
      };
      const saved = primaryGoal
        ? await goalsApi.primaryGoal.update(primaryGoal.id, payload)
        : await goalsApi.primaryGoal.create(payload);
      const savedGoal = saved as StudentPrimaryGoal;
      goalDraft.clear();
      setPrimaryGoals((current) => [
        ...current.filter((row) => row.goal_track !== savedGoal.goal_track),
        savedGoal,
      ]);
      applyPrimaryGoalToForm(savedGoal);
      await loadWeeklyCheckpoint(activeSection);
      setPrimaryGoalEditing(false);
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      setPrimaryGoalError(
        apiError === 'active_primary_goal_exists'
          ? `You already have an active ${selectedGoalTrack === 'required_pathway' ? 'Main Goal' : 'Personal Project'} for this section.`
          : apiError || 'Failed to save goal.',
      );
    } finally {
      setPrimaryGoalSaving(false);
    }
  };

  const saveLongerTermDirection = async () => {
    if (!activeSection || !directionTitle.trim()) {
      setDirectionError('Add the larger direction or target.');
      return;
    }
    setDirectionSaving(true);
    setDirectionError('');
    try {
      const saved = await goalsApi.longerTermDirection.save({
        section_id: activeSection,
        title: directionTitle.trim(),
        why_text: directionWhy.trim(),
        target_metric_text: directionMetric.trim(),
      });
      directionDraftDirtyRef.current = false;
      setLongerTermDirection(saved as StudentLongerTermDirection);
      setDirectionEditing(false);
    } catch (err: any) {
      setDirectionError(err?.response?.data?.error || 'Failed to save longer-term direction.');
    } finally {
      setDirectionSaving(false);
    }
  };

  const convertLegacyGoalToDirection = async () => {
    if (!primaryGoal?.id || !activeSection) return;
    setDirectionSaving(true);
    setPrimaryGoalError('');
    try {
      await goalsApi.primaryGoal.convertToDirection(primaryGoal.id);
      await loadPrimaryGoal(activeSection);
    } catch (err: any) {
      setPrimaryGoalError(err?.response?.data?.error || 'Failed to preserve this goal as your longer-term direction.');
    } finally {
      setDirectionSaving(false);
    }
  };

  const selectFiveWeekGoalTrack = (track: 'required_pathway' | 'personal_project') => {
    const requiredGoal = primaryGoals.find((row) => row.goal_track === 'required_pathway');
    if (track === 'personal_project' && !requiredGoal) {
      setPrimaryGoalError('Create your Main Goal before adding a Personal Project goal.');
      return;
    }
    const nextGoal = primaryGoals.find((row) => row.goal_track === track) || null;
    setSelectedGoalTrack(track);
    applyPrimaryGoalToForm(nextGoal);
    setPrimaryGoalEditing(Boolean(activePerformanceCycle && !nextGoal));
  };

  const refreshMilestones = async (goalId: number) => {
    const rows = await goalsApi.primaryGoal.milestones.list(goalId);
    const nextRows = Array.isArray(rows) ? rows as GoalMilestone[] : [];
    setMilestones(nextRows);
    setPrimaryGoal((current) => current && current.id === goalId ? { ...current, milestones: nextRows } : current);
  };

  const createMilestone = async () => {
    if (!primaryGoal?.id || !milestoneOutcome.trim() || !milestoneDueDate) {
      setMilestoneError('Add the expected outcome and due date.');
      return;
    }
    try {
      setMilestoneSaving(true);
      setMilestoneError('');
      await goalsApi.primaryGoal.milestones.create(primaryGoal.id, {
        hook_stage: milestoneHookStage,
        expected_outcome: milestoneOutcome.trim(),
        due_date: milestoneDueDate,
        evidence_expected: milestoneEvidenceExpected,
      });
      setMilestoneOutcome('');
      setMilestoneHookStage('define');
      setMilestoneDueDate('');
      setMilestoneEvidenceExpected(false);
      await refreshMilestones(primaryGoal.id);
    } catch (err: any) {
      setMilestoneError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to save milestone.');
    } finally {
      setMilestoneSaving(false);
    }
  };

  const createHookStageMilestones = async () => {
    if (!primaryGoal?.id) return;
    const stageTemplates: Array<{ stage: Exclude<GoalMilestone['hook_stage'], ''>; label: string; ratio: number }> = [
      { stage: 'define', label: 'Define the technical problem, requirements, and success criteria.', ratio: 0.2 },
      { stage: 'do', label: 'Complete the planned technical work and document the process.', ratio: 0.5 },
      { stage: 'explain', label: 'Explain the technical decisions, process, and learning.', ratio: 0.75 },
      { stage: 'apply', label: 'Apply the skill independently in an authentic task or project.', ratio: 1 },
    ];
    const existingStages = new Set(milestones.map((row) => row.hook_stage).filter(Boolean));
    const start = new Date(`${primaryGoal.start_date}T12:00:00`);
    const end = new Date(`${primaryGoal.end_date}T12:00:00`);
    const span = Math.max(0, end.getTime() - start.getTime());
    try {
      setMilestoneSaving(true);
      setMilestoneError('');
      for (const template of stageTemplates.filter((row) => !existingStages.has(row.stage))) {
        const due = new Date(start.getTime() + (span * template.ratio));
        const configuredDays = (currentSection?.goal_days || []).filter((day) => day >= 0 && day <= 4);
        const allowedDays = new Set(configuredDays.length ? configuredDays : [0, 1, 2, 3, 4]);
        while (due > start && !allowedDays.has((due.getDay() + 6) % 7)) due.setDate(due.getDate() - 1);
        await goalsApi.primaryGoal.milestones.create(primaryGoal.id, {
          hook_stage: template.stage,
          expected_outcome: template.label,
          due_date: due.toISOString().slice(0, 10),
          evidence_expected: true,
        });
      }
      await refreshMilestones(primaryGoal.id);
    } catch (err: any) {
      setMilestoneError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to create Hook milestones.');
    } finally {
      setMilestoneSaving(false);
    }
  };

  const updateMilestone = async (
    milestone: GoalMilestone,
    payload: Parameters<typeof goalsApi.primaryGoal.milestones.update>[2],
  ) => {
    if (!primaryGoal?.id) return;
    try {
      setMilestoneError('');
      await goalsApi.primaryGoal.milestones.update(primaryGoal.id, milestone.id, payload);
      await refreshMilestones(primaryGoal.id);
    } catch (err: any) {
      setMilestoneError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to update milestone.');
    }
  };

  const attachGoalEvidence = async () => {
    if (!primaryGoal?.id || !goalEvidenceSource) return;
    const candidate = (goalEvidence?.available_sources || []).find(
      (row) => `${row.source_type}:${row.source_id}` === goalEvidenceSource,
    );
    if (!candidate) return;
    try {
      setGoalEvidenceSaving(true);
      setGoalEvidenceError('');
      const payload = await goalsApi.primaryGoal.evidenceLinks.create(primaryGoal.id, {
        source_type: candidate.source_type,
        source_id: candidate.source_id,
        milestone: goalEvidenceMilestone ? Number(goalEvidenceMilestone) : null,
        lesson_run: candidate.lesson_run || null,
      });
      setGoalEvidence(payload as GoalEvidencePayload);
      setGoalEvidenceSource('');
      setGoalEvidenceMilestone('');
    } catch (err: any) {
      setGoalEvidenceError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to attach evidence.');
    } finally {
      setGoalEvidenceSaving(false);
    }
  };

  const removeGoalEvidence = async (linkId: number) => {
    if (!primaryGoal?.id) return;
    try {
      setGoalEvidenceSaving(true);
      setGoalEvidenceError('');
      await goalsApi.primaryGoal.evidenceLinks.remove(primaryGoal.id, linkId);
      const payload = await goalsApi.primaryGoal.evidenceLinks.list(primaryGoal.id);
      setGoalEvidence(payload as GoalEvidencePayload);
    } catch (err: any) {
      setGoalEvidenceError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to remove evidence.');
    } finally {
      setGoalEvidenceSaving(false);
    }
  };

  const exportGoalEvidenceReport = async () => {
    if (!primaryGoal?.id) return;
    try {
      setGoalEvidenceExporting(true);
      setGoalEvidenceError('');
      const generated = await exportApi.generateGoalEvidence(primaryGoal.id);
      const fileResponse = await exportApi.download(generated.export_id);
      const blob = new Blob([fileResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      setGoalEvidenceError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to generate the goal evidence PDF.');
    } finally {
      setGoalEvidenceExporting(false);
    }
  };

  const applyNsrPayload = (payload: NSRCyclePayload) => {
    setNsrCycle(payload);
    setNsrPrimaryItem(payload.focus?.primary_item_key || '');
    setNsrSecondaryItem(payload.focus?.secondary_item_key || '');
    setNsrReflection(payload.focus?.student_reflection || '');
  };

  const saveNsrFocus = async () => {
    if (!nsrCycle || !nsrPrimaryItem) return;
    try {
      setNsrSaving(true);
      setNsrError('');
      const payload = await nsrApi.saveMyFocus(nsrCycle.cycle.id, {
        primary_item_key: nsrPrimaryItem,
        secondary_item_key: nsrSecondaryItem,
      });
      applyNsrPayload(payload as NSRCyclePayload);
    } catch (err: any) {
      setNsrError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to save Next Step Ready focus.');
    } finally {
      setNsrSaving(false);
    }
  };

  const saveNsrReflection = async () => {
    if (!nsrCycle?.focus) return;
    try {
      setNsrSaving(true);
      setNsrError('');
      const payload = await nsrApi.saveMyReflection(nsrCycle.cycle.id, nsrReflection);
      applyNsrPayload(payload as NSRCyclePayload);
    } catch (err: any) {
      setNsrError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to save Next Step Ready reflection.');
    } finally {
      setNsrSaving(false);
    }
  };

  const requestPrimaryGoalReview = async () => {
    if (!primaryGoal?.id || !primaryGoal.is_active || primaryGoal.closed_at) return;
    goalExampleSourceRef.current = primaryGoal;
    setGoalExamplesOpen(true);
    setGoalExampleNotice('');
    setPrimaryGoalReview(null);
    try {
      setPrimaryGoalReviewLoading(true);
      setPrimaryGoalReviewError('');
      const response = primaryGoal.definition_revision && primaryGoal.definition_digest
        ? await goalsApi.primaryGoal.aiReview(primaryGoal.id, primaryGoal.definition_revision, primaryGoal.definition_digest)
        : await goalsApi.primaryGoal.aiReview(primaryGoal.id);
      if (selectedGoalRef.current?.id !== primaryGoal.id
        || selectedGoalRef.current?.definition_digest !== primaryGoal.definition_digest
        || goalExampleSourceRef.current !== primaryGoal) return;
      if (response?.quality_evaluation && response.quality_evaluation.status !== 'completed') {
        setPrimaryGoalReviewError('Examples are temporarily unavailable. Your teacher can still review your goal, and you can keep working.');
        return;
      }
      const review = (response?.review || null) as PrimaryGoalAiReview | null;
      if (!review?.suggestion_text && !review?.suggested_revision) {
        setPrimaryGoalReviewError('No example was returned. Try again in a minute.');
        return;
      }
      setPrimaryGoalReview(review);
      if (response?.quality_workflow) {
        setPrimaryGoal((current) => current ? { ...current, quality_workflow: response.quality_workflow } : current);
        setPrimaryGoals((current) => current.map((goal) => goal.id === primaryGoal.id
          ? { ...goal, quality_workflow: response.quality_workflow } : goal));
      }
      setPrimaryGoalReviewEventId(Number(response?.event_id) || null);
    } catch (err: any) {
      setPrimaryGoalReviewError(err?.response?.data?.error || 'Failed to review this goal.');
    } finally {
      setPrimaryGoalReviewLoading(false);
    }
  };

  const acceptGoalExample = async () => {
    const source = goalExampleSourceRef.current;
    const title = primaryGoalReview?.suggested_revision?.trim();
    if (!source || !title || !primaryGoal?.is_active || primaryGoal.closed_at || goalExampleSaving) return;
    if (source.id !== primaryGoal.id || source.definition_digest !== primaryGoal.definition_digest
      || source.definition_revision !== primaryGoal.definition_revision) {
      setPrimaryGoalReviewError('Your goal changed. Get a new example before applying it.');
      return;
    }
    setGoalExampleSaving(true);
    setPrimaryGoalReviewError('');
    try {
      const saved = await goalsApi.primaryGoal.update(source.id, { title }) as StudentPrimaryGoal;
      setPrimaryGoals((current) => current.map((goal) => goal.id === saved.id ? saved : goal));
      if (selectedGoalRef.current?.id === source.id) {
        applyPrimaryGoalToForm(saved);
        setGoalExampleNotice('Example applied to your goal statement. Your dates, why, and target are unchanged.');
      }
    } catch (err: any) {
      if (selectedGoalRef.current?.id === source.id) {
        setPrimaryGoalReviewError(err?.response?.data?.error || 'Could not update your goal. Your saved goal is unchanged. Try again.');
      }
    } finally {
      setGoalExampleSaving(false);
    }
  };

  const goalExamplesButton = primaryGoal?.is_active && !primaryGoal.closed_at && aiGoalAssistFeatureEnabled ? (
    <Button type="button" size="sm" variant="secondary" className="mt-3" onClick={requestPrimaryGoalReview}
      disabled={primaryGoalReviewLoading || goalExampleSaving}>
      {primaryGoalReviewLoading ? 'Getting examples...' : 'Get examples'}
    </Button>
  ) : null;

  const requestDailyNudge = async () => {
    if (!activeSection || !primaryGoal?.id || !primaryGoal.is_active || !aiGoalAssistFeatureEnabled) return;
    try {
      setDailyNudgeLoading(true);
      setDailyNudgeError('');
      const response = await goalsApi.dailyNudge({ section_id: activeSection });
      const nudge = (response?.nudge || null) as DailyGoalAiNudge | null;
      if (!nudge?.focus_text && !nudge?.next_step_text) {
        setDailyNudgeError('AI did not return a daily nudge. Try again in a minute.');
        return;
      }
      setDailyNudge(nudge);
      setDailyNudgeEventId(Number(response?.event_id) || null);
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      setDailyNudgeError(
        apiError === 'active_primary_goal_required'
          ? 'Add a long-term goal first so the nudge has something to connect to.'
          : apiError === 'ai_daily_nudge_disabled'
            ? 'Daily AI nudges are not turned on for this section.'
            : apiError || 'Failed to get a daily nudge.',
      );
    } finally {
      setDailyNudgeLoading(false);
    }
  };

  const saveWeeklyCheckpoint = async () => {
    if (!activeSection || !primaryGoal) return;
    if (!weeklyProgressText.trim()) {
      setWeeklyCheckpointError('Add one short progress note for this week.');
      return;
    }
    if (weeklyConfidenceRating < 1 || weeklyConfidenceRating > 5) {
      setWeeklyCheckpointError('Choose a confidence rating from 1 to 5.');
      return;
    }
    setWeeklyCheckpointSaving(true);
    setWeeklyCheckpointError('');
    try {
      const payload = {
        section_id: activeSection,
        progress_text: weeklyProgressText.trim(),
        evidence_text: weeklyEvidenceText.trim(),
        obstacle_text: weeklyObstacleText.trim(),
        adjustment_text: weeklyAdjustmentText.trim(),
        confidence_rating: weeklyConfidenceRating,
      };
      const response = weeklyCheckpoint
        ? await goalsApi.weeklyCheckpoint.update(payload)
        : await goalsApi.weeklyCheckpoint.save(payload);
      applyWeeklyCheckpointPayload(response);
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      const availability = err?.response?.data?.availability;
      setWeeklyCheckpointError(
        apiError === 'active_primary_goal_required'
          ? 'Add a long-term goal before saving a weekly checkpoint.'
          : apiError === 'weekly_checkpoint_not_available'
            ? String(availability?.label || 'Weekly checkpoint is not open yet.')
          : apiError || 'Failed to save weekly checkpoint.',
      );
    } finally {
      setWeeklyCheckpointSaving(false);
    }
  };

  const saveGoalFinalReflection = async () => {
    if (!primaryGoal) return;
    if (!goalEvidenceText.trim() || !goalWhatWorkedText.trim() || !goalNextTimeChangeText.trim()) {
      setGoalClosureError('Add evidence, what worked, and one change for next time.');
      return;
    }
    setGoalClosureSaving(true);
    setGoalClosureError('');
    try {
      const payload = {
        outcome_status: goalOutcomeStatus,
        evidence_text: goalEvidenceText.trim(),
        what_worked_text: goalWhatWorkedText.trim(),
        obstacle_text: goalObstacleText.trim(),
        next_time_change_text: goalNextTimeChangeText.trim(),
        nsr_focus_reflection_text: goalNsrFocusReflectionText.trim(),
        next_goal_direction_text: goalNextDirectionText.trim(),
      };
      const response = goalFinalReflection
        ? await goalsApi.primaryGoal.finalReflection.update(primaryGoal.id, payload)
        : await goalsApi.primaryGoal.finalReflection.save(primaryGoal.id, payload);
      applyPrimaryGoalToForm((response?.primary_goal || primaryGoal) as StudentPrimaryGoal);
      applyGoalFinalReflection((response?.final_reflection || response?.saved_reflection || null) as GoalFinalReflection | null);
      setReviewGoals((goals) => goals.map((goal) => (
        goal.id === primaryGoal.id ? { ...goal, final_reflection_submitted: true } : goal
      )));
      clearWeeklyCheckpoint();
    } catch (err: any) {
      setGoalClosureError(err?.response?.data?.error || 'Failed to close this goal.');
    } finally {
      setGoalClosureSaving(false);
    }
  };

  const openFiveWeekReflection = async (goal: StudentPrimaryGoal) => {
    setActiveTab('goals');
    setReviewingGoalId(goal.id);
    applyPrimaryGoalToForm(goal);
    clearGoalFinalReflection();
    setPerformanceReview(null);
    setGoalClosureError('');
    setGoalClosureOpen(true);
    try {
      const reflectionPayload = await goalsApi.primaryGoal.finalReflection.get(goal.id);
      applyGoalFinalReflection((reflectionPayload?.final_reflection || null) as GoalFinalReflection | null);
    } catch (err: any) {
      setGoalClosureError(err?.response?.data?.detail || err?.response?.data?.error || 'Failed to load the five-week reflection.');
    }
    if (goal.instructional_cycle) {
      try {
        const reviewPayload = await performanceReviewApi.my(goal.id);
        setPerformanceReview(reviewPayload as FiveWeekPerformanceReviewPayload);
      } catch (err: any) {
        if (Number(err?.response?.status) !== 404) {
          setGoalClosureError(err?.response?.data?.detail || 'Failed to load the five-week review.');
        }
      }
    }
    setGoalClosureOpen(true);
    window.setTimeout(() => {
      document.getElementById('five-week-reflection')?.scrollIntoView?.({ block: 'start' });
    }, 0);
  };

  const returnToCurrentGoal = async () => {
    if (!activeSection) return;
    setGoalClosureOpen(false);
    setReviewingGoalId(null);
    await loadGoals(activeSection);
  };

  const handlePauseWork = async () => {
    if (!activeSection || !activeWorkBlock) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.update({ section_id: activeSection, block_id: activeWorkBlock.id, action: 'pause' });
      setWorkStatus('paused');
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to pause the focus block.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const handleCompleteWork = async () => {
    if (!activeSection || !currentWorkBlock) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.update({ section_id: activeSection, block_id: currentWorkBlock.id, action: 'complete' });
      setWorkStatus('completed');
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to finish the focus block.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const submitCheckIn = async () => {
    if (!activeSection) return;
    const seniorPriorityWorkflow = usesSeniorPriorityWorkflow(goalPolicy);
    const requiredGoal = primaryGoals.find((row) => row.goal_track === 'required_pathway');
    if (seniorPriorityWorkflow && activePerformanceCycle && !requiredGoal) {
      setGoalsError('Create your five-week Main Goal before starting today\'s work.');
      return;
    }
    if (!seniorPriorityWorkflow && !dailyPlan.track) {
      setGoalsError('Choose what track you are working on today.');
      return;
    }
    if (!dailyPlan.target) {
      setGoalsError('Add what you will complete today.');
      return;
    }
    if (!dailyPlan.first_step) {
      setGoalsError('Add one small action you can do right away to get started.');
      return;
    }
    if (!dailyPlan.success_criteria) {
      setGoalsError('Add a success check so you know when you are done.');
      return;
    }
    const personalGoal = primaryGoals.find((row) => row.goal_track === 'personal_project');
    if (seniorPriorityWorkflow && personalGoal && (!personalProjectTask.trim() || !personalProjectProof.trim())) {
      setGoalsError('Add today\'s project task and the project evidence you expect to show.');
      return;
    }
    if (!seniorPriorityWorkflow && aiApplied && !aiRationale.trim()) {
      setGoalsError('AI rationale is required when applying an AI goal suggestion.');
      return;
    }
    try {
      setSubmittingCheckIn(true);
      setGoalsError('');
      const payload = await goalsApi.checkIn({
        section_id: activeSection,
        start_work: seniorPriorityWorkflow ? true : undefined,
        current_focus: seniorPriorityWorkflow ? seniorFocusPreview : undefined,
        primary_goal_id: seniorPriorityWorkflow
          ? requiredGoal?.id
          : primaryGoal?.is_active ? primaryGoal.id : undefined,
        connect_primary_goal: seniorPriorityWorkflow
          ? Boolean(requiredGoal)
          : Boolean(primaryGoal?.is_active),
        learning_goal_text: finalLearningGoalText,
        completion_goal_text: composedCompletionGoalText,
        daily_plan: dailyPlan,
        plan_track: dailyPlan.track,
        plan_target: dailyPlan.target,
        plan_first_step: dailyPlan.first_step,
        plan_success_criteria: dailyPlan.success_criteria,
        plan_work_item: dailyPlan.work_item,
        project_plan_target: seniorPriorityWorkflow ? personalProjectTask.trim() : undefined,
        project_plan_success_criteria: seniorPriorityWorkflow ? personalProjectProof.trim() : undefined,
        protected_goal_minutes: protectedGoalMinutes ? Number(protectedGoalMinutes) : undefined,
        planned_secondary_work: plannedSecondaryWork.trim() || undefined,
        planned_work_mode: plannedWorkMode || undefined,
        learning_goal_builder: learningGoalBuilder,
        first_step_text: learningGoalBuilder.first_step,
        completion_goal_builder: {
          mode: selectedCompletionWorkItem ? 'work_item' : 'manual',
          project_role: completionProjectRole || undefined,
          selected_work_item: selectedCompletionWorkItem
            ? {
                kind: selectedCompletionWorkItem.kind,
                id: selectedCompletionWorkItem.id,
                title: selectedCompletionWorkItem.title,
                source: selectedCompletionWorkItem.source,
                track_key: selectedCompletionWorkItem.trackKey,
                project_role: completionProjectRole || undefined,
              }
            : null,
          manual_text: selectedCompletionWorkItem ? '' : dailyPlan.target,
        },
        learning_stage_tag: learningStageTag || undefined,
        learning_readiness_tags: parseTags(learningTags),
        completion_readiness_tags: parseTags(completionTags),
        learning_ai_assisted: aiApplied,
        learning_ai_rationale: aiApplied ? aiRationale.trim() : undefined,
      });
      setGoalCycle(payload);
      const learningGoal = (payload?.goals || []).find((goal: any) => goal.goal_type === 'learning');
      setAiApplied(Boolean(learningGoal?.ai_assisted));
      setAiRationale(String(learningGoal?.ai_rationale || ''));
      setAiSuggestion(null);
      setAiSuggestionEventId(null);

      if (seniorPriorityWorkflow && protectedGoalMinutes && !activeWorkBlock) {
        try {
          const startedPayload = await goalsApi.externalWorkBlocks.start({
            section_id: activeSection,
            title: dailyPlan.target,
            source_app: 'Main Goal',
            planned_minutes: Number(protectedGoalMinutes),
          });
          if (startedPayload?.block) {
            setExternalWorkBlocks((current) => [startedPayload.block, ...current.filter((block) => block.id !== startedPayload.block.id)]);
          }
        } catch {
          setGoalsError('Your check-in was saved, but the focus timer did not start. Use Start Focus Block to try again.');
        }
      }

      // Seniors use one action: save direction and move directly into work.
      setShowStartWorkTransition(!seniorPriorityWorkflow);
      setWorkStatus(seniorPriorityWorkflow ? 'in_progress' : 'not_started');

      await loadGoals(activeSection);
      if (seniorPriorityWorkflow) {
        setActiveTab(redesignEnabled ? 'today' : 'work');
      }
    } catch (err: any) {
      const errorCode = String(err?.response?.data?.error || '');
      setGoalsError(err?.response?.data?.detail || errorCode || 'Failed to submit check-in.');
    } finally {
      setSubmittingCheckIn(false);
    }
  };

  const runAiAssist = async () => {
    if (!activeSection || !dailyPlan.target.trim()) return;
    try {
      setAiAssistLoading(true);
      setGoalsError('');
      const response = await goalsApi.aiAssist({
        section_id: activeSection,
        goal_text: composedCompletionGoalText || composedLearningGoalText,
        daily_plan: dailyPlan,
        goal_builder: learningGoalBuilder,
        first_step_text: dailyPlan.first_step,
        stage_tag: learningStageTag || undefined,
      });
      const suggested = response?.suggestion;
      const suggestedText = String(suggested?.refined_target || suggested?.refined_goal || '').trim();
      if (suggestedText) {
        setAiSuggestion(suggested as GoalAiSuggestion);
        setAiSuggestionEventId(Number(response?.event_id) || null);
        setAiRationale('');
        setAiApplied(false);
      } else {
        setGoalsError('AI did not return a usable suggestion.');
      }
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to generate AI suggestion.');
    } finally {
      setAiAssistLoading(false);
    }
  };

  const applyAiSuggestion = async () => {
    if (!activeSection || !aiSuggestion?.refined_goal?.trim()) return;
    if (!aiSuggestionEventId) {
      setGoalsError('Refresh AI suggestion before applying so the decision can be recorded.');
      return;
    }
    if (!aiRationale.trim()) {
      setGoalsError('Add a short rationale before applying AI suggestion.');
      return;
    }
    try {
      setAiAssistLoading(true);
      setGoalsError('');
      await goalsApi.aiAssist({
        section_id: activeSection,
        event_id: aiSuggestionEventId,
        accepted: true,
        rationale_text: aiRationale.trim(),
      });
      setAiRefinedLearningGoalText(aiSuggestion.refined_goal.trim());
      if (aiSuggestion.refined_target?.trim()) {
        setPlanTarget(aiSuggestion.refined_target.trim());
      }
      if (aiSuggestion.suggested_first_step?.trim()) {
        setFirstStepText(aiSuggestion.suggested_first_step.trim());
      }
      if (aiSuggestion.suggested_success_criteria?.trim()) {
        setPlanSuccessCriteria(aiSuggestion.suggested_success_criteria.trim());
      } else if (aiSuggestion.measurable_criterion?.trim()) {
        setPlanSuccessCriteria(aiSuggestion.measurable_criterion.trim());
      }
      setAiApplied(true);
      setAiSuggestion(null);
      setAiSuggestionEventId(null);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to apply AI suggestion.');
    } finally {
      setAiAssistLoading(false);
    }
  };

  const dismissAiSuggestion = async () => {
    if (!activeSection || !aiSuggestion?.refined_goal?.trim()) {
      setAiSuggestion(null);
      setAiSuggestionEventId(null);
      return;
    }
    if (!aiSuggestionEventId) {
      setGoalsError('Refresh AI suggestion before dismissing so the decision can be recorded.');
      return;
    }
    if (!aiRationale.trim()) {
      setGoalsError('Add a short rationale before dismissing AI suggestion.');
      return;
    }
    try {
      setAiAssistLoading(true);
      setGoalsError('');
      await goalsApi.aiAssist({
        section_id: activeSection,
        event_id: aiSuggestionEventId,
        accepted: false,
        rationale_text: aiRationale.trim(),
      });
      setAiApplied(false);
      setAiRefinedLearningGoalText('');
      setAiRationale('');
      setAiSuggestion(null);
      setAiSuggestionEventId(null);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to dismiss AI suggestion.');
    } finally {
      setAiAssistLoading(false);
    }
  };

  const createExternalTask = async () => {
    if (!activeSection || !externalTitle.trim()) return;
    try {
      setExternalCreating(true);
      setGoalsError('');
      await goalsApi.externalTasks.create({
        section_id: activeSection,
        title: externalTitle.trim(),
        source_app: externalSource.trim() || undefined,
      });
      setExternalTitle('');
      setExternalSource('');
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to add external task.');
    } finally {
      setExternalCreating(false);
    }
  };

  const updateExternalStatus = async (entryId: number, nextStatus: 'not_started' | 'in_progress' | 'completed') => {
    if (!activeSection) return;
    try {
      setGoalsError('');
      await goalsApi.externalTasks.update({
        section_id: activeSection,
        entry_id: entryId,
        status: nextStatus,
        evidence_note: nextStatus === 'completed' ? 'Completed in external app.' : undefined,
      });
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to update external task.');
    }
  };

  const startExternalWorkBlock = async () => {
    if (!activeSection) return;
    if (activeWorkBlock) {
      setGoalsError('Stop or complete the current block before starting another one.');
      return;
    }
    if (!startWorkTaskId && !startWorkTitle.trim()) {
      setGoalsError('Choose existing work or start from today\'s plan.');
      return;
    }
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.start({
        section_id: activeSection,
        external_task_entry_id: startWorkTaskId ? Number(startWorkTaskId) : undefined,
        title: startWorkTaskId ? undefined : startWorkTitle.trim(),
        source_app: startWorkSource.trim() || undefined,
        planned_minutes: Number.isFinite(startWorkPlannedMinutes) ? Number(startWorkPlannedMinutes) : undefined,
      });
      setSwitchTaskId('');
      setSwitchTitle('');
      setSwitchSource('');
      setSwitchPlannedMinutes(30);
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to start work session.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const startExternalWorkFromTask = async (entryId: number, title: string, sourceApp: string) => {
    if (!activeSection) return;
    if (activeWorkBlock) {
      setGoalsError('Stop or complete the current block before starting another one.');
      return;
    }
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.start({
        section_id: activeSection,
        external_task_entry_id: entryId,
        title,
        source_app: sourceApp || undefined,
        planned_minutes: Number.isFinite(startWorkPlannedMinutes) ? Number(startWorkPlannedMinutes) : undefined,
      });
      setStartWorkTaskId(String(entryId));
      setStartWorkTitle(title);
      setStartWorkSource(sourceApp || '');
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to start work session from outside work.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const addExternalWorkMinutes = async () => {
    if (!activeSection || !activeWorkBlock) return;
    const parsedDelta = Number(minutesDelta);
    if (!Number.isFinite(parsedDelta) || parsedDelta <= 0) {
      setGoalsError('Minutes delta must be a positive number.');
      return;
    }
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.update({
        section_id: activeSection,
        block_id: activeWorkBlock.id,
        action: 'add_minutes',
        minutes_delta: Math.floor(parsedDelta),
      });
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to add minutes.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const saveExternalWorkEvidence = async () => {
    if (!activeSection || !activeWorkBlock) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.update({
        section_id: activeSection,
        block_id: activeWorkBlock.id,
        action: 'evidence',
        evidence_note: blockEvidenceNote,
        evidence_link: blockEvidenceLink,
      });
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to save evidence.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const stopExternalWorkBlock = async (complete: boolean) => {
    if (!activeSection || !activeWorkBlock) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.update({
        section_id: activeSection,
        block_id: activeWorkBlock.id,
        action: complete ? 'complete' : 'stop',
        evidence_note: blockEvidenceNote,
        evidence_link: blockEvidenceLink,
      });
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || `Failed to ${complete ? 'complete' : 'stop'} block.`);
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const switchExternalWorkBlock = async () => {
    if (!activeSection || !activeWorkBlock) return;
    if (!switchTaskId && !switchTitle.trim()) {
      setGoalsError('Choose existing work or describe the next work session.');
      return;
    }
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkEvents.log({
        section_id: activeSection,
        block_id: activeWorkBlock.id,
        event_type: 'switch',
        external_task_entry_id: switchTaskId ? Number(switchTaskId) : undefined,
        title: switchTaskId ? undefined : switchTitle.trim(),
        source_app: switchSource.trim() || undefined,
        planned_minutes: Number.isFinite(switchPlannedMinutes) ? Number(switchPlannedMinutes) : undefined,
      });
      setSwitchTaskId('');
      setSwitchTitle('');
      setSwitchSource('');
      setSwitchPlannedMinutes(30);
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to switch work session.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const extendMainGoalWork = async (minutes: number) => {
    if (!activeSection || !activeWorkBlock) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.update({ section_id: activeSection, block_id: activeWorkBlock.id, action: 'extend', minutes_delta: minutes });
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to extend the focus block.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const resumeMainGoalWork = async () => {
    if (!activeSection || !pausedWorkBlock) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkBlocks.update({ section_id: activeSection, block_id: pausedWorkBlock.id, action: 'resume' });
      setWorkStatus('in_progress');
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to resume the focus block.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const requestFocusHelp = async () => {
    if (!activeSection || !currentWorkBlock) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      setFocusStatusMessage('');
      await goalsApi.externalWorkBlocks.update({ section_id: activeSection, block_id: currentWorkBlock.id, action: 'request_support' });
      setFocusStatusMessage('Help requested. Your teacher can now see the request on the Goals roster.');
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to request help.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const updateFocusProgress = () => {
    document.getElementById('pathway-evidence-text')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => document.getElementById('pathway-evidence-text')?.focus(), 300);
  };

  const moveToPlannedSecondaryWork = async () => {
    if (!activeSection || !activeWorkBlock || !plannedSecondaryWork.trim()) return;
    try {
      setExternalWorkActionLoading(true);
      setGoalsError('');
      await goalsApi.externalWorkEvents.log({
        section_id: activeSection,
        block_id: activeWorkBlock.id,
        event_type: 'switch',
        title: plannedSecondaryWork.trim(),
        source_app: plannedWorkMode ? plannedWorkMode.replace(/_/g, ' ') : 'Planned secondary work',
        planned_minutes: protectedGoalMinutes ? Math.max(10, 85 - Number(protectedGoalMinutes)) : 30,
      });
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to switch to the planned secondary work.');
    } finally {
      setExternalWorkActionLoading(false);
    }
  };

  const submitCheckOut = async () => {
    if (!activeSection) return;
    const seniorPriorityWorkflow = usesSeniorPriorityWorkflow(goalPolicy);
    if (seniorPriorityWorkflow && !priorityResult) {
      setGoalsError('Choose the end-of-class result for today\'s priority.');
      return;
    }
    const personalGoal = primaryGoals.find((row) => row.goal_track === 'personal_project');
    if (seniorPriorityWorkflow && !pathwayEvidenceText.trim()) {
      setGoalsError('Add evidence of progress, or explain why progress was blocked.');
      return;
    }
    if (seniorPriorityWorkflow && personalGoal && !projectResult) {
      setGoalsError('Choose the end-of-class result for the project track.');
      return;
    }
    if (seniorPriorityWorkflow && personalGoal && !projectEvidenceText.trim()) {
      setGoalsError('Add project evidence, or explain why project progress was blocked.');
      return;
    }
    if (!seniorPriorityWorkflow && !checkoutGoalStatus) {
      setGoalsError('Choose whether you met the goal: Yes, Partial, or No.');
      return;
    }
    if (!seniorPriorityWorkflow && !checkoutReflection.trim()) {
      setGoalsError('Add a short reason before check-out.');
      return;
    }
    if (!seniorPriorityWorkflow && goalCycle?.primary_goal && !movedTowardPrimaryGoal) {
      setGoalsError('Choose whether today helped you move toward your long-term goal.');
      return;
    }
    try {
      setSubmittingCheckOut(true);
      setGoalsError('');
      const payload = await goalsApi.checkOut({
        section_id: activeSection,
        reflection_text: seniorPriorityWorkflow ? undefined : checkoutReflection.trim(),
        goal_status: seniorPriorityWorkflow ? undefined : checkoutGoalStatus || undefined,
        priority_result: seniorPriorityWorkflow ? priorityResult || undefined : undefined,
        pathway_evidence_text: seniorPriorityWorkflow ? pathwayEvidenceText.trim() : undefined,
        pathway_evidence_url: seniorPriorityWorkflow ? pathwayEvidenceUrl.trim() || undefined : undefined,
        project_result: seniorPriorityWorkflow && personalGoal ? projectResult || undefined : undefined,
        project_evidence_text: seniorPriorityWorkflow && personalGoal ? projectEvidenceText.trim() : undefined,
        project_evidence_url: seniorPriorityWorkflow && personalGoal ? projectEvidenceUrl.trim() || undefined : undefined,
        project_portfolio_candidate: seniorPriorityWorkflow && personalGoal ? projectPortfolioCandidate : undefined,
        next_step_text: checkoutNextStep.trim() || undefined,
        plan_change_reason: planChangeReason || undefined,
        plan_change_note: planChangeNote.trim() || undefined,
        moved_toward_primary_goal: movedTowardPrimaryGoal
          ? movedTowardPrimaryGoal === 'yes'
          : undefined,
        completion_rating: seniorPriorityWorkflow ? undefined : completionRating,
        confidence_rating: seniorPriorityWorkflow ? undefined : confidenceRating,
      });
      setGoalCycle(payload);
      await loadGoals(activeSection);
    } catch (err: any) {
      setGoalsError(err?.response?.data?.error || 'Failed to submit check-out.');
    } finally {
      setSubmittingCheckOut(false);
    }
  };

  const primaryGoalClosed = Boolean(primaryGoal && !primaryGoal.is_active);
  const goalClosureContent = primaryGoal && finalReflectionFeatureEnabled ? (
    <div className={redesignEnabled ? 'mt-4 rounded-xl border border-border bg-surface-alt/50 p-4' : 'mt-4 rounded border border-slate-200 bg-white p-3'}>
      {primaryGoalClosed && goalFinalReflection ? (
        <div className="space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">Goal closed</Badge>
            <span className="text-muted">Outcome: {goalFinalReflection.outcome_status.replace(/_/g, ' ')}</span>
          </div>
          <p><span className="font-semibold">Evidence:</span> {goalFinalReflection.evidence_text}</p>
          <p><span className="font-semibold">What worked:</span> {goalFinalReflection.what_worked_text}</p>
          {goalFinalReflection.obstacle_text && (
            <p><span className="font-semibold">What got in the way:</span> {goalFinalReflection.obstacle_text}</p>
          )}
          <p><span className="font-semibold">Next time:</span> {goalFinalReflection.next_time_change_text}</p>
          {goalFinalReflection.nsr_focus_reflection_text && (
            <p><span className="font-semibold">NSR focus reflection:</span> {goalFinalReflection.nsr_focus_reflection_text}</p>
          )}
          {goalFinalReflection.next_goal_direction_text && (
            <p><span className="font-semibold">Next direction:</span> {goalFinalReflection.next_goal_direction_text}</p>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => setGoalClosureOpen(true)}>
              Edit Reflection
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                applyPrimaryGoalToForm(null);
                clearGoalFinalReflection();
                clearWeeklyCheckpoint();
              }}
            >
              Start New Goal
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-text">{seniorPriorityWorkflow ? 'Five-week goal reflection' : 'Goal closure'}</p>
            <p className="mt-1 text-sm text-muted">{seniorPriorityWorkflow ? 'When this five-week goal is finished, reflect on your evidence. Submitting the reflection closes this goal; it does not submit your daily wrap-up or finish your longer-term direction.' : 'Close the loop when this goal is finished or ready to reset.'}</p>
          </div>
          {!(seniorPriorityWorkflow && redesignEnabled) && (
            <Button type="button" size="sm" variant="secondary" onClick={() => setGoalClosureOpen((open) => !open)}>
              {goalClosureOpen ? 'Hide Reflection' : seniorPriorityWorkflow ? 'Finish this five-week goal' : 'Close Goal'}
            </Button>
          )}
        </div>
      )}
      {goalClosureOpen && (
        <div className="mt-4 space-y-3">
          {goalClosureError && (
            <div className={redesignEnabled ? 'rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger' : 'rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700'}>
              {goalClosureError}
            </div>
          )}
          <label className="block text-sm">
            Outcome
            <select
              className="mt-1 w-full rounded border px-3 py-2"
              value={goalOutcomeStatus}
              onChange={(event) => setGoalOutcomeStatus(event.target.value as 'met' | 'partially_met' | 'not_met')}
            >
              <option value="met">Met</option>
              <option value="partially_met">Partially met</option>
              <option value="not_met">Not met yet</option>
            </select>
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm">
              {performanceReview ? 'What is your strongest technical evidence?' : 'What evidence shows that?'}
              <textarea
                className="mt-1 w-full rounded border px-3 py-2"
                value={goalEvidenceText}
                onChange={(event) => setGoalEvidenceText(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              {performanceReview ? 'What meaningful technical work did you complete?' : 'What helped?'}
              <textarea
                className="mt-1 w-full rounded border px-3 py-2"
                value={goalWhatWorkedText}
                onChange={(event) => setGoalWhatWorkedText(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              What got in the way with this goal?
              <textarea
                className="mt-1 w-full rounded border px-3 py-2"
                value={goalObstacleText}
                onChange={(event) => setGoalObstacleText(event.target.value)}
              />
            </label>
            <label className="block text-sm">
              What would you change next time?
              <textarea
                className="mt-1 w-full rounded border px-3 py-2"
                value={goalNextTimeChangeText}
                onChange={(event) => setGoalNextTimeChangeText(event.target.value)}
              />
            </label>
            {performanceReview && (
              <label className="block text-sm md:col-span-2">
                How did you grow in your selected NSR focus?
                <textarea
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={goalNsrFocusReflectionText}
                  onChange={(event) => setGoalNsrFocusReflectionText(event.target.value)}
                />
              </label>
            )}
            <label className="block text-sm md:col-span-2">
              What should your next goal focus on?
              <input
                className="mt-1 w-full rounded border px-3 py-2"
                value={goalNextDirectionText}
                onChange={(event) => setGoalNextDirectionText(event.target.value)}
              />
            </label>
          </div>
          <Button type="button" onClick={saveGoalFinalReflection} disabled={goalClosureSaving}>
            {goalClosureSaving ? 'Saving...' : goalFinalReflection ? 'Update Final Reflection' : seniorPriorityWorkflow ? 'Submit reflection and close goal' : 'Submit Final Reflection'}
          </Button>
        </div>
      )}
    </div>
  ) : null;

  const finalizedPerformanceReview = performanceReview?.review?.status === 'finalized'
    ? performanceReview.review
    : null;
  const performanceReviewContent = finalizedPerformanceReview ? (
    <div className={redesignEnabled ? 'mt-4 rounded-xl border border-border bg-surface-alt/50 p-4' : 'mt-4 rounded border border-slate-200 bg-white p-3'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-text">Finalized five-week performance review</p>
        <Badge variant="success">Teacher finalized</Badge>
      </div>
      <p className="mt-1 text-xs text-muted">Teacher determinations are separate from your reported completion and do not create a combined score or routine NSR rating.</p>
      {Array.isArray(performanceReview?.context?.cycle_goals) && (performanceReview?.context?.cycle_goals?.length || 0) > 0 && (
        <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
          {(performanceReview?.context?.cycle_goals || []).map((goal: any) => (
            <div key={goal.id} className="rounded border border-border bg-surface p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">{goal.goal_track === 'required_pathway' ? 'Main Goal' : 'Personal Project'}</p>
              <p className="mt-1 font-semibold text-text">{goal.title}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
        <div><span className="font-semibold">Technical performance:</span> {finalizedPerformanceReview.technical_performance_summary}</div>
        <div><span className="font-semibold">Technical determination:</span> {finalizedPerformanceReview.technical_determination}</div>
        <div><span className="font-semibold">Evidence verification:</span> {finalizedPerformanceReview.evidence_verification_summary}</div>
        <div><span className="font-semibold">NSR growth:</span> {finalizedPerformanceReview.nsr_growth_summary}</div>
        <div><span className="font-semibold">Next technical priority:</span> {finalizedPerformanceReview.next_technical_priority}</div>
        <div><span className="font-semibold">Next NSR direction:</span> {finalizedPerformanceReview.next_nsr_direction}</div>
      </div>
    </div>
  ) : null;

  const primaryGoalReviewContent = primaryGoal && primaryGoal.is_active && !primaryGoal.closed_at && aiGoalAssistFeatureEnabled && goalExamplesOpen ? (
    <section aria-label="Goal example" className={redesignEnabled ? 'mt-4 rounded-xl border border-border bg-surface-alt/50 p-4' : 'mt-4 rounded border border-slate-200 bg-white p-3'}>
      <p className="text-sm font-semibold text-text">An example for your goal</p>
      <p className="mt-1 text-xs text-muted">Generated with AI from your current goal. Check that it fits your plans before using it. Your teacher reviews goal changes.</p>
      {primaryGoalReviewLoading && <p role="status" className="mt-3 text-sm text-muted">Getting an example…</p>}
      {primaryGoalReviewError && (
        <div role="alert" className={redesignEnabled ? 'mt-3 rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger' : 'mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700'}>
          {primaryGoalReviewError}
        </div>
      )}
      {primaryGoalReview && (
        <div className="mt-4 space-y-3 text-sm">
          {(primaryGoalReview.strengths || []).length > 0 && (
            <div>
              <p className="font-semibold text-text">What is working</p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-muted">
                {(primaryGoalReview.strengths || []).slice(0, 3).map((item, index) => (
                  <li key={`primary-goal-strength-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {(primaryGoalReview.concerns || []).length > 0 && (
            <div>
              <p className="font-semibold text-text">Optional improvement</p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-muted">
                {(primaryGoalReview.concerns || []).slice(0, 3).map((item, index) => (
                  <li key={`primary-goal-concern-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-muted">
            <span className="font-semibold text-text">Suggestion:</span> {primaryGoalReview.suggestion_text}
          </p>
          {primaryGoalReview.suggested_revision?.trim() && (
            <div className={redesignEnabled ? 'rounded-xl border border-border bg-surface px-4 py-3' : 'rounded border border-slate-200 bg-slate-50 px-3 py-2'}>
              <p className="font-semibold text-text">Example goal statement</p>
              <p className="mt-1 text-muted">{primaryGoalReview.suggested_revision}</p>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="mt-2"
                onClick={acceptGoalExample}
                disabled={goalExampleSaving}
              >
                {goalExampleSaving ? 'Applying...' : 'Use this example'}
              </Button>
              <p className="mt-2 text-xs text-muted">Replaces only your goal statement. Your dates, why, and target stay the same.</p>
            </div>
          )}
          {primaryGoalReviewEventId && (
            <p className="text-xs text-muted">Feedback saved for review history.</p>
          )}
        </div>
      )}
      <Button type="button" size="sm" variant="ghost" className="mt-3" disabled={goalExampleSaving}
        onClick={() => { setGoalExamplesOpen(false); setPrimaryGoalReview(null); setPrimaryGoalReviewError(''); goalExampleSourceRef.current = null; }}>
        Keep my goal
      </Button>
    </section>
  ) : null;

  const practiceStandardsContent = primaryGoal?.is_active && primaryGoal.goal_purpose === 'technical_cte' ? (
    <PracticeStandardsExplorer
      goal={primaryGoal}
      onChange={(practiceStandards) => {
        setPrimaryGoal((current) => current ? { ...current, practice_standards: practiceStandards } : current);
        setPrimaryGoals((current) => current.map((row) => row.id === primaryGoal.id ? { ...row, practice_standards: practiceStandards } : row));
      }}
    />
  ) : null;

  const milestonesEditable = Boolean(
    primaryGoal?.is_active
    && !primaryGoal?.closed_at
    && primaryGoal?.instructional_cycle
    && primaryGoal?.instructional_cycle_detail?.state === 'active',
  );
  const milestonePanel = primaryGoal?.instructional_cycle ? (
    <SectionCard
      title="Goal Milestones"
      meta="Open one milestone to edit it or submit it for teacher review. Text edits save when you leave a field; selections save immediately. Connect saved work in the portfolio below."
    >
      <div className="space-y-4">
        {milestoneError && (
          <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
            {typeof milestoneError === 'string' ? milestoneError : 'Failed to update milestone.'}
          </div>
        )}
        {milestones.length === 0 ? (
          <div className="rounded-xl border border-info/20 bg-info/10 p-4 text-sm text-text">
            <p className="font-semibold">Set up your four Hook stages</p>
            <p className="mt-1 text-muted">Create a starting milestone for DEFINE, DO, EXPLAIN, and APPLY. You can personalize each outcome and due date.</p>
            {milestonesEditable && <Button className="mt-3" type="button" onClick={createHookStageMilestones} disabled={milestoneSaving}>Create Hook Milestones</Button>}
          </div>
        ) : (
          <div className="space-y-3">
            {milestones.map((milestone) => (
              <details key={milestone.id} className="rounded-xl border border-border bg-surface-alt/40 p-4">
                <summary className="cursor-pointer text-sm">
                  <span className="font-semibold">{(milestone.hook_stage || 'Other').toUpperCase()} · {milestone.expected_outcome}</span>
                  <span className="mt-1 block text-xs text-muted">Due {formatDate(milestone.due_date)} · {milestone.status.replace(/_/g, ' ')} · {milestone.verification_status === 'verified' ? 'Teacher reviewed' : milestone.verification_status === 'pending' ? 'Waiting for teacher review' : milestone.verification_status === 'needs_revision' ? 'Needs changes' : 'Not submitted'}</span>
                </summary>
                <div className="mt-4 grid gap-3 md:grid-cols-[140px_minmax(0,1fr)_180px]">
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`milestone-stage-${milestone.id}`}>Hook stage</FieldLabel>
                    <Select id={`milestone-stage-${milestone.id}`} value={milestone.hook_stage || ''} disabled={!milestonesEditable} onChange={(event) => void updateMilestone(milestone, { hook_stage: event.target.value as GoalMilestone['hook_stage'] })}>
                      <option value="">Other</option><option value="define">DEFINE</option><option value="do">DO</option><option value="explain">EXPLAIN</option><option value="apply">APPLY</option>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`milestone-outcome-${milestone.id}`}>Expected deliverable/outcome</FieldLabel>
                    <Input
                      id={`milestone-outcome-${milestone.id}`}
                      defaultValue={milestone.expected_outcome}
                      disabled={!milestonesEditable}
                      onBlur={(event) => {
                        const value = event.target.value.trim();
                        if (value && value !== milestone.expected_outcome) void updateMilestone(milestone, { expected_outcome: value });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`milestone-due-${milestone.id}`}>Due date</FieldLabel>
                    <Input
                      id={`milestone-due-${milestone.id}`}
                      type="date"
                      defaultValue={milestone.due_date}
                      disabled={!milestonesEditable}
                      onBlur={(event) => {
                        if (event.target.value && event.target.value !== milestone.due_date) void updateMilestone(milestone, { due_date: event.target.value });
                      }}
                    />
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`milestone-evidence-${milestone.id}`}>Evidence for HeadRush</FieldLabel>
                    <Input id={`milestone-evidence-${milestone.id}`} defaultValue={milestone.evidence_description || ''} placeholder="Describe the strongest artifact or proof" disabled={!milestonesEditable} onBlur={(event) => { if (event.target.value.trim() !== (milestone.evidence_description || '')) void updateMilestone(milestone, { evidence_description: event.target.value.trim() }); }} />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`milestone-evidence-url-${milestone.id}`}>Evidence link (optional)</FieldLabel>
                    <Input id={`milestone-evidence-url-${milestone.id}`} type="url" defaultValue={milestone.evidence_url || ''} placeholder="https://..." disabled={!milestonesEditable} onBlur={(event) => { if (event.target.value.trim() !== (milestone.evidence_url || '')) void updateMilestone(milestone, { evidence_url: event.target.value.trim() }); }} />
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-end">
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`milestone-status-${milestone.id}`}>Progress</FieldLabel>
                    <Select
                      id={`milestone-status-${milestone.id}`}
                      value={milestone.status}
                      disabled={!milestonesEditable}
                      onChange={(event) => void updateMilestone(milestone, { status: event.target.value as GoalMilestone['status'] })}
                    >
                      <option value="planned">Planned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="blocked">Blocked</option>
                      <option value="ready_for_review">Ready for Review</option>
                      <option value="completed">Completed (reported)</option>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <FieldLabel htmlFor={`milestone-note-${milestone.id}`}>Progress/block note (optional)</FieldLabel>
                    <Input
                      id={`milestone-note-${milestone.id}`}
                      defaultValue={milestone.progress_note}
                      maxLength={500}
                      disabled={!milestonesEditable}
                      onBlur={(event) => {
                        if (event.target.value.trim() !== milestone.progress_note) void updateMilestone(milestone, { progress_note: event.target.value.trim() });
                      }}
                    />
                  </div>
                  <label className="flex items-center gap-2 pb-2 text-sm text-text">
                    <input
                      type="checkbox"
                      checked={milestone.evidence_expected}
                      disabled={!milestonesEditable}
                      onChange={(event) => void updateMilestone(milestone, { evidence_expected: event.target.checked })}
                    />
                    Evidence expected
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant={milestone.verification_status === 'verified' ? 'success' : milestone.verification_status === 'needs_revision' ? 'danger' : milestone.verification_status === 'pending' ? 'warning' : 'muted'}>
                    Teacher: {(milestone.verification_status || 'not_submitted').replace(/_/g, ' ')}
                  </Badge>
                  <label className="flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 text-text">
                    <input type="checkbox" checked={milestone.headrush_status === 'uploaded'} disabled={!milestonesEditable} onChange={(event) => void updateMilestone(milestone, { headrush_status: event.target.checked ? 'uploaded' : 'not_uploaded' })} />
                    Uploaded to HeadRush
                  </label>
                  <span className="text-muted">{milestone.linked_evidence_count || 0} linked course evidence item{milestone.linked_evidence_count === 1 ? '' : 's'}</span>
                  {milestone.verification_note && <span className="basis-full text-muted">Teacher note: {milestone.verification_note}</span>}
                </div>
                {milestonesEditable && <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button type="button" size="sm" disabled={milestone.status === 'ready_for_review' && milestone.verification_status === 'pending'} onClick={() => void updateMilestone(milestone, { status: 'ready_for_review' })}>
                    {milestone.status === 'ready_for_review' && milestone.verification_status === 'pending' ? 'Submitted for teacher review' : 'Submit for teacher review'}
                  </Button>
                  <p className="text-xs text-muted">Submit after adding your evidence. This does not upload to Headrush or assign proficiency.</p>
                </div>}
              </details>
            ))}
          </div>
        )}
        {milestonesEditable ? (
          <details className="rounded-xl border border-dashed border-border p-4">
            <summary className="cursor-pointer text-sm font-semibold">Add another milestone</summary>
            <div className="mt-4 grid gap-3 md:grid-cols-[140px_minmax(0,1fr)_180px]">
              <div className="space-y-2">
                <FieldLabel htmlFor="new-milestone-stage">Hook stage</FieldLabel>
                <Select id="new-milestone-stage" value={milestoneHookStage} onChange={(event) => setMilestoneHookStage(event.target.value as GoalMilestone['hook_stage'])}>
                  <option value="define">DEFINE</option><option value="do">DO</option><option value="explain">EXPLAIN</option><option value="apply">APPLY</option><option value="">Other</option>
                </Select>
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="new-milestone-outcome">New expected deliverable/outcome</FieldLabel>
                <Input id="new-milestone-outcome" value={milestoneOutcome} onChange={(event) => setMilestoneOutcome(event.target.value)} />
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="new-milestone-due">Due date</FieldLabel>
                <Input id="new-milestone-due" type="date" value={milestoneDueDate} onChange={(event) => setMilestoneDueDate(event.target.value)} />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-text">
                <input type="checkbox" checked={milestoneEvidenceExpected} onChange={(event) => setMilestoneEvidenceExpected(event.target.checked)} />
                Evidence expected
              </label>
              <Button type="button" onClick={createMilestone} disabled={milestoneSaving}>
                {milestoneSaving ? 'Saving...' : 'Add Milestone'}
              </Button>
            </div>
          </details>
        ) : (
          <p className="text-xs text-muted">Milestones are read-only because this goal or cycle is in review/history.</p>
        )}
      </div>
    </SectionCard>
  ) : null;

  const technicalEvidencePanel = primaryGoal?.instructional_cycle && goalEvidence ? (
    <SectionCard
      title="Assessment records"
      meta="Coverage comes only from authored DEFINE / DO / EXPLAIN / APPLY assessments. Completion and teacher verification are shown separately."
      actions={(
        <Button type="button" size="sm" variant="secondary" disabled={goalEvidenceExporting} onClick={() => void exportGoalEvidenceReport()}>
          {goalEvidenceExporting ? 'Generating PDF...' : 'Goal + Evidence PDF'}
        </Button>
      )}
    >
      <div className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {(['define', 'do', 'explain', 'apply'] as const).map((stage) => {
            const coverage = goalEvidence.stages[stage];
            return (
              <div key={stage} className="rounded-xl border border-border bg-surface-alt/50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">{stage}</p>
                <p className="mt-1 text-sm font-semibold text-text">{coverage.state.replace(/_/g, ' ')}</p>
                <p className="mt-1 text-xs text-muted">
                  {coverage.completed_count}/{coverage.authored_count} complete · {coverage.verified_count} verified
                </p>
              </div>
            );
          })}
        </div>
        {goalEvidenceError && (
          <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">{goalEvidenceError}</div>
        )}
        {goalEvidence.links.length === 0 ? (
          <p className="text-sm text-muted">No technical evidence attached yet.</p>
        ) : (
          <div className="space-y-2">
            {goalEvidence.links.map((link) => (
              <div key={link.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3 text-sm">
                <div>
                  <p className="font-semibold text-text">{link.stage.toUpperCase()} · {link.step_title}</p>
                  <p className="mt-1 text-xs text-muted">
                    {link.completed ? 'Completed' : 'Incomplete'} · {link.verification_state.replace(/_/g, ' ')}
                    {link.milestone ? ` · milestone #${link.milestone}` : ''}
                  </p>
                </div>
                {goalEvidence.editable && (
                  <Button type="button" size="sm" variant="ghost" disabled={goalEvidenceSaving} onClick={() => void removeGoalEvidence(link.id)}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {goalEvidence.editable ? (
          goalEvidence.available_sources.length > 0 ? (
            <div className="grid gap-3 rounded-xl border border-dashed border-border p-4 md:grid-cols-[minmax(0,1fr)_220px_auto] md:items-end">
              <div className="space-y-2">
                <FieldLabel htmlFor="goal-evidence-source">Eligible assessment evidence</FieldLabel>
                <Select id="goal-evidence-source" value={goalEvidenceSource} onChange={(event) => setGoalEvidenceSource(event.target.value)}>
                  <option value="">Select evidence</option>
                  {goalEvidence.available_sources.map((source) => (
                    <option key={`${source.source_type}:${source.source_id}`} value={`${source.source_type}:${source.source_id}`}>
                      {source.stage.toUpperCase()} · {source.step_title} ({source.source_status})
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <FieldLabel htmlFor="goal-evidence-milestone">Milestone (optional)</FieldLabel>
                <Select id="goal-evidence-milestone" value={goalEvidenceMilestone} onChange={(event) => setGoalEvidenceMilestone(event.target.value)}>
                  <option value="">Goal-level evidence</option>
                  {milestones.map((milestone) => <option key={milestone.id} value={milestone.id}>{milestone.expected_outcome}</option>)}
                </Select>
              </div>
              <Button type="button" disabled={!goalEvidenceSource || goalEvidenceSaving} onClick={() => void attachGoalEvidence()}>
                {goalEvidenceSaving ? 'Saving...' : 'Attach Evidence'}
              </Button>
            </div>
          ) : <p className="text-xs text-muted">No additional eligible authored assessment evidence is available.</p>
        ) : <p className="text-xs text-muted">Evidence curation is read-only because this cycle is finalized.</p>}
      </div>
    </SectionCard>
  ) : null;

  const nsrPanel = nsrCycle ? (
    <SectionCard
      title="Next Step Ready Growth"
      meta="Choose one primary growth focus and, when useful, one secondary focus. M/P/NP descriptions are behavior examples—not a routine score or technical mastery rating."
    >
      <div className="space-y-4">
        {nsrError && <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">{nsrError}</div>}
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <FieldLabel htmlFor="nsr-primary-focus">Primary growth focus</FieldLabel>
            <Select id="nsr-primary-focus" value={nsrPrimaryItem} disabled={!nsrCycle.permissions.focus_editable} onChange={(event) => setNsrPrimaryItem(event.target.value)}>
              <option value="">Select a focus</option>
              {nsrCycle.rubric.categories.map((category) => (
                <optgroup key={category.key} label={category.label}>
                  {category.items.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </optgroup>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="nsr-secondary-focus">Secondary focus (optional)</FieldLabel>
            <Select id="nsr-secondary-focus" value={nsrSecondaryItem} disabled={!nsrCycle.permissions.focus_editable} onChange={(event) => setNsrSecondaryItem(event.target.value)}>
              <option value="">No secondary focus</option>
              {nsrCycle.rubric.categories.map((category) => (
                <optgroup key={category.key} label={category.label}>
                  {category.items.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </optgroup>
              ))}
            </Select>
          </div>
        </div>
        {nsrCycle.permissions.focus_editable && (
          <Button type="button" disabled={!nsrPrimaryItem || nsrSaving} onClick={() => void saveNsrFocus()}>
            {nsrSaving ? 'Saving...' : 'Save Growth Focus'}
          </Button>
        )}
        {nsrCycle.focus?.primary_item && (
          <div className="space-y-3 rounded-xl border border-border bg-surface-alt/40 p-4">
            <p className="font-semibold text-text">{nsrCycle.focus.primary_item.category_label} · {nsrCycle.focus.primary_item.label}</p>
            <div className="grid gap-2 md:grid-cols-3">
              {(['M', 'P', 'NP'] as const).map((label) => (
                <div key={label} className="rounded-lg border border-border bg-surface p-3 text-sm">
                  <p className="font-semibold text-text">{label} — {nsrCycle.rubric.labels[label]}</p>
                  <p className="mt-1 text-xs text-muted">{nsrCycle.focus?.primary_item.descriptors[label]}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {nsrCycle.focus && (
          <div className="space-y-2">
            <FieldLabel htmlFor="nsr-student-reflection">My growth reflection</FieldLabel>
            <Textarea id="nsr-student-reflection" value={nsrReflection} maxLength={2000} disabled={!nsrCycle.permissions.reflection_editable} onChange={(event) => setNsrReflection(event.target.value)} />
            {nsrCycle.permissions.reflection_editable && (
              <Button type="button" variant="secondary" disabled={nsrSaving} onClick={() => void saveNsrReflection()}>Save Reflection</Button>
            )}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-text">Teacher observations shared with me</p>
          {nsrCycle.observations.length === 0 ? <p className="mt-2 text-sm text-muted">No shared observations yet.</p> : (
            <div className="mt-2 space-y-2">
              {nsrCycle.observations.map((observation) => (
                <div key={observation.id} className="rounded-xl border border-border p-3 text-sm">
                  <p className="font-semibold text-text">{observation.rubric_item.category_label} · {observation.rubric_item.label}</p>
                  <p className="mt-1 text-muted">{observation.observation_text}</p>
                  <p className="mt-1 text-xs text-muted">{observation.observed_on} · {observation.teacher_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  ) : null;

  const instructionalCheckInPanel = instructionalCheckIns.length > 0 ? (
    <SectionCard
      title="Teacher Check-ins"
      meta="Agreed instructional next steps from brief teacher check-ins. These records are read-only."
    >
      <div className="space-y-3">
        {instructionalCheckIns.slice(0, 4).map((checkIn) => (
          <div key={checkIn.id} className="rounded-xl border border-border bg-surface-alt/40 p-4 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-text">{checkIn.check_in_kind === 'midpoint' ? 'Cycle midpoint' : 'Instructional check-in'}</p>
              <p className="text-xs text-muted">{new Date(checkIn.created_at).toLocaleDateString()}</p>
            </div>
            {checkIn.barrier_problem && <p className="mt-2 text-muted"><span className="font-semibold text-text">Barrier/problem:</span> {checkIn.barrier_problem}</p>}
            {checkIn.instructional_move && <p className="mt-2 text-muted"><span className="font-semibold text-text">Teacher move:</span> {checkIn.instructional_move}</p>}
            <p className="mt-2 text-muted"><span className="font-semibold text-text">What I will produce next:</span> {checkIn.agreed_next_deliverable}</p>
            {checkIn.follow_up_on && <p className="mt-2 text-xs text-muted">Follow-up: {checkIn.follow_up_on}</p>}
          </div>
        ))}
      </div>
    </SectionCard>
  ) : null;

  const legacyUnlinkedPrimaryGoal = Boolean(
    seniorPriorityWorkflow && primaryGoal?.is_active && !primaryGoal.instructional_cycle,
  );
  const requiredPathwayGoal = primaryGoals.find((row) => row.goal_track === 'required_pathway') || null;
  const personalProjectGoal = primaryGoals.find((row) => row.goal_track === 'personal_project') || null;
  const reviewingGoal = reviewGoals.find((goal) => goal.id === reviewingGoalId) || null;
  const pendingReviewGoals = reviewGoals.filter((goal) => !goal.final_reflection_submitted);

  const longerTermDirectionPanel = seniorPriorityWorkflow ? (
    redesignEnabled ? (
      <SectionCard
        title="Longer-Term Direction"
        meta="Your larger certification, project, career, or year-long direction. It does not control the five-week cycle dates."
        actions={longerTermDirection && !directionEditing ? (
          <Button type="button" size="sm" variant="secondary" onClick={() => setDirectionEditing(true)}>
            Edit Direction
          </Button>
        ) : undefined}
      >
        {directionEditing ? (
          <div className="space-y-3">
            {directionError && <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">{directionError}</div>}
            <FieldLabel htmlFor="longer-direction-title">Larger direction or target</FieldLabel>
            <Input id="longer-direction-title" value={directionTitle} onChange={(event) => { directionDraftDirtyRef.current = true; setDirectionTitle(event.target.value); }} placeholder="Earn my CompTIA A+ certification" />
            <div className="grid gap-3 md:grid-cols-2">
              <div><FieldLabel htmlFor="longer-direction-why">Why it matters</FieldLabel><Textarea id="longer-direction-why" value={directionWhy} onChange={(event) => { directionDraftDirtyRef.current = true; setDirectionWhy(event.target.value); }} /></div>
              <div><FieldLabel htmlFor="longer-direction-proof">What progress looks like</FieldLabel><Textarea id="longer-direction-proof" value={directionMetric} onChange={(event) => { directionDraftDirtyRef.current = true; setDirectionMetric(event.target.value); }} /></div>
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={saveLongerTermDirection} disabled={directionSaving}>{directionSaving ? 'Saving...' : 'Save Direction'}</Button>
              {longerTermDirection && <Button type="button" variant="ghost" onClick={() => setDirectionEditing(false)}>Cancel</Button>}
            </div>
          </div>
        ) : longerTermDirection ? (
          <div>
            <p className="text-lg font-semibold text-text">{longerTermDirection.title}</p>
            {longerTermDirection.why_text && <p className="mt-2 text-sm text-muted"><span className="font-semibold text-text">Why:</span> {longerTermDirection.why_text}</p>}
            {longerTermDirection.target_metric_text && <p className="mt-2 text-sm text-muted"><span className="font-semibold text-text">Progress:</span> {longerTermDirection.target_metric_text}</p>}
          </div>
        ) : legacyUnlinkedPrimaryGoal ? (
          <p className="text-sm text-muted">Your existing longer-term entry can be preserved here using the conversion option below.</p>
        ) : (
          <div className="flex items-center justify-between gap-3 text-sm text-muted"><span>Add an optional larger direction that can continue across multiple five-week cycles.</span><Button type="button" size="sm" variant="secondary" onClick={() => setDirectionEditing(true)}>Add Direction</Button></div>
        )}
      </SectionCard>
    ) : (
      <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3"><div><p className="text-xs uppercase tracking-wide text-slate-500">Longer-Term Direction</p><h2 className="text-lg font-semibold text-gray-900">Your larger direction</h2><p className="text-sm text-gray-600">This can continue across multiple five-week cycles and has no required dates.</p></div>{longerTermDirection && !directionEditing && <button type="button" className="rounded border border-slate-300 px-3 py-1 text-sm" onClick={() => setDirectionEditing(true)}>Edit Direction</button>}</div>
        {directionEditing ? <div className="mt-4 space-y-3">{directionError && <div className="text-sm text-rose-700">{directionError}</div>}<input className="w-full rounded border px-3 py-2" value={directionTitle} onChange={(event) => { directionDraftDirtyRef.current = true; setDirectionTitle(event.target.value); }} placeholder="Larger direction or target" /><div className="grid gap-3 md:grid-cols-2"><textarea className="rounded border px-3 py-2" value={directionWhy} onChange={(event) => { directionDraftDirtyRef.current = true; setDirectionWhy(event.target.value); }} placeholder="Why it matters" /><textarea className="rounded border px-3 py-2" value={directionMetric} onChange={(event) => { directionDraftDirtyRef.current = true; setDirectionMetric(event.target.value); }} placeholder="What progress looks like" /></div><button type="button" className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white" onClick={saveLongerTermDirection} disabled={directionSaving}>{directionSaving ? 'Saving...' : 'Save Direction'}</button></div> : longerTermDirection ? <div className="mt-4 text-sm text-gray-700"><p className="font-semibold text-gray-900">{longerTermDirection.title}</p>{longerTermDirection.why_text && <p className="mt-2"><span className="font-semibold">Why:</span> {longerTermDirection.why_text}</p>}{longerTermDirection.target_metric_text && <p className="mt-1"><span className="font-semibold">Progress:</span> {longerTermDirection.target_metric_text}</p>}</div> : !legacyUnlinkedPrimaryGoal && <button type="button" className="mt-4 rounded border border-slate-300 px-3 py-1 text-sm" onClick={() => setDirectionEditing(true)}>Add Direction</button>}
      </div>
    )
  ) : null;

  const teacherDailySupportNotice = goalCycle?.latest_support ? (
    <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-950">
      <p className="font-semibold">
        {goalCycle.latest_support.support_mode === 'teacher_stated' ? 'Your teacher stated today’s plan' : 'Your teacher helped adapt today’s plan'}
      </p>
      <p className="mt-1 text-violet-800">{goalCycle.latest_support.student_visible_rationale}</p>
      <p className="mt-1 text-xs text-violet-700">You can use the prepared plan below and ask your teacher for clarification or a different support.</p>
    </div>
  ) : null;

  const primaryGoalPanel = redesignEnabled ? (
    <SectionCard
      title={reviewingGoal
        ? `${reviewingGoal.instructional_cycle_detail?.title || 'Five-week goal'} reflection`
        : seniorPriorityWorkflow ? 'Five-Week Goal' : 'Long-Term Goal'}
      meta={reviewingGoal
        ? `${formatDate(reviewingGoal.start_date)} - ${formatDate(reviewingGoal.end_date)}. Complete this reflection before returning to your current goal.`
        : seniorPriorityWorkflow
        ? activePerformanceCycle
          ? `${activePerformanceCycle.title}: ${formatDate(activePerformanceCycle.start_date)} - ${formatDate(activePerformanceCycle.end_date)}. Dates are set by your teacher.`
          : 'Your teacher will activate a performance period before you create this goal.'
        : effectiveGoalMode === 'basic' ? "Keep one larger target visible while you plan today's work." : 'Use this as the anchor for daily work, check-ins, and reflection.'}
      actions={reviewingGoal ? (
        <Button type="button" size="sm" variant="secondary" onClick={returnToCurrentGoal}>
          Return to current goal
        </Button>
      ) : primaryGoal && !primaryGoal.conference_managed && primaryGoal.is_active && !primaryGoalEditing && !legacyUnlinkedPrimaryGoal ? (
        <Button type="button" size="sm" variant="secondary" onClick={() => setPrimaryGoalEditing(true)}>
          Edit Goal
        </Button>
      ) : undefined}
    >
      {!reviewingGoal && seniorPriorityWorkflow && activePerformanceCycle && !legacyUnlinkedPrimaryGoal && (
        <div className="mb-4 grid gap-2 sm:grid-cols-2">
          <Button type="button" variant={selectedGoalTrack === 'required_pathway' ? 'primary' : 'secondary'} onClick={() => selectFiveWeekGoalTrack('required_pathway')}>
            Main Goal · {requiredPathwayGoal ? 'Created' : 'Required'}
          </Button>
          <Button type="button" variant={selectedGoalTrack === 'personal_project' ? 'primary' : 'secondary'} onClick={() => selectFiveWeekGoalTrack('personal_project')} disabled={!requiredPathwayGoal}>
            Personal Project · {personalProjectGoal ? 'Created' : 'Optional'}
          </Button>
        </div>
      )}
      {reviewingGoal ? (
        <div id="five-week-reflection" className="scroll-mt-28">
          <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Goal being reflected on</p>
            <p className="mt-1 font-semibold text-text">{reviewingGoal.title}</p>
          </div>
          {goalClosureContent}
          {performanceReviewContent}
        </div>
      ) : legacyUnlinkedPrimaryGoal ? (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-4 text-sm text-text">
          <p className="font-semibold">Keep your existing goal as your longer-term direction</p>
          <p className="mt-1 text-muted">“{primaryGoal?.title}” uses student-selected dates. Preserve it as the date-free direction, then create a new goal for the active five-week cycle.</p>
          {primaryGoalError && <p className="mt-2 text-danger">{primaryGoalError}</p>}
          <Button type="button" className="mt-3" onClick={convertLegacyGoalToDirection} disabled={directionSaving}>{directionSaving ? 'Converting...' : 'Keep as Direction & Create Five-Week Goal'}</Button>
        </div>
      ) : seniorPriorityWorkflow && !activePerformanceCycle && !primaryGoal ? (
        <p className="text-sm text-muted">No performance period is active for your grade yet. Your longer-term direction can still be added above.</p>
      ) : primaryGoalEditing ? (
        <div className="space-y-4">
          {primaryGoalError && (
            <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
              {primaryGoalError}
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <FieldLabel htmlFor="primary-goal-purpose">What kind of growth is this goal about?</FieldLabel>
              <Select id="primary-goal-purpose" value={primaryGoalPurpose} onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalPurpose(event.target.value as typeof primaryGoalPurpose); }}>
                <option value="">Choose a purpose</option>
                <option value="technical_cte">Technical / CTE skill development</option>
                <option value="personal_professional">Personal / professional growth</option>
              </Select>
              <p className="text-xs text-muted">This is separate from whether the goal belongs to your Main Goal or Personal Project track.</p>
            </div>
            <div className="space-y-2 md:col-span-2">
              <FieldLabel htmlFor="primary-goal-title">{seniorPriorityWorkflow ? selectedGoalTrack === 'required_pathway' ? 'Main Goal' : 'Personal Project goal' : 'Goal title'}</FieldLabel>
              <Input
                id="primary-goal-title"
                value={primaryGoalTitle}
                onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalTitle(event.target.value); }}
                placeholder="Earn my Python fundamentals badge"
              />
            </div>
            <div className="space-y-2">
              <FieldLabel htmlFor="primary-goal-why">Why it matters</FieldLabel>
              <Textarea
                id="primary-goal-why"
                value={primaryGoalWhy}
                onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalWhy(event.target.value); }}
                placeholder="This helps me move toward..."
              />
            </div>
            <div className="space-y-2">
              <FieldLabel htmlFor="primary-goal-metric">Target metric</FieldLabel>
              <Textarea
                id="primary-goal-metric"
                value={primaryGoalMetric}
                onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalMetric(event.target.value); }}
                placeholder="I will know I made progress when..."
              />
            </div>
            {seniorPriorityWorkflow ? (
              <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3 text-sm md:col-span-2">
                <span className="font-semibold text-text">Teacher-set five-week period:</span>{' '}
                <span className="text-muted">{formatDate(activePerformanceCycle?.start_date)} - {formatDate(activePerformanceCycle?.end_date)}</span>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <FieldLabel htmlFor="primary-goal-start-date">Start date</FieldLabel>
                  <Input id="primary-goal-start-date" type="date" value={primaryGoalStartDate} onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalStartDate(event.target.value); }} />
                </div>
                <div className="space-y-2">
                  <FieldLabel htmlFor="primary-goal-end-date">End date</FieldLabel>
                  <Input id="primary-goal-end-date" type="date" value={primaryGoalEndDate} onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalEndDate(event.target.value); }} />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={savePrimaryGoal} disabled={primaryGoalSaving}>
              {primaryGoalSaving ? 'Saving...' : seniorPriorityWorkflow ? 'Save Five-Week Goal' : 'Save Long-Term Goal'}
            </Button>
            {primaryGoal && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  goalDraft.clear(); applyPrimaryGoalToForm(primaryGoal);
                  setPrimaryGoalEditing(false);
                  setPrimaryGoalError('');
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      ) : primaryGoal ? (
        <div>
          {primaryGoal.latest_support && (
            <div className="mb-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-950">
              <p className="font-semibold">{primaryGoal.latest_support.support_mode === 'teacher_stated' ? 'Your teacher stated this goal' : 'Your teacher helped adapt this goal'}</p>
              <p className="mt-1 text-violet-800">{primaryGoal.latest_support.student_visible_rationale}</p>
            </div>
          )}
          {primaryGoal.quality_workflow && (
            <div className={`mb-3 rounded-xl border px-4 py-3 text-sm ${primaryGoal.quality_workflow.gate_satisfied ? 'border-success/30 bg-success/10' : 'border-warning/40 bg-warning/10'}`}>
              <p className="font-semibold text-text">
                Teacher review: {primaryGoal.quality_workflow.state.replace(/_/g, ' ')}
              </p>
              <p className="mt-1 text-muted">You can save your daily plan and keep working while your teacher reviews this goal.</p>
              {primaryGoal.quality_workflow.teacher_review?.note && <p className="mt-2 text-text"><span className="font-semibold">Teacher feedback:</span> {primaryGoal.quality_workflow.teacher_review.note}</p>}
              {primaryGoal.quality_workflow.standards_status === 'catalog_unavailable' && <p className="mt-1 text-muted">Goal quality is ready. Idaho CTE standards alignment is not yet available; no standard has been inferred.</p>}
              {primaryGoal.quality_workflow.standards_status === 'match_pending' && <p className="mt-1 text-muted">Goal quality is ready. Your teacher still needs to review and confirm an Idaho CTE standard.</p>}
              {primaryGoal.quality_workflow.standard_confirmation?.outcome === 'standard_confirmed' && <div className="mt-2 rounded-lg bg-white/70 p-2 text-muted"><p><span className="font-semibold">Confirmed standard:</span> {primaryGoal.quality_workflow.standard_confirmation.official_code} — {primaryGoal.quality_workflow.standard_confirmation.official_text}</p>{primaryGoal.quality_workflow.standard_confirmation.evidence_target && <p className="mt-1"><span className="font-semibold">Evidence target:</span> {primaryGoal.quality_workflow.standard_confirmation.evidence_target}</p>}</div>}
              {primaryGoal.quality_workflow.standard_confirmation?.outcome === 'no_defensible_standard' && <p className="mt-1 text-muted">Your teacher reviewed this goal and documented that no defensible catalog standard applies.</p>}
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(220px,0.35fr)]">
            <div>
              <p className="text-lg font-semibold text-text">{primaryGoal.title}</p>
              <p className="mt-2 text-sm text-muted">
                <span className="font-semibold text-text">Why:</span> {primaryGoal.why_text}
              </p>
              <p className="mt-2 whitespace-pre-line text-sm text-muted">
                <span className="font-semibold text-text">Target:</span> {primaryGoal.target_metric_text}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3 text-sm text-text">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">Date range</p>
              <p className="mt-2 font-semibold">
                {formatDate(primaryGoal.start_date)} - {formatDate(primaryGoal.end_date)}
              </p>
              {primaryGoal.closed_at && (
                <p className="mt-2 text-xs text-muted">Closed {formatDate(primaryGoal.closed_at)}</p>
              )}
              {goalExamplesButton}
            </div>
          </div>
          {goalExampleNotice && <p role="status" className="mt-3 text-sm text-text">{goalExampleNotice}</p>}
          {primaryGoalReviewContent}
          {practiceStandardsContent}
          {seniorPriorityWorkflow && redesignEnabled ? (
            finalReflectionFeatureEnabled && (
              <details id="five-week-reflection" className="scroll-mt-28 rounded-xl border border-border p-3" open={goalClosureOpen}
                onToggle={(event) => setGoalClosureOpen(event.currentTarget.open)}>
                <summary className="cursor-pointer text-sm font-medium text-muted">{primaryGoalClosed ? 'Five-week goal reflection' : 'Finish this five-week goal'}</summary>
                {goalClosureContent}
              </details>
            )
          ) : goalClosureContent}
          {performanceReviewContent}
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted sm:flex-row sm:items-center sm:justify-between">
          <span>{seniorPriorityWorkflow
            ? 'Create one focused goal for this performance period.'
            : "Add one long-term goal so today's plan has a larger target to point toward."}</span>
          {seniorPriorityWorkflow && (
            <Button type="button" size="sm" variant="secondary" onClick={() => setPrimaryGoalEditing(true)}>
              Add Five-Week Goal
            </Button>
          )}
        </div>
      )}
    </SectionCard>
  ) : (
    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">{seniorPriorityWorkflow ? 'Five-Week Goal' : 'Long-Term Goal'}</p>
          <h2 className="text-lg font-semibold text-gray-900">{seniorPriorityWorkflow ? activePerformanceCycle?.title || 'Waiting for a performance period' : 'Primary goal anchor'}</h2>
          <p className="text-sm text-gray-600">{seniorPriorityWorkflow && activePerformanceCycle ? `${formatDate(activePerformanceCycle.start_date)} - ${formatDate(activePerformanceCycle.end_date)} · dates set by your teacher` : 'One larger target that today\'s work can connect to.'}</p>
        </div>
        {primaryGoal && !primaryGoal.conference_managed && primaryGoal.is_active && !primaryGoalEditing && !legacyUnlinkedPrimaryGoal && (
          <button
            type="button"
            className="w-fit rounded border border-slate-300 px-3 py-1 text-sm text-slate-700"
            onClick={() => setPrimaryGoalEditing(true)}
          >
            Edit Goal
          </button>
        )}
      </div>
      {seniorPriorityWorkflow && activePerformanceCycle && !legacyUnlinkedPrimaryGoal && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <button type="button" className={`rounded border px-3 py-2 text-sm font-semibold ${selectedGoalTrack === 'required_pathway' ? 'border-indigo-600 bg-indigo-50 text-indigo-800' : 'border-slate-300'}`} onClick={() => selectFiveWeekGoalTrack('required_pathway')}>Main Goal · {requiredPathwayGoal ? 'Created' : 'Required'}</button>
          <button type="button" className={`rounded border px-3 py-2 text-sm font-semibold ${selectedGoalTrack === 'personal_project' ? 'border-indigo-600 bg-indigo-50 text-indigo-800' : 'border-slate-300'}`} onClick={() => selectFiveWeekGoalTrack('personal_project')} disabled={!requiredPathwayGoal}>Personal Project · {personalProjectGoal ? 'Created' : 'Optional'}</button>
        </div>
      )}
      {legacyUnlinkedPrimaryGoal ? (
        <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-4 text-sm text-gray-700">
          <p className="font-semibold text-gray-900">Keep your existing goal as your longer-term direction</p>
          <p className="mt-1">“{primaryGoal?.title}” uses student-selected dates. Preserve it, then create a separate goal for the teacher’s five-week cycle.</p>
          {primaryGoalError && <p className="mt-2 text-rose-700">{primaryGoalError}</p>}
          <button type="button" className="mt-3 rounded bg-indigo-600 px-4 py-2 font-semibold text-white" onClick={convertLegacyGoalToDirection} disabled={directionSaving}>{directionSaving ? 'Converting...' : 'Keep as Direction & Create Five-Week Goal'}</button>
        </div>
      ) : seniorPriorityWorkflow && !activePerformanceCycle && !primaryGoal ? (
        <p className="mt-4 text-sm text-gray-500">No performance period is active for your grade yet. You can still add your longer-term direction above.</p>
      ) : primaryGoalEditing ? (
        <div className="mt-4 space-y-3">
          {primaryGoalError && (
            <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {primaryGoalError}
            </div>
          )}
          <label className="block text-sm text-gray-700">
            What kind of growth is this goal about?
            <select className="mt-1 w-full rounded border px-3 py-2" value={primaryGoalPurpose} onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalPurpose(event.target.value as typeof primaryGoalPurpose); }}>
              <option value="">Choose a purpose</option><option value="technical_cte">Technical / CTE skill development</option><option value="personal_professional">Personal / professional growth</option>
            </select>
            <span className="mt-1 block text-xs text-gray-500">Purpose is separate from the Main Goal or Personal Project track.</span>
          </label>
          <label className="block text-sm text-gray-700">
            {seniorPriorityWorkflow ? selectedGoalTrack === 'required_pathway' ? 'Main Goal' : 'Personal Project goal' : 'Goal title'}
            <input
              className="mt-1 w-full rounded border px-3 py-2"
              value={primaryGoalTitle}
              onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalTitle(event.target.value); }}
              placeholder="Earn my Python fundamentals badge"
            />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-sm text-gray-700">
              Why it matters
              <textarea
                className="mt-1 w-full rounded border px-3 py-2"
                value={primaryGoalWhy}
                onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalWhy(event.target.value); }}
                placeholder="This helps me move toward..."
              />
            </label>
            <label className="block text-sm text-gray-700">
              Target metric
              <textarea
                className="mt-1 w-full rounded border px-3 py-2"
                value={primaryGoalMetric}
                onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalMetric(event.target.value); }}
                placeholder="I will know I made progress when..."
              />
            </label>
            {seniorPriorityWorkflow ? (
              <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-gray-700 md:col-span-2">
                <span className="font-semibold">Teacher-set five-week period:</span>{' '}
                {formatDate(activePerformanceCycle?.start_date)} - {formatDate(activePerformanceCycle?.end_date)}
              </div>
            ) : (
              <>
                <label className="block text-sm text-gray-700">
                  Start date
                  <input className="mt-1 w-full rounded border px-3 py-2" type="date" value={primaryGoalStartDate} onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalStartDate(event.target.value); }} />
                </label>
                <label className="block text-sm text-gray-700">
                  End date
                  <input className="mt-1 w-full rounded border px-3 py-2" type="date" value={primaryGoalEndDate} onChange={(event) => { primaryGoalDraftDirtyRef.current = true; setPrimaryGoalEndDate(event.target.value); }} />
                </label>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
              onClick={savePrimaryGoal}
              disabled={primaryGoalSaving}
            >
              {primaryGoalSaving ? 'Saving...' : seniorPriorityWorkflow ? 'Save Five-Week Goal' : 'Save Long-Term Goal'}
            </button>
            {primaryGoal && (
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700"
                onClick={() => {
                  goalDraft.clear(); applyPrimaryGoalToForm(primaryGoal);
                  setPrimaryGoalEditing(false);
                  setPrimaryGoalError('');
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : primaryGoal ? (
        <div>
          {primaryGoal.latest_support && (
            <div className="mt-4 rounded border border-violet-200 bg-violet-50 p-3 text-sm text-violet-950">
              <div className="font-semibold">{primaryGoal.latest_support.support_mode === 'teacher_stated' ? 'Your teacher stated this goal' : 'Your teacher helped adapt this goal'}</div>
              <div className="mt-1 text-violet-800">{primaryGoal.latest_support.student_visible_rationale}</div>
            </div>
          )}
          <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-gray-700">
            <div className="font-semibold text-gray-900">{primaryGoal.title}</div>
            <div className="mt-2"><span className="font-semibold">Why:</span> {primaryGoal.why_text}</div>
            <div className="mt-1 whitespace-pre-line"><span className="font-semibold">Target:</span> {primaryGoal.target_metric_text}</div>
            <div className="mt-1 text-xs text-gray-500">
              {formatDate(primaryGoal.start_date)} - {formatDate(primaryGoal.end_date)}
              {primaryGoal.closed_at ? ` | Closed ${formatDate(primaryGoal.closed_at)}` : ''}
            </div>
          </div>
          {primaryGoal.quality_workflow && (
            <div className="mt-3 rounded border border-sky-200 bg-sky-50 p-3 text-sm">
              <p className="font-semibold">Teacher review: {primaryGoal.quality_workflow.state.replace(/_/g, ' ')}</p>
              <p className="mt-1">You can save your daily plan and keep working while your teacher reviews this goal.</p>
              {primaryGoal.quality_workflow.teacher_review?.note && <p className="mt-2"><span className="font-semibold">Teacher feedback:</span> {primaryGoal.quality_workflow.teacher_review.note}</p>}
            </div>
          )}
          {goalExamplesButton}
          {goalExampleNotice && <p role="status">{goalExampleNotice}</p>}
          {primaryGoalReviewContent}
          {practiceStandardsContent}
          {goalClosureContent}
          {performanceReviewContent}
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
          <span>{seniorPriorityWorkflow
            ? 'Create one focused goal for this performance period.'
            : 'Add one long-term goal so today\'s plan has a larger target to point toward.'}</span>
          {seniorPriorityWorkflow && (
            <button
              type="button"
              className="w-fit rounded border border-slate-300 px-3 py-1 text-sm text-slate-700"
              onClick={() => setPrimaryGoalEditing(true)}
            >
              Add Five-Week Goal
            </button>
          )}
        </div>
      )}
    </div>
  );

  const dailyNudgePanel = primaryGoal && primaryGoal.is_active && aiGoalAssistFeatureEnabled ? (
    redesignEnabled ? (
      <SectionCard
        title="Need a suggestion?"
        meta="A short coaching cue for the next useful move."
        actions={(
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={requestDailyNudge}
            disabled={dailyNudgeLoading}
          >
            {dailyNudgeLoading ? 'Thinking...' : dailyNudge ? 'Refresh Nudge' : 'Get Today\'s Nudge'}
          </Button>
        )}
      >
        <div className="space-y-3">
          {dailyNudgeError && (
            <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
              {dailyNudgeError}
            </div>
          )}
          {dailyNudge ? (
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">Focus</p>
                <p className="mt-2 text-sm text-text">{dailyNudge.focus_text}</p>
              </div>
              <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">Next step</p>
                <p className="mt-2 text-sm text-text">{dailyNudge.next_step_text}</p>
              </div>
              {dailyNudge.encouragement_text && (
                <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">Keep in mind</p>
                  <p className="mt-2 text-sm text-text">{dailyNudge.encouragement_text}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted">
              Use this when you want one concrete cue connected to your long-term goal and recent progress.
            </p>
          )}
        </div>
      </SectionCard>
    ) : (
      <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Today's Focus</p>
            <h2 className="text-lg font-semibold text-gray-900">Daily nudge</h2>
            <p className="text-sm text-gray-600">A short cue for the next useful move.</p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={requestDailyNudge}
            disabled={dailyNudgeLoading}
          >
            {dailyNudgeLoading ? 'Thinking...' : dailyNudge ? 'Refresh Nudge' : 'Get Today\'s Nudge'}
          </Button>
        </div>
        {dailyNudgeError && (
          <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {dailyNudgeError}
          </div>
        )}
        {dailyNudge ? (
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-gray-700">
              <div className="font-semibold text-gray-900">Focus</div>
              <div className="mt-1">{dailyNudge.focus_text}</div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-gray-700">
              <div className="font-semibold text-gray-900">Next step</div>
              <div className="mt-1">{dailyNudge.next_step_text}</div>
            </div>
            {dailyNudge.encouragement_text && (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-gray-700">
                <div className="font-semibold text-gray-900">Keep in mind</div>
                <div className="mt-1">{dailyNudge.encouragement_text}</div>
              </div>
            )}
          </div>
        ) : (
          <p className="mt-4 text-sm text-gray-500">
            Use this when you want one concrete cue connected to your long-term goal and recent progress.
          </p>
        )}
      </div>
    )
  ) : null;

  const weeklyCheckpointAvailable = Boolean(weeklyCheckpointAvailability?.available ?? true);
  const weeklyCheckpointDueLabel = weeklyCheckpointAvailability?.due_label || 'the end of the week';
  const weeklyCheckpointStatusLabel = weeklyCheckpoint
    ? 'Submitted'
    : weeklyCheckpointAvailable
      ? 'Due today'
      : `Opens ${weeklyCheckpointDueLabel}`;
  const weeklyCheckpointSummary = weeklyCheckpoint ? (
    <div className={redesignEnabled ? 'rounded-xl border border-border bg-surface-alt/50 p-4 text-sm text-text' : 'rounded border border-slate-200 bg-slate-50 p-3 text-sm text-gray-700'}>
      <p className="font-semibold">Saved checkpoint</p>
      <p className="mt-2">
        <span className="font-semibold">Progress:</span> {weeklyCheckpoint.progress_text || '(empty)'}
      </p>
      <p className="mt-1">
        <span className="font-semibold">Evidence:</span> {weeklyCheckpoint.evidence_text || '(empty)'}
      </p>
      <p className="mt-1">
        <span className="font-semibold">Obstacle:</span> {weeklyCheckpoint.obstacle_text || '(none)'}
      </p>
      <p className="mt-1">
        <span className="font-semibold">Next adjustment:</span> {weeklyCheckpoint.adjustment_text || '(empty)'}
      </p>
      <p className="mt-2 text-xs text-muted">Confidence {weeklyCheckpoint.confidence_rating}/5</p>
    </div>
  ) : null;

  const weeklyCheckpointHistoryContent = weeklyCheckpointHistory.length > 1 ? (
    <details className={redesignEnabled ? 'rounded-xl border border-border bg-surface-alt/50 p-4' : 'mt-4 rounded border border-slate-200 bg-slate-50 p-3'}>
      <summary className={redesignEnabled ? 'cursor-pointer text-sm font-semibold text-text' : 'cursor-pointer text-sm font-medium text-gray-800'}>
        Recent checkpoints
      </summary>
      <div className="mt-3 space-y-2">
        {weeklyCheckpointHistory.slice(1, 5).map((checkpoint) => (
          <div key={checkpoint.id} className={redesignEnabled ? 'rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text' : 'rounded border border-slate-200 bg-white px-3 py-2 text-sm text-gray-700'}>
            <span className="font-semibold">{formatDate(checkpoint.week_start_date)}:</span>{' '}
            {checkpoint.progress_text || 'No progress note'}
            <span className="text-muted"> | Confidence {checkpoint.confidence_rating}/5</span>
          </div>
        ))}
      </div>
    </details>
  ) : null;

  const weeklyCheckpointPanel = !weeklyCheckpointFeatureEnabled || !primaryGoal || !primaryGoal.is_active ? null : redesignEnabled ? (
    <SectionCard
      title="End-of-Week Goal Checkpoint"
      meta="A short weekly reset for your long-term goal."
      actions={(
        <Badge variant={weeklyCheckpoint ? 'success' : weeklyCheckpointAvailable ? 'warning' : 'muted'}>
          {weeklyCheckpointStatusLabel}
        </Badge>
      )}
    >
      <div className="space-y-4">
        {weeklyCheckpointWeekStart && (
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">
            Week of {formatDate(weeklyCheckpointWeekStart)}
          </p>
        )}
        {weeklyCheckpointError && (
          <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
            {weeklyCheckpointError}
          </div>
        )}
        {!weeklyCheckpointAvailable ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-info/20 bg-info/10 px-4 py-3 text-sm text-text">
              <p className="font-semibold">{weeklyCheckpointAvailability?.label || `End-of-week checkpoint opens ${weeklyCheckpointDueLabel}.`}</p>
              <p className="mt-1 text-muted">
                Use daily goals during the week. Come back at the end of the week to look back, name obstacles, and choose next week&apos;s adjustment.
              </p>
            </div>
            {weeklyCheckpointSummary}
            {weeklyCheckpointHistoryContent}
          </div>
        ) : (
          <>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <FieldLabel htmlFor="weekly-progress-text">Looking back this week, what progress did you make?</FieldLabel>
            <Input
              id="weekly-progress-text"
              value={weeklyProgressText}
              onChange={(event) => setWeeklyProgressText(event.target.value)}
              placeholder="Finished two practice checks, submitted my draft, or stayed on track"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="weekly-evidence-text">What shows it?</FieldLabel>
            <Input
              id="weekly-evidence-text"
              value={weeklyEvidenceText}
              onChange={(event) => setWeeklyEvidenceText(event.target.value)}
              placeholder="Score, artifact, teacher feedback, or work log"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="weekly-obstacle-text">What got in the way?</FieldLabel>
            <Input
              id="weekly-obstacle-text"
              value={weeklyObstacleText}
              onChange={(event) => setWeeklyObstacleText(event.target.value)}
              placeholder="Nothing, time, focus, unclear step, missing help"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="weekly-adjustment-text">What will you change next week?</FieldLabel>
            <Input
              id="weekly-adjustment-text"
              value={weeklyAdjustmentText}
              onChange={(event) => setWeeklyAdjustmentText(event.target.value)}
              placeholder="Start earlier, ask for help, pick a smaller step"
            />
          </div>
          <div className="space-y-2">
            <FieldLabel htmlFor="weekly-confidence-rating">Confidence (1-5)</FieldLabel>
            <Input
              id="weekly-confidence-rating"
              type="number"
              min={1}
              max={5}
              value={weeklyConfidenceRating}
              onChange={(event) => setWeeklyConfidenceRating(Number(event.target.value))}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={saveWeeklyCheckpoint} disabled={weeklyCheckpointSaving}>
            {weeklyCheckpointSaving ? 'Saving...' : weeklyCheckpoint ? 'Update Checkpoint' : 'Save Checkpoint'}
          </Button>
          <p className="text-sm text-muted">Short answers are enough. This is weekly, not daily.</p>
        </div>
        {weeklyCheckpointHistoryContent}
          </>
        )}
      </div>
    </SectionCard>
  ) : (
    <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">End-of-Week Goal Checkpoint</p>
          <h2 className="text-lg font-semibold text-gray-900">Short weekly reset</h2>
          <p className="text-sm text-gray-600">Look back at the week, name the obstacle, and choose one adjustment.</p>
        </div>
        <span className="w-fit rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
          {weeklyCheckpointStatusLabel}
        </span>
      </div>
      {weeklyCheckpointWeekStart && (
        <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Week of {formatDate(weeklyCheckpointWeekStart)}
        </p>
      )}
      {weeklyCheckpointError && (
        <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {weeklyCheckpointError}
        </div>
      )}
      {!weeklyCheckpointAvailable ? (
        <div className="mt-4 space-y-4">
          <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">
              {weeklyCheckpointAvailability?.label || `End-of-week checkpoint opens ${weeklyCheckpointDueLabel}.`}
            </p>
            <p className="mt-1 text-gray-600">
              Use daily goals during the week. Come back at the end of the week to reflect once.
            </p>
          </div>
          {weeklyCheckpointSummary}
          {weeklyCheckpointHistoryContent}
        </div>
      ) : (
        <>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="block text-sm text-gray-700 md:col-span-2">
          Looking back this week, what progress did you make?
          <input
            className="mt-1 w-full rounded border px-3 py-2"
            value={weeklyProgressText}
            onChange={(event) => setWeeklyProgressText(event.target.value)}
            placeholder="Finished two practice checks, submitted my draft, or stayed on track"
          />
        </label>
        <label className="block text-sm text-gray-700">
          What shows it?
          <input
            className="mt-1 w-full rounded border px-3 py-2"
            value={weeklyEvidenceText}
            onChange={(event) => setWeeklyEvidenceText(event.target.value)}
            placeholder="Score, artifact, teacher feedback, or work log"
          />
        </label>
        <label className="block text-sm text-gray-700">
          What got in the way?
          <input
            className="mt-1 w-full rounded border px-3 py-2"
            value={weeklyObstacleText}
            onChange={(event) => setWeeklyObstacleText(event.target.value)}
            placeholder="Nothing, time, focus, unclear step, missing help"
          />
        </label>
        <label className="block text-sm text-gray-700">
          What will you change next week?
          <input
            className="mt-1 w-full rounded border px-3 py-2"
            value={weeklyAdjustmentText}
            onChange={(event) => setWeeklyAdjustmentText(event.target.value)}
            placeholder="Start earlier, ask for help, pick a smaller step"
          />
        </label>
        <label className="block text-sm text-gray-700">
          Confidence (1-5)
          <input
            className="mt-1 w-full rounded border px-3 py-2"
            type="number"
            min={1}
            max={5}
            value={weeklyConfidenceRating}
            onChange={(event) => setWeeklyConfidenceRating(Number(event.target.value))}
          />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          onClick={saveWeeklyCheckpoint}
          disabled={weeklyCheckpointSaving}
        >
          {weeklyCheckpointSaving ? 'Saving...' : weeklyCheckpoint ? 'Update Checkpoint' : 'Save Checkpoint'}
        </button>
        <p className="text-sm text-gray-500">Short answers are enough. This is weekly, not daily.</p>
      </div>
      {weeklyCheckpointHistoryContent}
        </>
      )}
    </div>
  );

  const weeklyCheckpointPastPanel = redesignEnabled && weeklyCheckpointHistory.length > 0 ? (
    <SectionCard title="Weekly Checkpoint History" meta="Past weekly reflections and planning adjustments.">
      <div className="space-y-3">
        {weeklyCheckpointHistory.map((checkpoint) => (
          <details key={checkpoint.id} className="rounded-xl border border-border bg-surface-alt/40 p-4">
            <summary className="cursor-pointer text-sm font-semibold text-text">
              Week of {formatDate(checkpoint.week_start_date)} · Confidence {checkpoint.confidence_rating}/5
            </summary>
            <div className="mt-3 space-y-1 text-sm text-muted">
              <p><span className="font-semibold text-text">Progress:</span> {checkpoint.progress_text || '(empty)'}</p>
              <p><span className="font-semibold text-text">Evidence:</span> {checkpoint.evidence_text || '(empty)'}</p>
              <p><span className="font-semibold text-text">Obstacle:</span> {checkpoint.obstacle_text || '(none)'}</p>
              <p><span className="font-semibold text-text">Next adjustment:</span> {checkpoint.adjustment_text || '(empty)'}</p>
            </div>
          </details>
        ))}
      </div>
    </SectionCard>
  ) : null;

  // Keep the existing plan, session, and wrap-up controls together in DOM order.
  const dailyWorkFirst = redesignEnabled && seniorPriorityWorkflow && goalsFeatureEnabled;
  const sectionSwitcher = (
    <>
        {sections.length > 1 && (
          redesignEnabled ? (
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 shadow-card">
              <label htmlFor="student-section-switcher" className="text-sm font-semibold text-text">Class</label>
              <div className="min-w-56 max-w-sm flex-1">
                <Select
                  id="student-section-switcher"
                  value={activeSection ?? ''}
                  onChange={(e) => {
                    void handleSectionChange(e.target.value);
                  }}
                >
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <label className="text-sm text-gray-500">Section</label>
              <select
                className="mt-1 w-full border rounded px-3 py-2"
                value={activeSection ?? ''}
                onChange={(e) => {
                  void handleSectionChange(e.target.value);
                }}
              >
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
          )
        )}

    </>
  );
  const dailyWorkPanel = (
    <>
      {activeTab === 'today' && goalsFeatureEnabled && !seniorGoalSetupRequired && <div id="student-daily-work" tabIndex={-1} aria-label="Daily work controls" className="scroll-mt-28" />}
      {activeTab === 'today' && goalsFeatureEnabled && !seniorGoalSetupRequired && (
        redesignEnabled ? (
          <SectionCard
            title={seniorPriorityWorkflow ? "Today's Work" : 'Daily Goals'}
            meta={seniorPriorityWorkflow
              ? 'Make a short morning plan, begin useful work, and wrap up at the end of class.'
              : dailyGoalAnchor
                ? 'Set today\'s next step, track work, and connect it to your long-term goal.'
                : 'Set today\'s direction, track outside work, and close the day with a reflection.'}
            className={cn(
              dailyGoalLocked && 'border-warning/30 shadow-card ring-1 ring-warning/20',
              workUnlocked && 'border-success/20',
            )}
            actions={(
              <div className="flex flex-wrap items-center gap-2">
                {goalCycle?.status && (
                  <Badge variant={cycleStatusBadgeVariant(goalCycle.status)}>
                    {goalStatusLabel}
                  </Badge>
                )}
                {goalCycle?.teacher_bypass_active && <Badge variant="warning">Teacher bypass active</Badge>}
              </div>
            )}
          >
            <div className="space-y-6">
              {seniorPriorityWorkflow && !loadingGoals && (
                <div className="space-y-3">
                  <ol aria-label="Today's steps" className="grid grid-cols-3 gap-2 text-sm">
                    {['Plan', 'Work', 'Wrap up'].map((label, index) => {
                      const currentStep = goalCycle?.status === 'checked_out' || workStatus === 'completed'
                        ? 2 : goalCycle?.status === 'checked_in' ? 1 : 0;
                      return <li key={label} aria-current={currentStep === index ? 'step' : undefined}
                        className={cn('rounded-lg border p-3', currentStep === index ? 'border-brand bg-brand/5 font-semibold' : 'border-border text-muted')}>
                        {index + 1}. {label}
                      </li>;
                    })}
                  </ol>
                  {goalCycle?.status === 'checked_out' ? <p className="text-sm font-semibold text-success">Today's wrap-up is saved.</p>
                    : goalCycle?.status === 'checked_in' ? (
                      <Button type="button" variant="secondary" onClick={() => {
                        const target = document.getElementById('student-wrap-up');
                        target?.focus();
                        target?.scrollIntoView?.({ block: 'start' });
                      }}>Ready to wrap up?</Button>
                    ) : <p className="text-sm text-muted">{isSchoolDay ? 'Start with your plan. Start Work saves it and begins your work session.' : 'Planning is optional today. You can view assignments or add a plan to begin a work session.'}</p>}
                </div>
              )}
              {!seniorPriorityWorkflow && !loadingGoals && (
                <div className={cn(
                  'rounded-2xl border px-4 py-4',
                  dailyGoalLocked
                    ? 'border-warning/25 bg-warning/10'
                    : workUnlocked
                      ? 'border-success/20 bg-success/10'
                      : 'border-border bg-surface-alt/50',
                )}
                >
                  <p className={cn(
                    'text-[11px] font-semibold uppercase tracking-[0.18em]',
                    dailyGoalLocked ? 'text-warning' : workUnlocked ? 'text-success' : 'text-muted',
                  )}
                  >
                    {dailyGoalLocked ? 'Step 1' : workUnlocked ? 'Step 2' : 'Today'}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-text">
                    {dailyGoalLocked ? 'Step 1: Complete Your Daily Goal' : workUnlocked ? 'Step 2: Start Your Work' : 'Today\'s Plan'}
                  </p>
                  <p className="mt-1 text-sm text-muted">
                    {dailyGoalLocked
                      ? 'Complete Step 1 to unlock work and live lessons.'
                      : workUnlocked
                        ? 'Daily goal complete. Work and live lessons are unlocked.'
                        : 'Follow the next step for today.'}
                  </p>
                </div>
              )}

              {!seniorPriorityWorkflow && <div className="grid gap-3 md:grid-cols-4">
                <GoalsStat
                  label="Day Type"
                  value={dayTypeLabel}
                  detail={dayTypeDetail}
                />
                <GoalsStat
                  label="Check-In Status"
                  value={goalStatusLabel}
                  detail={gateRequired
                    ? 'Morning check-in still blocks live lessons and assignment work.'
                    : goalCycle?.status === 'checked_out'
                      ? 'Today is already closed and reflected.'
                      : goalCycle?.teacher_bypass_active
                        ? 'A teacher bypass is active for this date.'
                        : 'Your workspace is open and ready to track.'}
                />
                <GoalsStat
                  label="Missing Work"
                  value={missingWorkCount}
                  detail={missingWorkDetail}
                />
                <GoalsStat
                  label="Work Time Today"
                  value={activeWorkBlock ? formatDuration(activeWorkElapsedSeconds) : totalLoggedExternalMinutes ? `${totalLoggedExternalMinutes}m` : workStatusLabel(workStatus)}
                  detail={loadingExternalWork
                    ? 'Syncing work session timeline.'
                    : activeWorkBlock
                      ? `${activeWorkBlock.title} is running now.`
                      : workStatus === 'not_started'
                        ? 'Start a session when you are ready.'
                        : `Status: ${workStatusLabel(workStatus)}.`}
                />
              </div>}

              {dailyGoalAnchor && !seniorPriorityWorkflow && (
                <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                  <span className="font-semibold">{seniorPriorityWorkflow ? 'Longer-term direction:' : 'Long-term goal:'}</span> {dailyGoalAnchor.title}
                  <span className="ml-1 text-muted">
                    {dailyPlanConnectsToPrimaryGoal
                      ? 'Today\'s plan can be your next step toward this.'
                      : 'Today is not linked to this goal yet.'}
                  </span>
                </div>
              )}

              {(goalsError || gateFromNavigation?.gate_required) && (
                <div className="rounded-xl border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-danger">
                  <p>{goalsError || 'Morning check-in is required before continuing.'}</p>
                </div>
              )}

              {gateRequired && !loadingGoals && (
                <div className="rounded-xl border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-text">
                  Morning check-in is required before starting assignments or live lesson work.
                </div>
              )}

              {dailyPriorityMissing && !loadingGoals && (
                <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-text">
                  Add today&apos;s priority and what you will be able to show by the end of class. Lessons and assignments remain available.
                </div>
              )}

              {loadingGoals ? (
                <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
                  {seniorPriorityWorkflow ? 'Loading today\'s work...' : 'Loading daily goals...'}
                </div>
              ) : (
                <>
                  {/* Non-school day: Show simplified view */}
                  {showNonSchoolFocusView ? (
                    <div className="space-y-4">
                      <SectionCard
                        title={seniorPriorityWorkflow ? "Optional plan today" : "Today's Focus"}
                        meta={seniorPriorityWorkflow
                          ? 'No morning plan is required today. Choose useful work or set an optional direction.'
                          : 'No daily goals are required today. Choose one useful focus or set an optional plan.'}
                        className="border-info/20"
                      >
                        <div className="space-y-4">
                          <div className="rounded-xl border border-info/20 bg-info/10 px-4 py-3 text-sm text-text">
                            <p className="font-semibold">{seniorPriorityWorkflow ? 'No morning plan required today.' : 'No daily goals required today.'}</p>
                            <p className="mt-1 text-muted">
                              {seniorPriorityWorkflow
                                ? 'Start with useful work, or add a short morning plan if it will help you focus.'
                                : 'Use this lighter view for makeup work and practice. If a written plan will help, set optional daily goals below.'}
                            </p>
                          </div>

                          {/* Primary Goal Section */}
                          {primaryGoalFeatureEnabled && primaryGoal && (
                            <div className="rounded-xl border border-brand/20 bg-brand/5 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-text">{seniorPriorityWorkflow ? 'Longer-Term Direction' : 'Long-term Goal'}</p>
                                  <p className="mt-1 text-sm font-medium text-text">{primaryGoal.title}</p>
                                  <p className="mt-2 text-xs text-muted">
                                    {primaryGoal.why_text}
                                  </p>
                                </div>
                                <Badge variant="muted" className="text-xs">
                                  Active
                                </Badge>
                              </div>
                            </div>
                          )}

                          {/* Missing Work Priority */}
                          {missingWorkCount > 0 && (
                            <div className="rounded-xl border border-warning/20 bg-warning/5 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-text">Makeup Work Priority</p>
                                  <p className="mt-1 text-sm text-text">
                                    {missingWorkCount} item{missingWorkCount === 1 ? '' : 's'} need attention
                                  </p>
                                  <p className="mt-2 text-xs text-muted">
                                    Complete these to stay on track with your learning goals.
                                  </p>
                                </div>
                                <Badge variant="warning" className="text-xs">
                                  {missingWorkCount}
                                </Badge>
                              </div>
                            </div>
                          )}

                          {/* Optional Daily Goal */}
                          <div className="flex flex-wrap gap-3">
                            <Button
                              variant="primary"
                              onClick={() => setDailyGoalOptional(true)}
                            >
                              {seniorPriorityWorkflow ? 'Set Optional Morning Plan' : 'Set Optional Daily Goals'}
                            </Button>
                          </div>
                        </div>
                      </SectionCard>
                    </div>
                  ) : (
                    /* School day or optional goals: Show full check-in */
                    showDailyCheckInForm ? (
                    seniorPriorityWorkflow ? (
                      <SectionCard
                        title="Your Main Focus Today"
                        meta="Choose the most useful step toward your Main Goal. Your personal project is optional additional work."
                        className="border-brand/20"
                      >
                        <div className="space-y-4">
                          {teacherDailySupportNotice}
                          <div className="space-y-2">
                            <FieldLabel htmlFor="senior-priority">Today's Main Task</FieldLabel>
                            <p className="text-sm text-muted">What is the most useful step you can take toward your Main Goal today?</p>
                            <Input
                              id="senior-priority"
                              placeholder="Module, assignment, certification, or another useful step"
                              value={planTarget}
                              onChange={(event) => setPlanTarget(event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <FieldLabel htmlFor="senior-first-step">How Will You Get Started?</FieldLabel>
                            <p className="text-sm text-muted">
                              {goalCycle?.latest_support && firstStepText.trim()
                                ? 'Your teacher prepared this starting action. Ask for clarification or adjust it if you need a different way to begin.'
                                : 'Choose one small action you can do right away.'}
                            </p>
                            <Input
                              id="senior-first-step"
                              placeholder="For example: Open the lesson and read the first question."
                              value={firstStepText}
                              onChange={(event) => setFirstStepText(event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <FieldLabel htmlFor="senior-outcome">What Will You Show?</FieldLabel>
                            <p className="text-sm text-muted">What progress will you be able to show by the end of class?</p>
                            <Input
                              id="senior-outcome"
                              placeholder="Completed work, screenshot, score, artifact, or teacher check"
                              value={planSuccessCriteria}
                              onChange={(event) => setPlanSuccessCriteria(event.target.value)}
                            />
                          </div>
                          <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                            <p className="text-sm font-semibold text-text">Plan Your 90 Minutes <span className="font-normal text-muted">(optional this cycle)</span></p>
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              <div className="space-y-2">
                                <FieldLabel htmlFor="protected-goal-minutes">Protected time for your Main Goal</FieldLabel>
                                <Select id="protected-goal-minutes" value={protectedGoalMinutes} onChange={(event) => setProtectedGoalMinutes(event.target.value)}>
                                  <option value="">Choose minutes</option>
                                  {[5, 10, 15, 20, 25, 30, 40, 45, 60].map((minutes) => <option key={minutes} value={minutes}>{minutes} minutes</option>)}
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <FieldLabel htmlFor="planned-work-mode">Likely class mode</FieldLabel>
                                <Select id="planned-work-mode" value={plannedWorkMode} onChange={(event) => setPlannedWorkMode(event.target.value)}>
                                  <option value="">No additional mode planned</option>
                                  <option value="five_week_goal">Five-week goal</option><option value="nerd_herd">Nerd Herd service</option><option value="live_lesson">Teacher live lesson</option><option value="personal_project">Personal project</option><option value="required_recovery">Required / recovery work</option><option value="exploration">Exploration</option><option value="transition_cleanup">Transition / cleanup</option>
                                </Select>
                              </div>
                            </div>
                            <div className="mt-3 space-y-2">
                              <FieldLabel htmlFor="planned-secondary-work">After protected goal time, I plan to...</FieldLabel>
                              <Input id="planned-secondary-work" placeholder="Nerd Herd ticket, 3D print, personal project, or other useful work" value={plannedSecondaryWork} onChange={(event) => setPlannedSecondaryWork(event.target.value)} />
                            </div>
                          </div>
                          {personalProjectGoal ? (
                            <div className="space-y-2 rounded-xl border border-border bg-surface-alt/50 p-4">
                              <FieldLabel htmlFor="senior-personal-project">Project Task</FieldLabel>
                              <p className="text-sm text-muted">Because you have an active project goal, record the progress you will make today.</p>
                              <Input id="senior-personal-project" placeholder="Personal or senior project work for today" value={personalProjectTask} onChange={(event) => setPersonalProjectTask(event.target.value)} />
                              <FieldLabel htmlFor="senior-personal-project-proof">Expected Project Evidence</FieldLabel>
                              <Input id="senior-personal-project-proof" placeholder="What project progress will you show by the end of class?" value={personalProjectProof} onChange={(event) => setPersonalProjectProof(event.target.value)} />
                            </div>
                          ) : (
                            <p className="text-sm text-muted">Personal-project planning becomes available after its optional five-week goal is created.</p>
                          )}
                          <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3 text-sm text-text">
                            <span className="font-semibold">Main Focus:</span> Main Goal
                            {personalProjectTask.trim() && <span className="mt-1 block text-muted">Also planned: {personalProjectTask.trim()}</span>}
                          </div>
                          <Button type="button" onClick={submitCheckIn} disabled={submittingCheckIn}>
                            {submittingCheckIn ? 'Starting...' : 'Start Work'}
                          </Button>
                        </div>
                      </SectionCard>
                    ) : (
                    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,0.95fr)]">
                      <SectionCard
                        title={!isSchoolDay ? 'Optional daily goals' : 'Morning check-in'}
                        meta={!isSchoolDay
                          ? 'Optional today. Set a small plan only if it will help you focus.'
                          : dailyGoalAnchor
                          ? 'Choose the next useful step toward your long-term goal.'
                          : 'Choose today\'s track, target, first step, and success check.'}
                        className="border-brand/15"
                      >
                        <div className="space-y-5">
                          {teacherDailySupportNotice}
                          <div className="rounded-2xl border border-brand/15 bg-brand/5 p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-text">Daily plan</p>
                              <p className="mt-1 text-sm text-muted">
                                  {!isSchoolDay
                                    ? 'What useful work do you want to focus on today?'
                                    : dailyGoalAnchor
                                    ? 'What is your next step toward your goal today?'
                                    : 'Set your Track, Target, First Step, and Success Check for today.'}
                                </p>
                              </div>
                              <Button
                                id="ai-refine-goal"
                                type="button"
                                variant="secondary"
                                onClick={runAiAssist}
                                disabled={aiAssistLoading || !dailyPlan.target.trim()}
                              >
                                {aiAssistLoading ? 'Refining...' : 'Get AI Suggestions'}
                              </Button>
                            </div>
                            <div className="mt-4 grid gap-3 md:grid-cols-2">
                              <div className="space-y-2">
                                <FieldLabel htmlFor="plan-track">Track</FieldLabel>
                                <Select
                                  id="plan-track"
                                  value={planTrack}
                                  onChange={(event) => {
                                    const nextTrack = event.target.value as PlanTrack;
                                    setPlanTrack(nextTrack);
                                    if (nextTrack === 'main_project' || nextTrack === 'secondary_project') {
                                      setCompletionProjectRole(nextTrack);
                                    }
                                    clearAiRefinement();
                                  }}
                                >
                                  {PLAN_TRACK_OPTIONS.map((option) => (
                                    <option key={option.value || 'choose'} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                                {(sectionSubgroup || suggestedPlanTrack) && (
                                  <p className="text-xs text-muted">
                                    Pathway: {sectionSubgroup || trackLabel(suggestedPlanTrack)}
                                    {suggestedPlanTrack && !planTrack ? ` | Suggested track: ${trackLabel(suggestedPlanTrack)}` : ''}
                                  </p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <FieldLabel htmlFor="plan-target">{seniorPriorityWorkflow ? "Today's Priority" : 'Target'}</FieldLabel>
                                <Input
                                  id="plan-target"
                                  placeholder={dailyGoalAnchor
                                    ? 'Target: What step will move your goal forward today?'
                                    : 'Target: What will you accomplish today?'}
                                  value={planTarget}
                                  onChange={(event) => {
                                    setPlanTarget(event.target.value);
                                    clearAiRefinement();
                                  }}
                                />
                              </div>
                              <div className="space-y-2">
                                <FieldLabel htmlFor="first-step-text">First step</FieldLabel>
                                <Input
                                  id="first-step-text"
                                  placeholder="First step: How will you start?"
                                  value={firstStepText}
                                  onChange={(event) => {
                                    setFirstStepText(event.target.value);
                                    clearAiRefinement();
                                  }}
                                />
                              </div>
                              <div className="space-y-2">
                                <FieldLabel htmlFor="plan-success-criteria">{seniorPriorityWorkflow ? 'What should exist by the end of class?' : 'Success Check'}</FieldLabel>
                                <Input
                                  id="plan-success-criteria"
                                  placeholder="Success Check: How will you know you're done?"
                                  value={planSuccessCriteria}
                                  onChange={(event) => {
                                    setPlanSuccessCriteria(event.target.value);
                                    clearAiRefinement();
                                  }}
                                />
                              </div>
                            </div>
                            <div className="mt-4 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-text">
                              <span className="font-semibold">Your Plan:</span>{' '}
                              {dailyPlan.track && dailyPlan.target
                                ? `${trackLabel(dailyPlan.track)} → ${dailyPlan.target}`
                                : 'Choose a track and target.'}
                              {dailyPlan.first_step ? ` → ${dailyPlan.first_step}` : ''}
                              {dailyPlan.success_criteria ? ` → ${dailyPlan.success_criteria}` : ''}
                            </div>
                          </div>

                          {aiApplied && !aiSuggestion && (
                            <div className="rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">
                              AI refinement applied. Your plan details have been updated with AI suggestions.
                            </div>
                          )}

                          <div className="rounded-2xl border border-border bg-surface-alt/50 p-4">
                            <div>
                              <p className="text-sm font-semibold text-text">Optional work item</p>
                              <p className="mt-1 text-sm text-muted">If today&apos;s target is listed here, select it to connect the plan. Otherwise keep your custom target above.</p>
                            </div>
                            {availableCompletionWorkItems.length > 0 && (
                              <div className="mt-4 grid gap-2 md:grid-cols-2">
                                {availableCompletionWorkItems.map((item) => {
                                  const selected = selectedCompletionWorkKey === item.key;
                                  return (
                                    <button
                                      key={item.key}
                                      type="button"
                                      className={`rounded-xl border px-4 py-3 text-left transition ${
                                        selected
                                          ? 'border-brand bg-brand/10 text-text shadow-sm'
                                          : 'border-border bg-surface hover:border-brand/50'
                                      }`}
                                      onClick={() => {
                                        setSelectedCompletionWorkKey(selected ? '' : item.key);
                                        if (!selected) {
                                          setPlanTarget((current) => current.trim() || item.title);
                                          const inferredTrack = item.trackKey === 'python_essentials'
                                            ? 'python'
                                            : item.trackKey === 'comptia'
                                              ? 'comptia'
                                              : item.trackKey === 'project'
                                                ? 'main_project'
                                                : item.kind === 'internal_assignment'
                                                  ? 'missing_work'
                                                  : planTrack;
                                          if (inferredTrack) {
                                            setPlanTrack(inferredTrack as PlanTrack);
                                          }
                                        }
                                        clearAiRefinement();
                                      }}
                                    >
                                      <span className="block text-sm font-semibold">{item.title}</span>
                                      <span className="mt-1 block text-xs text-muted">{item.source}</span>
                                      <span className="mt-1 block text-xs text-muted">{item.detail}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            <div className="mt-4 space-y-2">
                              <FieldLabel htmlFor="completion-project-role">Project role</FieldLabel>
                              <Select
                                id="completion-project-role"
                                value={completionProjectRole}
                                onChange={(event) => setCompletionProjectRole(event.target.value as ProjectRole)}
                              >
                                {PROJECT_ROLE_OPTIONS.map((option) => (
                                  <option key={option.value || 'none'} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </Select>
                              <p className="text-xs text-muted">
                                Use this only when today&apos;s target is project work; it helps your teacher separate main and secondary projects.
                              </p>
                            </div>
                            <div className="mt-4 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-text">
                              <span className="font-semibold">Linked work:</span>{' '}
                              {selectedCompletionWorkItem?.title || 'No linked work item. Your custom target will still be saved.'}
                            </div>
                          </div>

                          <details className="rounded-xl border border-border bg-surface-alt/50 p-4">
                            <summary className="cursor-pointer text-sm font-semibold text-text">Advanced details (optional)</summary>
                            <div className="mt-4 grid gap-3 md:grid-cols-3">
                            <div className="space-y-2">
                              <FieldLabel htmlFor="learning-stage-tag">Stage tag</FieldLabel>
                              <Select
                                id="learning-stage-tag"
                                value={learningStageTag}
                                onChange={(event) => setLearningStageTag(event.target.value)}
                              >
                                <option value="">Optional stage tag</option>
                                <option value="why">WHY</option>
                                <option value="define">DEFINE</option>
                                <option value="do">DO</option>
                                <option value="explain">EXPLAIN</option>
                                <option value="apply">APPLY</option>
                                <option value="reflect">REFLECT</option>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <FieldLabel htmlFor="learning-tags">Learning tags</FieldLabel>
                              <Input
                                id="learning-tags"
                                placeholder="Learning readiness tags (comma)"
                                value={learningTags}
                                onChange={(event) => setLearningTags(event.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <FieldLabel htmlFor="completion-tags">Completion tags</FieldLabel>
                              <Input
                                id="completion-tags"
                                placeholder="Completion readiness tags (comma)"
                                value={completionTags}
                                onChange={(event) => setCompletionTags(event.target.value)}
                              />
                            </div>
                          </div>
                          </details>

                          <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface-alt/50 p-4 md:flex-row md:items-center md:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-text">Unlock the rest of your workspace</p>
                              <p className="mt-1 text-sm text-muted">
                                {!isSchoolDay
                                  ? 'Submit this optional plan to use the full daily goal workspace today.'
                                  : 'Submit your goals to access live lessons, assignments, and the external-work tracker.'}
                              </p>
                            </div>
                            <Button
                              type="button"
                              onClick={submitCheckIn}
                              disabled={submittingCheckIn}
                            >
                              {submittingCheckIn
                                ? 'Submitting...'
                                : !isSchoolDay
                                  ? 'Submit Optional Check-In'
                                  : 'Submit Morning Check-In'}
                            </Button>
                          </div>
                        </div>
                      </SectionCard>

                      <div className="space-y-4">
                        {aiSuggestion ? (
                          <SectionCard
                            title="AI goal review"
                            meta="Review the suggestion and record why you accept or dismiss it."
                            className="border-brand/20"
                          >
                            <div className="space-y-4">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <Badge variant="warning">Clarity score: {Number(aiSuggestion.clarity_score || 0)}</Badge>
                                {aiSuggestion.smart_rubric && (
                                  <Badge variant="muted">
                                    Clarity score: {Number(aiSuggestion.smart_rubric.overall_score || 0)} / 100
                                  </Badge>
                                )}
                              </div>
                              <div className="rounded-xl border border-brand/20 bg-brand/5 p-4 text-sm text-text">
                                {aiSuggestion.refined_goal}
                              </div>
                              {aiSuggestion.refined_target && (
                                <div className="rounded-xl border border-border bg-surface-alt/50 p-3 text-sm text-text">
                                  <span className="font-semibold">Target:</span> {aiSuggestion.refined_target}
                                </div>
                              )}
                              {aiSuggestion.measurable_criterion && (
                                <div className="rounded-xl border border-border bg-surface-alt/50 p-3 text-sm text-text">
                                  <span className="font-semibold">Success Check:</span> {aiSuggestion.suggested_success_criteria || aiSuggestion.measurable_criterion}
                                </div>
                              )}
                              {aiSuggestion.coaching_question && (
                                <div className="rounded-xl border border-border bg-surface-alt/50 p-3 text-sm text-text">
                                  <span className="font-semibold">Coaching prompt:</span> {aiSuggestion.coaching_question}
                                </div>
                              )}
                              {aiSuggestion.suggested_first_step && (
                                <div className="rounded-xl border border-success/20 bg-success/10 p-3 text-sm text-text">
                                  <span className="font-semibold">Suggested first step:</span> {aiSuggestion.suggested_first_step}
                                </div>
                              )}
                              {aiSuggestion.smart_rubric && (
                                <div className="rounded-xl border border-border bg-surface-alt/50 p-3 space-y-2">
                                  <div className="flex flex-wrap gap-2">
                                    {(['S', 'M', 'A', 'R', 'T'] as const).map((dimension) => {
                                      const row = aiSuggestion.smart_rubric?.dimensions?.[dimension];
                                      if (!row) return null;
                                      return (
                                        <Badge
                                          key={dimension}
                                          variant={row.meets ? 'success' : 'warning'}
                                          className="text-[11px]"
                                        >
                                          {dimension}: {row.score}
                                        </Badge>
                                      );
                                    })}
                                  </div>
                                  {(aiSuggestion.smart_rubric.deficiency_labels || []).length > 0 && (
                                    <p className="text-xs text-warning">
                                      Improve: {(aiSuggestion.smart_rubric.deficiency_labels || []).map(smartDeficiencyLabel).join(' | ')}
                                    </p>
                                  )}
                                </div>
                              )}
                              {aiSuggestion.priority_target?.available && (
                                <div className="rounded-xl border border-border bg-surface-alt/50 p-3 text-xs text-text">
                                  Priority target: {aiSuggestion.priority_target.title || '(untitled)'}
                                  {aiSuggestion.priority_target.source_app ? ` (${aiSuggestion.priority_target.source_app})` : ''}
                                  {' '}| {aiSuggestion.priority_target.kind === 'internal_assignment' ? 'internal' : 'external'}
                                  {aiSuggestion.priority_target.overdue ? ' | overdue' : ''}
                                  {!aiSuggestion.priority_target.overdue && aiSuggestion.priority_target.due_today ? ' | due today' : ''}
                                  {aiSuggestion.priority_target.in_grace ? ' | grace window' : ''}
                                  {aiSuggestion.priority_target.priority_reason ? ` | ${aiSuggestion.priority_target.priority_reason}` : ''}
                                </div>
                              )}
                              {aiSuggestion.guardrails && (
                                <div className="rounded-xl border border-border bg-surface-alt/50 p-3 text-xs text-text">
                                  Guardrails: coaching only, student owns work, no auto-complete.
                                </div>
                              )}
                              <div className="space-y-2">
                                <FieldLabel htmlFor="ai-rationale">Decision rationale</FieldLabel>
                                <Textarea
                                  id="ai-rationale"
                                  placeholder="Why are you accepting or dismissing this suggestion?"
                                  value={aiRationale}
                                  onChange={(event) => setAiRationale(event.target.value)}
                                />
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  onClick={applyAiSuggestion}
                                  disabled={aiAssistLoading}
                                >
                                  Apply Suggestion
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={dismissAiSuggestion}
                                  disabled={aiAssistLoading}
                                >
                                  Dismiss Suggestion
                                </Button>
                              </div>
                            </div>
                          </SectionCard>
                        ) : (
                          <SectionCard
                            title="Drafting guide"
                            meta="Use one specific target, one clear result, and a simple reason you picked it."
                          >
                            <div className="space-y-3 text-sm text-muted">
                              <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <p className="font-semibold text-text">Strong learning plan</p>
                                <p className="mt-1">Name the concept or skill you want to improve and how you will know it worked.</p>
                              </div>
                              <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <p className="font-semibold text-text">Strong completion plan</p>
                                <p className="mt-1">Pick one assignment, missing-work task, or deliverable you can finish today.</p>
                              </div>
                              <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <p className="font-semibold text-text">AI assist is optional</p>
                                <p className="mt-1">Draft your own plan first, then use AI to tighten scope or measurability if needed.</p>
                              </div>
                            </div>
                          </SectionCard>
                        )}

                        <SectionCard
                          title="What opens after check-in"
                          meta="These areas become the active workflow once today's daily plan is submitted."
                          className="opacity-75"
                        >
                          <div className="space-y-3">
                            <div className="space-y-3">
                              <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <p className="text-sm font-semibold text-text">Live lesson access</p>
                                <p className="mt-1 text-sm text-muted">Join the teacher's room without the morning gate blocking you.</p>
                              </div>
                              <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <p className="text-sm font-semibold text-text">Assignment queue</p>
                                <p className="mt-1 text-sm text-muted">Continue make-up work and track due items from the dashboard.</p>
                              </div>
                              <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <p className="text-sm font-semibold text-text">External-work tracker</p>
                                <p className="mt-1 text-sm text-muted">Start timed blocks, save evidence, and switch cleanly between outside tasks.</p>
                              </div>
                            </div>
                          </div>
                        </SectionCard>
                      </div>
                    </div>
                    )
                  ) : (
                    <div className={cn(
                      'grid gap-4',
                      seniorPriorityWorkflow
                        ? 'grid-cols-1'
                        : 'xl:grid-cols-[minmax(0,1.45fr)_minmax(0,0.95fr)]',
                    )}>
                      <div className="space-y-4">
                        <div className="rounded-2xl border border-success/20 bg-success/10 px-4 py-4">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-success">Step 2</p>
                          <p className="mt-1 text-lg font-semibold text-text">Step 2: Complete Today&apos;s Work</p>
                          <p className="mt-1 text-sm text-muted">Follow the plan below. When class is ending, complete the required daily Wrap Up.</p>
                        </div>
                        <SectionCard
                          title={seniorPriorityWorkflow ? "Today's Plan" : 'Your Work Today'}
                          meta={seniorPriorityWorkflow ? 'The task and evidence target you set at check-in.' : 'Your plan, current work status, and next action.'}
                          actions={<Badge variant={cycleStatusBadgeVariant(goalCycle?.status)}>Status: {goalStatusLabel}</Badge>}
                        >
                          {seniorPriorityWorkflow ? (
                            <div className="space-y-3 text-sm">
                              <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">Today&apos;s Main Task</p>
                                <p className="mt-2 font-semibold text-text">{currentDailyPlan.target || '(not set)'}</p>
                              </div>
                              <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">What You Planned to Show</p>
                                <p className="mt-2 text-text">{currentDailyPlan.successCriteria || '(not set)'}</p>
                              </div>
                              <div className="flex items-center justify-between gap-3 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3">
                                <span className="font-semibold text-text">Work status</span>
                                <Badge variant="default">{workStatusLabel(workStatus)}</Badge>
                              </div>
                            </div>
                          ) : (<>
                          <div className="grid gap-3 md:grid-cols-2">
                            <Card className="border-brand/20 bg-brand/5 shadow-none">
                              <CardContent className="space-y-2 pt-4">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">Daily plan</p>
                                {currentDailyPlan.hasStructuredPlan ? (
                                  <div className="grid gap-1 rounded-xl border border-border bg-surface px-3 py-2 text-xs text-muted">
                                    <span><span className="font-semibold text-text">Track:</span> {trackLabel(currentDailyPlan.track)}</span>
                                    <span><span className="font-semibold text-text">Target:</span> {currentDailyPlan.target || '(not set)'}</span>
                                    <span><span className="font-semibold text-text">First step:</span> {currentDailyPlan.firstStep || '(not set)'}</span>
                                    <span><span className="font-semibold text-text">Success check:</span> {currentDailyPlan.successCriteria || '(not set)'}</span>
                                  </div>
                                ) : (
                                  <div className="space-y-1 text-sm text-text">
                                    <p><span className="font-semibold">Learning:</span> {currentDailyPlan.legacyLearningText || '(not set)'}</p>
                                    <p><span className="font-semibold">Completion:</span> {currentDailyPlan.legacyCompletionText || '(not set)'}</p>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                            <Card className="border-border/80 bg-surface-alt/50 shadow-none">
                              <CardContent className="space-y-2 pt-4">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">Work status</p>
                                <p className="text-sm font-semibold text-text">{currentDailyPlan.target || currentCompletionGoal?.smart_text || '(not set)'}</p>
                                <div className="rounded-xl border border-border bg-surface px-3 py-2">
                                  <div className="flex items-center justify-between gap-3 text-xs text-muted">
                                    <span>{completionGoalProgress.label}</span>
                                    <span className="font-semibold text-text">{workStatusLabel(completionGoalProgress.status)}</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                          {workStatus !== 'not_started' && (
                            <div className="mt-4 rounded-2xl border border-brand/30 bg-brand/10 px-4 py-3">
                              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                <div>
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Working now</p>
                                  <p className="mt-1 text-sm font-semibold text-text">
                                    Focus: {planWorkSession.focus || 'Daily work'}
                                  </p>
                                  <p className="mt-1 text-sm text-muted">
                                    Target: {planWorkSession.title}
                                  </p>
                                </div>
                                <Badge variant={
                                  workStatus === 'completed' ? 'success' :
                                  workStatus === 'paused' ? 'warning' :
                                  workStatus === 'in_progress' ? 'default' : 'muted'
                                }>
                                  Status: {workStatusLabel(workStatus)}
                                </Badge>
                              </div>
                            </div>
                          )}
                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            <GoalsStat
                              label="Missing Work"
                              value={missingWorkCount}
                              detail={missingWorkDetail}
                            />
                            {!seniorPriorityWorkflow && <GoalsStat
                              label="Work Time Today"
                              value={activeWorkBlock ? formatDuration(activeWorkElapsedSeconds) : totalLoggedExternalMinutes ? `${totalLoggedExternalMinutes}m` : workStatusLabel(workStatus)}
                              detail={activeWorkBlock ? activeWorkBlock.title : workStatus === 'not_started' ? 'No session has started yet.' : `Status: ${workStatusLabel(workStatus)}.`}
                            />}
                          </div>
                          </>)}
                        </SectionCard>

                        {seniorPriorityWorkflow && (
                          <div id="protected-focus-section">
                            <SectionCard
                              title={currentWorkBlock ? 'Protected Focus Block' : 'Protected Focus Block Not Started'}
                              meta="Use this block for your Main Goal. When it ends, make an intentional choice instead of drifting into the next activity."
                              className={activeWorkRemainingSeconds === 0 ? 'border-warning/40' : 'border-brand/20'}
                              actions={<Badge variant={activeWorkRemainingSeconds === 0 ? 'warning' : activeWorkBlock ? 'success' : pausedWorkBlock ? 'warning' : 'muted'}>{activeWorkBlock ? 'In progress' : pausedWorkBlock ? 'Paused' : 'Not running'}</Badge>}
                            >
                              {currentWorkBlock ? (
                                <div className="space-y-4">
                                  {focusSupportStatusMessage && (
                                    <div role="status" className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm font-medium text-success">
                                      {focusSupportStatusMessage}
                                    </div>
                                  )}
                                  <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface-alt/50 p-4 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <p className="text-base font-semibold text-text">{currentWorkBlock.title}</p>
                                      <p className="mt-1 text-sm text-muted">Planned: {currentWorkBlock.planned_minutes || 'open'} minutes · Elapsed: {formatDuration(activeWorkElapsedSeconds)}</p>
                                    </div>
                                    <div className="rounded-xl border border-brand/20 bg-brand/5 px-5 py-3 text-center">
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">{pausedWorkBlock ? 'Paused' : activeWorkRemainingSeconds === null ? 'Elapsed' : activeWorkRemainingSeconds === 0 ? 'Time to reset' : 'Time remaining'}</p>
                                      <p className="mt-1 text-2xl font-semibold text-text">{countdownHidden ? 'Hidden' : formatDuration(activeWorkRemainingSeconds === null ? activeWorkElapsedSeconds : activeWorkRemainingSeconds)}</p>
                                      <button type="button" className="mt-1 text-xs font-medium text-brand underline" onClick={toggleCountdownHidden}>{countdownHidden ? 'Show countdown' : 'Hide countdown'}</button>
                                    </div>
                                  </div>
                                  {activeWorkBlock && activeWorkRemainingSeconds === 0 && (
                                    <div className="rounded-xl border border-warning/30 bg-warning/10 p-4">
                                      <p className="font-semibold text-text">Pause and choose your next move.</p>
                                      <p className="mt-1 text-sm text-muted">The timer does not complete your task. Choose what will help you make useful progress.</p>
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        <Button type="button" variant="secondary" onClick={() => extendMainGoalWork(10)} disabled={externalWorkActionLoading}>Continue Working +10 min</Button>
                                        <Button type="button" variant="secondary" onClick={handlePauseWork} disabled={externalWorkActionLoading}>Take a Short Break</Button>
                                        <Button type="button" variant="secondary" onClick={updateFocusProgress}>Update Progress</Button>
                                        <Button type="button" onClick={requestFocusHelp} disabled={externalWorkActionLoading}>Need Help</Button>
                                        {plannedSecondaryWork.trim() && <Button type="button" onClick={moveToPlannedSecondaryWork} disabled={externalWorkActionLoading}>Switch to: {plannedSecondaryWork.trim()}</Button>}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-2">
                                    {activeWorkBlock ? <Button type="button" variant="ghost" onClick={handlePauseWork} disabled={externalWorkActionLoading}>Pause</Button> : <Button type="button" onClick={resumeMainGoalWork} disabled={externalWorkActionLoading}>Resume</Button>}
                                    {activeWorkBlock && <Button type="button" variant="ghost" onClick={() => extendMainGoalWork(5)} disabled={externalWorkActionLoading}>Continue to a Safe Stopping Point +5 min</Button>}
                                    <Button type="button" variant="secondary" onClick={handleCompleteWork} disabled={externalWorkActionLoading}>Finish Focus Block</Button>
                                    <Button type="button" variant="secondary" onClick={requestFocusHelp} disabled={externalWorkActionLoading}>Need Help</Button>
                                  </div>
                                </div>
                              ) : protectedGoalMinutes ? (
                                <Button type="button" onClick={handleStartWorkBlock} disabled={externalWorkActionLoading}>Start {protectedGoalMinutes}-Minute Main Goal Block</Button>
                              ) : (
                                <p className="text-sm text-muted">No protected minutes were chosen at check-in. You can still work from today’s priority and complete your wrap-up normally.</p>
                              )}
                            </SectionCard>
                          </div>
                        )}

                        {!seniorPriorityWorkflow && <><div id="external-work-section">
                          <SectionCard
                            title={activeWorkBlock ? 'Current Work Session' : 'Start Work Session'}
                            meta={activeWorkBlock
                              ? 'Capture evidence, adjust minutes, and close or switch the active session.'
                              : 'Start from today\'s Daily Plan. The work session tracks time; it does not redefine your work.'}
                            className={workUnlocked ? 'border-success/25 shadow-card' : undefined}
                            actions={(
                              <Badge variant={activeWorkBlock ? workBlockStatusBadgeVariant(activeWorkBlock.status) : 'muted'}>
                                {loadingExternalWork
                                  ? 'Syncing timeline...'
                                  : activeWorkBlock
                                    ? `Live timer ${formatDuration(activeWorkElapsedSeconds)}`
                                    : 'Timeline ready'}
                              </Badge>
                            )}
                          >
                          {activeWorkBlock ? (
                            <div className="space-y-4">
                              <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface-alt/50 p-4 md:flex-row md:items-start md:justify-between">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-base font-semibold text-text">{activeWorkBlock.title}</p>
                                    <Badge variant={workBlockStatusBadgeVariant(activeWorkBlock.status)}>
                                      {activeWorkBlock.status}
                                    </Badge>
                                    <Badge variant="muted">{activeWorkBlock.track_key}</Badge>
                                  </div>
                                  <p className="mt-2 text-sm text-muted">
                                    {activeWorkBlock.source_app || 'Outside work'} • Logged minutes: {activeWorkBlock.actual_minutes}
                                  </p>
                                </div>
                                <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-right">
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Live timer</p>
                                  <p className="mt-1 text-2xl font-semibold text-text">{formatDuration(activeWorkElapsedSeconds)}</p>
                                </div>
                              </div>

                              <div className="grid gap-3 md:grid-cols-2">
                                <div className="space-y-2">
                                  <FieldLabel htmlFor="block-evidence-note">Evidence note</FieldLabel>
                                  <Textarea
                                    id="block-evidence-note"
                                    placeholder="Evidence note..."
                                    value={blockEvidenceNote}
                                    onChange={(event) => setBlockEvidenceNote(event.target.value)}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <FieldLabel htmlFor="block-evidence-link">Evidence link</FieldLabel>
                                  <Input
                                    id="block-evidence-link"
                                    placeholder="Evidence link..."
                                    value={blockEvidenceLink}
                                    onChange={(event) => setBlockEvidenceLink(event.target.value)}
                                  />
                                </div>
                              </div>

                              <div className="grid gap-3 md:grid-cols-[112px_repeat(4,minmax(0,1fr))]">
                                <div className="space-y-2">
                                  <FieldLabel htmlFor="minutes-delta">Minutes</FieldLabel>
                                  <Input
                                    id="minutes-delta"
                                    type="number"
                                    min={1}
                                    value={minutesDelta}
                                    onChange={(event) => setMinutesDelta(Number(event.target.value))}
                                  />
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-full"
                                    onClick={addExternalWorkMinutes}
                                    disabled={externalWorkActionLoading}
                                  >
                                    Add Minutes
                                  </Button>
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-full"
                                    onClick={saveExternalWorkEvidence}
                                    disabled={externalWorkActionLoading}
                                  >
                                    Save Evidence
                                  </Button>
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    className="w-full"
                                    onClick={handlePauseWork}
                                    disabled={externalWorkActionLoading}
                                  >
                                    Pause Session
                                  </Button>
                                </div>
                                <div className="flex items-end">
                                  <Button
                                    type="button"
                                    className="w-full"
                                    onClick={handleCompleteWork}
                                    disabled={externalWorkActionLoading}
                                  >
                                    Complete Session
                                  </Button>
                                </div>
                              </div>

                              <div className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <p className="text-sm font-semibold text-text">Switch to next work session</p>
                                    <p className="mt-1 text-sm text-muted">Change the active work item when needed.</p>
                                  </div>
                                  <Badge variant="muted">
                                    Used {switchCountToday}
                                  </Badge>
                                </div>
                                <div className="mt-4 grid gap-3 md:grid-cols-2">
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="switch-task-select">Choose existing work</FieldLabel>
                                    <Select
                                      id="switch-task-select"
                                      value={switchTaskId}
                                      onChange={(event) => setSwitchTaskId(event.target.value)}
                                    >
                                      <option value="">Type different work below</option>
                                      {openExternalTasks.map((entry) => (
                                        <option key={entry.id} value={entry.id}>
                                          {entry.title} ({entry.source_app || 'Outside work'})
                                        </option>
                                      ))}
                                    </Select>
                                  </div>
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="switch-planned-minutes">Planned minutes</FieldLabel>
                                    <Input
                                      id="switch-planned-minutes"
                                      type="number"
                                      min={1}
                                      value={switchPlannedMinutes}
                                      onChange={(event) => setSwitchPlannedMinutes(Number(event.target.value))}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="switch-title">Next work</FieldLabel>
                                    <Input
                                      id="switch-title"
                                      placeholder="What are you switching to?"
                                      value={switchTitle}
                                      onChange={(event) => setSwitchTitle(event.target.value)}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="switch-source">Where are you working?</FieldLabel>
                                    <Input
                                      id="switch-source"
                                      placeholder="VS Code, OpenEDG, CertMaster, GitHub"
                                      value={switchSource}
                                      onChange={(event) => setSwitchSource(event.target.value)}
                                    />
                                  </div>
                                </div>
                                <div className="mt-4">
                                  <Button
                                    type="button"
                                    onClick={switchExternalWorkBlock}
                                    disabled={externalWorkActionLoading}
                                  >
                                    Switch Session
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="rounded-xl border border-brand/20 bg-brand/5 px-4 py-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Starting from your Daily Plan</p>
                                <p className="mt-2 text-sm font-semibold text-text">{planWorkSession.title}</p>
                                <p className="mt-1 text-sm text-muted">
                                  {planWorkSession.sourceApp
                                    ? `Where: ${planWorkSession.sourceApp}`
                                    : `Focus: ${planWorkSession.focus}`}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center gap-3">
                                <Button
                                  type="button"
                                  onClick={handleStartWorkBlock}
                                  disabled={externalWorkActionLoading || loadingExternalWork}
                                >
                                  {externalWorkActionLoading ? 'Starting...' : 'Start Work Session'}
                                </Button>
                                <p className="text-sm text-muted">
                                  No extra title needed. This session tracks the plan you already made.
                                </p>
                              </div>
                              <details className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3">
                                <summary className="cursor-pointer text-sm font-semibold text-text">
                                  Choose existing work instead <span className="font-normal text-muted">(optional)</span>
                                </summary>
                                <div className="mt-4 grid gap-3 md:grid-cols-2">
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="start-work-task">Choose existing work</FieldLabel>
                                    <Select
                                      id="start-work-task"
                                      value={startWorkTaskId}
                                      onChange={(event) => setStartWorkTaskId(event.target.value)}
                                    >
                                      <option value="">Choose outside work</option>
                                      {openExternalTasks.map((entry) => (
                                        <option key={entry.id} value={entry.id}>
                                          {entry.title} ({entry.source_app || 'Outside work'})
                                        </option>
                                      ))}
                                    </Select>
                                  </div>
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="start-work-planned-minutes">Planned minutes</FieldLabel>
                                    <Input
                                      id="start-work-planned-minutes"
                                      type="number"
                                      min={1}
                                      value={startWorkPlannedMinutes}
                                      onChange={(event) => setStartWorkPlannedMinutes(Number(event.target.value))}
                                    />
                                  </div>
                                </div>
                                {openExternalTasks.length === 0 && (
                                  <p className="mt-3 text-sm text-muted">
                                    No outside work is listed yet. Use today&apos;s plan, or add outside work above first.
                                  </p>
                                )}
                                <Button
                                  type="button"
                                  className="mt-4"
                                  variant="secondary"
                                  onClick={startExternalWorkBlock}
                                  disabled={externalWorkActionLoading || loadingExternalWork || !startWorkTaskId}
                                >
                                  Start Selected Work
                                </Button>
                              </details>
                            </div>
                          )}
                          </SectionCard>
                        </div>

                        <details className="rounded-2xl border border-dashed border-border bg-surface/70 p-4">
                          <summary className="cursor-pointer text-sm font-semibold text-text">
                            Add Outside Work <span className="font-normal text-muted">(optional)</span>
                          </summary>
                          <p className="mt-2 text-sm text-muted">
                            Only use this if today&apos;s plan needs work from another app that is not already listed.
                          </p>
                          <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,0.7fr)_auto]">
                            <div className="space-y-2">
                              <FieldLabel htmlFor="external-task-title">What are you working on?</FieldLabel>
                              <Input
                                id="external-task-title"
                                placeholder="PCEP tuples practice, CompTIA lab, homepage project"
                                value={externalTitle}
                                onChange={(event) => setExternalTitle(event.target.value)}
                              />
                            </div>
                            <div className="space-y-2">
                              <FieldLabel htmlFor="external-task-source">Where are you working?</FieldLabel>
                              <Input
                                id="external-task-source"
                                placeholder="OpenEDG, CertMaster, VS Code, GitHub"
                                value={externalSource}
                                onChange={(event) => setExternalSource(event.target.value)}
                              />
                            </div>
                            <div className="flex items-end">
                              <Button
                                type="button"
                                className="w-full md:w-auto"
                                variant="secondary"
                                onClick={createExternalTask}
                                disabled={externalCreating || !externalTitle.trim()}
                              >
                                {externalCreating ? 'Adding...' : 'Add Outside Work'}
                              </Button>
                            </div>
                          </div>
                        </details>

                        {(goalCycle?.external_tasks || []).length > 0 && (
                        <SectionCard
                          title="Outside Work Added Today"
                          meta="Optional work from other apps. Your Daily Plan still drives the active work session."
                        >
                          {(goalCycle?.external_tasks || []).length === 0 ? (
                            <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
                              No outside work added yet.
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {(goalCycle?.external_tasks || []).map((entry) => (
                                <div key={entry.id} className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <p className="text-sm font-semibold text-text">{entry.title}</p>
                                        <Badge variant={externalTaskStatusBadgeVariant(entry.status)}>{entry.status.replace('_', ' ')}</Badge>
                                      </div>
                                      <p className="mt-2 text-sm text-muted">{entry.source_app || 'Outside work'}</p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => updateExternalStatus(entry.id, 'in_progress')}
                                      >
                                        In Progress
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => startExternalWorkFromTask(entry.id, entry.title, entry.source_app || '')}
                                        disabled={externalWorkActionLoading || Boolean(activeWorkBlock)}
                                      >
                                        Start Session
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={() => updateExternalStatus(entry.id, 'completed')}
                                      >
                                        Complete
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </SectionCard>
                        )}
                        </>}
                        </div>

                      <div className="space-y-4">
                        {goalCycle?.status === 'checked_in' && (
                          <div id="student-wrap-up" tabIndex={-1} aria-label="Daily wrap-up" className="scroll-mt-40">
                          <SectionCard
                            title={seniorPriorityWorkflow ? 'Step 3: Required Daily Wrap Up' : 'Daily Check-Out'}
                            meta={seniorPriorityWorkflow ? 'Complete this before leaving class every day. Record the result and show evidence of your progress.' : 'Reflect on your Target and Success Check from this morning.'}
                            className={seniorPriorityWorkflow ? 'border-warning/40 shadow-card ring-1 ring-warning/15' : undefined}
                            actions={seniorPriorityWorkflow ? <Badge variant="warning">Required today</Badge> : undefined}
                          >
                            <div className="space-y-4">
                              {!seniorPriorityWorkflow && <div className="rounded-xl border border-brand/15 bg-brand/5 px-4 py-3 text-sm text-text">
                                <div className="font-semibold text-sm mb-2">Today's Plan:</div>
                                <div><span className="font-semibold">{seniorPriorityWorkflow ? "Today's Priority:" : 'Target:'}</span> {currentDailyPlan.target || currentCompletionGoal?.smart_text || '(not set)'}</div>
                                <div className="mt-1"><span className="font-semibold">{seniorPriorityWorkflow ? 'End-of-class outcome:' : 'Success Check:'}</span> {currentDailyPlan.successCriteria || '(not set)'}</div>
                              </div>}
                              {seniorPriorityWorkflow && (
                                <div className="space-y-3">
                                  <p className="text-sm font-medium text-text">Main Goal Result</p>
                                  <div className="grid gap-2 sm:grid-cols-2">
                                    {([
                                      ['met', 'Met'],
                                      ['partially_met', 'Partially Met'],
                                      ['blocked', 'Blocked'],
                                      ['changed_authentic_work', 'Changed Because of Authentic Work'],
                                      ['ready_for_review', 'Ready for Review'],
                                    ] as const).map(([value, label]) => (
                                      <Button
                                        key={value}
                                        type="button"
                                        variant={priorityResult === value ? 'primary' : 'secondary'}
                                        onClick={() => setPriorityResult(value)}
                                      >
                                        {label}
                                      </Button>
                                    ))}
                                  </div>
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="pathway-evidence-text">Evidence of Today&apos;s Progress</FieldLabel>
                                    <p className="text-xs text-muted">In 2–4 sentences, explain what you completed or changed and where your teacher can see the proof. If you were blocked, explain what stopped you and what you tried.</p>
                                    <Textarea className="min-h-36" id="pathway-evidence-text" placeholder="Example: I completed the motherboard and memory lesson and correctly identified the components in the desktop lab. My completed CertMaster lesson and lab check show the work." value={pathwayEvidenceText} onChange={(event) => setPathwayEvidenceText(event.target.value)} />
                                  </div>
                                  <div className="space-y-2 rounded-xl border border-border bg-surface-alt/50 p-3">
                                    <FieldLabel htmlFor="pathway-evidence-url">Link to Your Evidence (optional)</FieldLabel>
                                    <p className="text-xs text-muted">Paste a link your teacher can open—for example, a Google Doc or Drive file, GitHub commit, Canva design, portfolio page, or submitted assignment. If the proof is already visible in your course platform, describe it above and leave this blank.</p>
                                    <Input id="pathway-evidence-url" type="url" placeholder="https://docs.google.com/... or another shareable link" value={pathwayEvidenceUrl} onChange={(event) => setPathwayEvidenceUrl(event.target.value)} />
                                    {goalCycle?.primary_goal && <WorkEvidencePanel goalId={Number(goalCycle.primary_goal)} sourceKey={`daily:${goalCycle.id}:pathway`} />}
                                  </div>
                                </div>
                              )}
                              {seniorPriorityWorkflow && personalProjectGoal && (
                                <div className="space-y-3 rounded-xl border border-border bg-surface-alt/50 p-4">
                                  <p className="text-sm font-semibold text-text">Project Result</p>
                                  <p className="text-xs text-muted">Project plan: {goalCycle?.project_plan_target || '(not set)'}</p>
                                  <div className="grid gap-2 sm:grid-cols-2">
                                    {([
                                      ['met', 'Met'], ['partially_met', 'Partially Met'], ['blocked', 'Blocked / No Progress'],
                                      ['changed_authentic_work', 'Changed Because of Authentic Work'], ['ready_for_review', 'Ready for Review'],
                                    ] as const).map(([value, label]) => (
                                      <Button key={value} type="button" variant={projectResult === value ? 'primary' : 'secondary'} onClick={() => setProjectResult(value)}>{label}</Button>
                                    ))}
                                  </div>
                                  <FieldLabel htmlFor="project-evidence-text">Project evidence or blocked explanation</FieldLabel>
                                  <Textarea id="project-evidence-text" placeholder="Describe today’s project progress, commit, design, photo, draft, or blocker." value={projectEvidenceText} onChange={(event) => setProjectEvidenceText(event.target.value)} />
                                  <FieldLabel htmlFor="project-evidence-url">Project evidence link (optional)</FieldLabel>
                                  <Input id="project-evidence-url" type="url" placeholder="https://..." value={projectEvidenceUrl} onChange={(event) => setProjectEvidenceUrl(event.target.value)} />
                                  {goalCycle && personalProjectGoal && <WorkEvidencePanel goalId={personalProjectGoal.id} sourceKey={`daily:${goalCycle.id}:project`} title="Project screenshots" />}
                                  <label className="flex items-center gap-2 text-sm text-text"><input type="checkbox" checked={projectPortfolioCandidate} onChange={(event) => setProjectPortfolioCandidate(event.target.checked)} /> Portfolio candidate</label>
                                </div>
                              )}
                              {!seniorPriorityWorkflow && goalCycle?.primary_goal && dailyGoalAnchor && (
                                <div className="space-y-2">
                                  <p className="text-sm font-medium text-text">Did today help you move toward your long-term goal?</p>
                                  <div className="grid grid-cols-2 gap-2">
                                    {([
                                      ['yes', 'Yes'],
                                      ['no', 'No'],
                                    ] as const).map(([value, label]) => (
                                      <Button
                                        key={value}
                                        type="button"
                                        variant={movedTowardPrimaryGoal === value ? 'primary' : 'secondary'}
                                        onClick={() => setMovedTowardPrimaryGoal(value)}
                                      >
                                        {label}
                                      </Button>
                                    ))}
                                  </div>
                                  <p className="text-xs text-muted">Goal: {dailyGoalAnchor.title}</p>
                                </div>
                              )}
                              {!seniorPriorityWorkflow && <div className="space-y-2">
                                <FieldLabel htmlFor="checkout-reflection">Why?</FieldLabel>
                                <Textarea
                                  id="checkout-reflection"
                                  placeholder="One short reason. How did it go with your Target and Success Check?"
                                  value={checkoutReflection}
                                  onChange={(event) => setCheckoutReflection(event.target.value)}
                                />
                              </div>}
                              <div className="space-y-2">
                                <FieldLabel htmlFor="checkout-next-step">What will you do next? (optional)</FieldLabel>
                                <Input
                                  id="checkout-next-step"
                                  placeholder="Finish question 2 tomorrow, ask for review, or submit the artifact"
                                  value={checkoutNextStep}
                                  onChange={(event) => setCheckoutNextStep(event.target.value)}
                                />
                              </div>
                              <div className="space-y-2 rounded-xl border border-border bg-surface-alt/50 p-4">
                                <FieldLabel htmlFor="plan-change-reason">What happened with your plan? (optional this cycle)</FieldLabel>
                                <Select id="plan-change-reason" value={planChangeReason} onChange={(event) => setPlanChangeReason(event.target.value)}>
                                  <option value="">Choose a reflection</option><option value="as_planned">Worked as planned</option><option value="continued_goal">Continued goal work</option><option value="nerd_herd">Nerd Herd responsibility</option><option value="live_lesson">Teacher live lesson</option><option value="blocked">I was blocked</option><option value="personal_project">Intentional personal project</option><option value="lost_focus">I lost focus</option><option value="other">Other</option>
                                </Select>
                                {(planChangeReason === 'other' || planChangeReason === 'blocked' || planChangeReason === 'lost_focus') && <Input id="plan-change-note" placeholder="Briefly explain what changed" value={planChangeNote} onChange={(event) => setPlanChangeNote(event.target.value)} />}
                              </div>
                              {!seniorPriorityWorkflow && <details className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                <summary className="cursor-pointer text-sm font-semibold text-text">Optional ratings</summary>
                                <div className="mt-4 grid gap-3 md:grid-cols-2">
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="completion-rating">Completion rating (1-5)</FieldLabel>
                                    <Input
                                      id="completion-rating"
                                      type="number"
                                      min={1}
                                      max={5}
                                      value={completionRating}
                                      onChange={(event) => setCompletionRating(Number(event.target.value))}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <FieldLabel htmlFor="confidence-rating">Confidence rating (1-5)</FieldLabel>
                                    <Input
                                      id="confidence-rating"
                                      type="number"
                                      min={1}
                                      max={5}
                                      value={confidenceRating}
                                      onChange={(event) => setConfidenceRating(Number(event.target.value))}
                                    />
                                  </div>
                                </div>
                              </details>}
                              <Button
                                type="button"
                                onClick={submitCheckOut}
                                disabled={submittingCheckOut}
                              >
                                {submittingCheckOut ? 'Submitting...' : seniorPriorityWorkflow ? 'Finish Wrap Up' : 'Submit Check-Out'}
                              </Button>
                            </div>
                          </SectionCard>
                          </div>
                        )}

                        {!seniorPriorityWorkflow && <details className="rounded-2xl border border-border bg-surface-alt/40 p-4">
                          <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-semibold text-text">
                            <span>Recent work events</span>
                            <Badge variant={loadingExternalWork ? 'warning' : 'success'}>{loadingExternalWork ? 'Syncing' : 'Ready'}</Badge>
                          </summary>
                          <p className="mt-2 text-sm text-muted">
                            Session starts, stops, switches, and evidence updates.
                          </p>
                          <div className="mt-4">
                            {externalWorkEvents.length === 0 ? (
                              <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
                                No work events yet.
                              </div>
                            ) : (
                              <div className="space-y-3 max-h-80 overflow-auto pr-1">
                                {externalWorkEvents.map((event) => (
                                  <div key={event.id} className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                      <div>
                                        <p className="text-sm font-semibold text-text">{externalEventLabel(event)}</p>
                                        <p className="mt-1 text-sm text-muted">{formatDateTime(event.created_at)}</p>
                                      </div>
                                      <Badge variant="muted">{String(event.event_type || '').replace(/_/g, ' ')}</Badge>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </details>}

                        <details className="rounded-2xl border border-border bg-surface-alt/40 p-4">
                          <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-semibold text-text">
                            <span>{seniorPriorityWorkflow ? 'Progress History' : 'Plan History'}</span>
                            {activeSection ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={(event) => {
                                  event.preventDefault();
                                  loadGoalHistory(activeSection);
                                }}
                                disabled={loadingGoalHistory}
                              >
                                {loadingGoalHistory ? 'Refreshing...' : 'Refresh'}
                              </Button>
                            ) : null}
                          </summary>
                          <p className="mt-2 text-sm text-muted">
                            {seniorPriorityWorkflow
                              ? 'Review recent priorities and outcomes to choose the next useful direction.'
                              : 'Use recent daily plans to calibrate tomorrow\'s work.'}
                          </p>
                          <div className="mt-4">
                          {loadingGoalHistory ? (
                            <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
                              Loading history...
                            </div>
                          ) : goalHistory.length === 0 ? (
                            <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
                              {seniorPriorityWorkflow ? 'No progress history yet.' : 'No plan history yet.'}
                            </div>
                          ) : (
                            <div className="space-y-3 max-h-80 overflow-auto pr-1">
                              {goalHistory.map((cycle) => {
                                const historyPlan = structuredPlanFromCycle(cycle);
                                const learningGoal = (cycle.goals || []).find((goal) => goal.goal_type === 'learning');
                                const completionGoal = (cycle.goals || []).find((goal) => goal.goal_type === 'completion');
                                return (
                                  <div key={cycle.id} className="rounded-xl border border-border bg-surface-alt/50 p-4">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="text-sm font-semibold text-text">{formatDate(cycle.local_school_date)}</p>
                                      <Badge variant={cycleStatusBadgeVariant(cycle.status)}>
                                        {String(cycle.status || 'not_checked_in').replace('_', ' ')}
                                      </Badge>
                                    </div>
                                    <div className="mt-3 grid gap-1 text-sm text-text">
                                      {historyPlan.hasStructuredPlan && historyPlan.track && (
                                        <span><span className="font-semibold">Track:</span> {trackLabel(historyPlan.track)}</span>
                                      )}
                                      <span><span className="font-semibold">{seniorPriorityWorkflow ? 'Priority:' : 'Target:'}</span> {historyPlan.target || learningGoal?.smart_text || '(not set)'}</span>
                                      {seniorPriorityWorkflow ? (
                                        <>
                                          <span><span className="font-semibold">End-of-class outcome:</span> {historyPlan.successCriteria || '(not set)'}</span>
                                          {cycle.priority_result && (
                                            <span><span className="font-semibold">Result:</span> {String(cycle.priority_result).replace(/_/g, ' ')}</span>
                                          )}
                                        </>
                                      ) : historyPlan.hasStructuredPlan ? (
                                        <>
                                          <span><span className="font-semibold">First step:</span> {historyPlan.firstStep || '(not set)'}</span>
                                          <span><span className="font-semibold">Success Check:</span> {historyPlan.successCriteria || '(not set)'}</span>
                                        </>
                                      ) : (
                                        <span><span className="font-semibold">Completion:</span> {completionGoal?.smart_text || '(not set)'}</span>
                                      )}
                                      {cycle.reflection_status && (
                                        <span><span className="font-semibold">Outcome:</span> {String(cycle.reflection_status).replace(/_/g, ' ')}</span>
                                      )}
                                    </div>
                                    {cycle.reflection_text && (
                                      <p className="mt-1 text-sm text-text">
                                        <span className="font-semibold">Reflection:</span> {cycle.reflection_text}
                                      </p>
                                    )}
                                    {cycle.status === 'checked_out' && (
                                      <p className="mt-2 text-xs text-muted">
                                        Completion {cycle.completion_rating || '-'} / 5 | Confidence {cycle.confidence_rating || '-'} / 5
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          </div>
                        </details>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </SectionCard>
        ) : (
          <div className="mt-6 bg-white rounded-lg border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{seniorPriorityWorkflow ? "Today's Work" : 'Daily Plan'}</p>
                <h2 className="text-lg font-semibold text-gray-900">
                  {seniorPriorityWorkflow ? 'Choose a direction, then begin' : 'Track → Target → First Step → Success Check'}
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {goalCycle?.teacher_bypass_active && (
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    Teacher bypass active
                  </span>
                )}
              </div>
            </div>

            {(goalsError || gateFromNavigation?.gate_required) && (
              <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                <p>{goalsError || 'Daily plan check-in is required before continuing.'}</p>
              </div>
            )}

            {loadingGoals ? (
              <div className="mt-3 text-sm text-gray-500">Loading daily plan...</div>
            ) : (
              <>
                {dailyGoalAnchor && (
                  <div className="mt-3 rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-gray-800">
                    <span className="font-semibold">{seniorPriorityWorkflow ? 'Longer-term direction:' : 'Long-term goal:'}</span> {dailyGoalAnchor.title}
                    {' '}
                    {dailyPlanConnectsToPrimaryGoal
                      ? "Today's plan can be your next step toward this."
                      : 'Today is not linked to this goal yet.'}
                  </div>
                )}
                {gateRequired && (
                  <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Daily plan check-in is required before starting assignments or live lesson work.
                  </div>
                )}
                {dailyPriorityMissing && (
                  <div className="mt-3 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                    Add today&apos;s priority and what you will be able to show by the end of class. Lessons and assignments remain available.
                  </div>
                )}
                {showNonSchoolFocusView ? (
                  <div className="mt-4 rounded border border-sky-200 bg-sky-50 p-4">
                    <div className="text-sm font-semibold text-gray-900">{seniorPriorityWorkflow ? 'No morning plan required today.' : 'No daily goals required today.'}</div>
                    <div className="mt-1 text-sm text-gray-600">
                      {seniorPriorityWorkflow
                        ? 'Start with useful work, or add a short morning plan if it will help you focus.'
                        : 'Use this lighter view for makeup work and practice. If a written plan will help, set optional daily goals.'}
                    </div>
                    {primaryGoalFeatureEnabled && primaryGoal && (
                      <div className="mt-3 rounded border border-indigo-100 bg-white px-3 py-2 text-sm text-gray-800">
                        <span className="font-semibold">{seniorPriorityWorkflow ? 'Longer-term direction:' : 'Long-term goal:'}</span> {primaryGoal.title}
                      </div>
                    )}
                    {missingWorkCount > 0 && (
                      <div className="mt-3 rounded border border-amber-100 bg-white px-3 py-2 text-sm text-gray-800">
                        <span className="font-semibold">Makeup work:</span> {missingWorkCount} item{missingWorkCount === 1 ? '' : 's'} need attention.
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setActiveTab('work')}
                      >
                        View assignments &amp; lessons
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setDailyGoalOptional(true)}
                      >
                        {seniorPriorityWorkflow ? 'Set Optional Morning Plan' : 'Set Optional Daily Goals'}
                      </Button>
                    </div>
                  </div>
                ) : showDailyCheckInForm && (
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    {seniorPriorityWorkflow ? (
                      <div className="rounded border border-indigo-200 bg-indigo-50 p-4 space-y-4">
                        {teacherDailySupportNotice}
                        <div>
                          <div className="text-lg font-semibold text-gray-900">Your Main Focus Today</div>
                          <div className="text-sm text-gray-600">Choose the most useful step toward your Main Goal. Personal-project work is optional additional work.</div>
                        </div>
                        <label className="block text-sm font-medium text-gray-800">
                          Today's Main Task
                          <span className="mt-1 block text-xs font-normal text-gray-600">What is the most useful step you can take toward your Main Goal today?</span>
                          <Input
                            className="mt-2 bg-white"
                            placeholder="Module, assignment, certification, or another useful step"
                            value={planTarget}
                            onChange={(event) => setPlanTarget(event.target.value)}
                          />
                        </label>
                        <label className="block text-sm font-medium text-gray-800">
                          How Will You Get Started?
                          <span className="mt-1 block text-xs font-normal text-gray-600">
                            {goalCycle?.latest_support && firstStepText.trim()
                              ? 'Your teacher prepared this starting action. Ask for clarification or adjust it if you need a different way to begin.'
                              : 'Choose one small action you can do right away.'}
                          </span>
                          <Input
                            className="mt-2 bg-white"
                            placeholder="For example: Open the lesson and read the first question."
                            value={firstStepText}
                            onChange={(event) => setFirstStepText(event.target.value)}
                          />
                        </label>
                        <label className="block text-sm font-medium text-gray-800">
                          What Will You Show?
                          <span className="mt-1 block text-xs font-normal text-gray-600">What progress will you be able to show by the end of class?</span>
                          <Input
                            className="mt-2 bg-white"
                            placeholder="Completed work, screenshot, score, artifact, or teacher check"
                            value={planSuccessCriteria}
                            onChange={(event) => setPlanSuccessCriteria(event.target.value)}
                          />
                        </label>
                        {personalProjectGoal && (
                          <label className="block text-sm font-medium text-gray-800">
                            Project Task
                            <span className="mt-1 block text-xs font-normal text-gray-600">Required daily while a personal or senior project goal is active.</span>
                            <Input className="mt-2 bg-white" placeholder="Personal or senior project work for today" value={personalProjectTask} onChange={(event) => setPersonalProjectTask(event.target.value)} />
                            <span className="mt-2 block text-xs font-normal text-gray-600">Expected Project Evidence</span>
                            <Input className="mt-1 bg-white" placeholder="What project progress will you show by the end of class?" value={personalProjectProof} onChange={(event) => setPersonalProjectProof(event.target.value)} />
                          </label>
                        )}
                        <div className="rounded border border-indigo-100 bg-white px-3 py-2 text-sm text-gray-800">
                          <span className="font-semibold">Main Focus:</span> Main Goal
                          {personalProjectTask.trim() && <span className="mt-1 block text-gray-600">Also planned: {personalProjectTask.trim()}</span>}
                        </div>
                        <Button type="button" onClick={submitCheckIn} disabled={submittingCheckIn}>
                          {submittingCheckIn ? 'Starting...' : 'Start Work'}
                        </Button>
                      </div>
                    ) : (<>
                    {teacherDailySupportNotice}
                    <div className="rounded border border-indigo-100 bg-indigo-50/50 p-3 space-y-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {!isSchoolDay ? 'Create Optional Daily Goals' : 'Create Your Daily Plan'}
                        </div>
                        <div className="text-xs text-gray-600">
                          {!isSchoolDay
                            ? 'Set a small plan only if it will help you focus today.'
                            : dailyGoalAnchor
                            ? 'What is your next step toward your goal today?'
                            : 'Set your Track, Target, First Step, and Success Check for today.'}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Select
                          value={planTrack}
                          onChange={(event) => {
                            const nextTrack = event.target.value as PlanTrack;
                            setPlanTrack(nextTrack);
                            if (nextTrack === 'main_project' || nextTrack === 'secondary_project') {
                              setCompletionProjectRole(nextTrack);
                            }
                            clearAiRefinement();
                          }}
                        >
                          {PLAN_TRACK_OPTIONS.map((option) => (
                            <option key={option.value || 'choose'} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                        {(sectionSubgroup || suggestedPlanTrack) && (
                          <div className="rounded border border-indigo-100 bg-white px-3 py-2 text-xs text-gray-700">
                            Pathway: {sectionSubgroup || trackLabel(suggestedPlanTrack)}
                            {suggestedPlanTrack && !planTrack ? ` | Suggested track: ${trackLabel(suggestedPlanTrack)}` : ''}
                          </div>
                        )}
                        <Input
                          placeholder={dailyGoalAnchor
                            ? 'Target: What step will move your goal forward today?'
                            : 'Target: What will you accomplish today?'}
                          value={planTarget}
                          onChange={(event) => {
                            setPlanTarget(event.target.value);
                            clearAiRefinement();
                          }}
                        />
                        <Input
                          placeholder="First step: How will you start?"
                          value={firstStepText}
                          onChange={(event) => {
                            setFirstStepText(event.target.value);
                            clearAiRefinement();
                          }}
                        />
                        <Input
                          placeholder="Success Check: How will you know you're done?"
                          value={planSuccessCriteria}
                          onChange={(event) => {
                            setPlanSuccessCriteria(event.target.value);
                            clearAiRefinement();
                          }}
                        />
                      </div>
                      <div className="rounded border border-indigo-100 bg-white p-2 text-xs text-gray-700">
                        <span className="font-semibold">Your Plan:</span>{' '}
                        {dailyPlan.track && dailyPlan.target
                          ? `${trackLabel(dailyPlan.track)} → ${dailyPlan.target}`
                          : 'Choose a track and target.'}
                        {dailyPlan.first_step ? ` → ${dailyPlan.first_step}` : ''}
                        {dailyPlan.success_criteria ? ` → ${dailyPlan.success_criteria}` : ''}
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={runAiAssist}
                        disabled={aiAssistLoading || !dailyPlan.target.trim()}
                      >
                        {aiAssistLoading ? 'Refining...' : 'Get AI Suggestions'}
                      </Button>
                    </div>
                    {aiSuggestion && (
                      <div className="rounded border border-indigo-200 bg-indigo-50 p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs uppercase tracking-wide text-indigo-700">AI Suggestion</div>
                          <div className="text-[11px] text-indigo-700">
                            Clarity score: {Number(aiSuggestion.clarity_score || 0)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-900">{aiSuggestion.refined_goal}</div>
                        {aiSuggestion.refined_target && (
                          <div className="text-xs text-indigo-900">
                            Target: {aiSuggestion.refined_target}
                          </div>
                        )}
                        {aiSuggestion.measurable_criterion && (
                          <div className="text-xs text-indigo-900">
                            Success Check: {aiSuggestion.suggested_success_criteria || aiSuggestion.measurable_criterion}
                          </div>
                        )}
                        {aiSuggestion.coaching_question && (
                          <div className="text-xs text-indigo-900">
                            Coaching prompt: {aiSuggestion.coaching_question}
                          </div>
                        )}
                        {aiSuggestion.suggested_first_step && (
                          <div className="text-xs text-indigo-900">
                            Suggested first step: {aiSuggestion.suggested_first_step}
                          </div>
                        )}
                        {aiSuggestion.smart_rubric && (
                          <div className="rounded border border-indigo-200 bg-white p-2 space-y-1">
                            <div className="text-xs font-semibold text-indigo-800">
                              Clarity rubric score: {Number(aiSuggestion.smart_rubric.overall_score || 0)} / 100
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {(['S', 'M', 'A', 'R', 'T'] as const).map((dimension) => {
                                const row = aiSuggestion.smart_rubric?.dimensions?.[dimension];
                                if (!row) return null;
                                return (
                                  <Badge
                                    key={dimension}
                                    variant={row.meets ? 'success' : 'warning'}
                                    className="text-[11px]"
                                  >
                                    {dimension}: {row.score}
                                  </Badge>
                                );
                              })}
                            </div>
                            {(aiSuggestion.smart_rubric.deficiency_labels || []).length > 0 && (
                              <div className="text-[11px] text-amber-800">
                                Improve: {(aiSuggestion.smart_rubric.deficiency_labels || []).map(smartDeficiencyLabel).join(' | ')}
                              </div>
                            )}
                          </div>
                        )}
                        {aiSuggestion.priority_target?.available && (
                          <div className="rounded border border-indigo-200 bg-white p-2 text-xs text-indigo-900">
                            Priority target: {aiSuggestion.priority_target.title || '(untitled)'}
                            {aiSuggestion.priority_target.source_app ? ` (${aiSuggestion.priority_target.source_app})` : ''}
                            {' '}| {aiSuggestion.priority_target.kind === 'internal_assignment' ? 'internal' : 'external'}
                            {aiSuggestion.priority_target.overdue ? ' | overdue' : ''}
                            {!aiSuggestion.priority_target.overdue && aiSuggestion.priority_target.due_today ? ' | due today' : ''}
                            {aiSuggestion.priority_target.in_grace ? ' | grace window' : ''}
                            {aiSuggestion.priority_target.priority_reason ? ` | ${aiSuggestion.priority_target.priority_reason}` : ''}
                          </div>
                        )}
                        {aiSuggestion.guardrails && (
                          <div className="rounded border border-indigo-200 bg-white p-2 text-[11px] text-indigo-900">
                            Guardrails: coaching only, student owns work, no auto-complete.
                          </div>
                        )}
                        <Textarea
                          className="bg-white"
                          placeholder="Why are you accepting or dismissing this suggestion?"
                          value={aiRationale}
                          onChange={(event) => setAiRationale(event.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            onClick={applyAiSuggestion}
                            disabled={aiAssistLoading}
                          >
                            Apply Suggestion
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={dismissAiSuggestion}
                            disabled={aiAssistLoading}
                          >
                            Dismiss Suggestion
                          </Button>
                        </div>
                      </div>
                    )}
                    {aiApplied && !aiSuggestion && (
                      <div className="text-xs text-emerald-700">
                        AI refinement applied. Rationale will be saved with this check-in.
                      </div>
                    )}
                    <div className="rounded border border-slate-200 bg-white p-3 space-y-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Optional work item</div>
                        <div className="text-xs text-gray-600">Select a listed item only if it matches today&apos;s target.</div>
                      </div>
                      {availableCompletionWorkItems.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {availableCompletionWorkItems.map((item) => {
                            const selected = selectedCompletionWorkKey === item.key;
                            return (
                              <button
                                key={item.key}
                                type="button"
                                className={`border rounded p-2 text-left text-sm ${
                                  selected
                                    ? 'border-indigo-400 bg-indigo-50'
                                    : 'border-slate-200 bg-slate-50'
                                }`}
                                onClick={() => {
                                  setSelectedCompletionWorkKey(selected ? '' : item.key);
                                  if (!selected) {
                                    setPlanTarget((current) => current.trim() || item.title);
                                  }
                                  clearAiRefinement();
                                }}
                              >
                                <span className="block font-medium text-gray-900">{item.title}</span>
                                <span className="block text-xs text-gray-500">{item.source} | {item.detail}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <Select
                        value={completionProjectRole}
                        onChange={(event) => setCompletionProjectRole(event.target.value as ProjectRole)}
                      >
                        {PROJECT_ROLE_OPTIONS.map((option) => (
                          <option key={option.value || 'none'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      <div className="text-xs text-gray-500">
                        Mark main or secondary project only when today&apos;s completion target is project work.
                      </div>
                      <div className="text-xs text-gray-700">
                        <span className="font-semibold">Linked work:</span> {selectedCompletionWorkItem?.title || 'No linked work item. Your custom target will still be saved.'}
                      </div>
                    </div>
                    <details className="rounded border border-slate-200 bg-slate-50 p-3">
                      <summary className="cursor-pointer text-sm font-medium text-gray-800">Advanced details (optional)</summary>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Select
                          value={learningStageTag}
                          onChange={(event) => setLearningStageTag(event.target.value)}
                        >
                          <option value="">Optional stage tag</option>
                          <option value="why">WHY</option>
                          <option value="define">DEFINE</option>
                          <option value="do">DO</option>
                          <option value="explain">EXPLAIN</option>
                          <option value="apply">APPLY</option>
                          <option value="reflect">REFLECT</option>
                        </Select>
                        <Input
                          placeholder="Learning tags (comma)"
                          value={learningTags}
                          onChange={(event) => setLearningTags(event.target.value)}
                        />
                        <Input
                          placeholder="Completion tags (comma)"
                          value={completionTags}
                          onChange={(event) => setCompletionTags(event.target.value)}
                        />
                      </div>
                    </details>
                    <Button
                      type="button"
                      onClick={submitCheckIn}
                      disabled={submittingCheckIn}
                    >
                      {submittingCheckIn
                        ? 'Submitting...'
                        : !isSchoolDay
                          ? 'Submit Optional Check-In'
                          : 'Submit Morning Check-In'}
                    </Button>
                    </>)}
                  </div>
                )}

                {/* Start Work Transition Card */}
                {!seniorPriorityWorkflow && showStartWorkTransition && goalCycle?.status === 'checked_in' && (
                  <div className="mt-6">
                    <SectionCard
                      title="Ready to Start Work?"
                      meta="Here's your plan for today. Take a moment to review, then begin."
                      className="border-brand/20 bg-brand/5"
                    >
                      <div className="space-y-4">
                        {/* Plan Summary */}
                        <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Work Type</p>
                              <p className="text-sm font-medium text-text capitalize">
                                {getWorkType(currentDailyPlan.track)?.replace(/_/g, ' ') || 'Daily Work'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Focus</p>
                              <p className="text-sm font-medium text-text">
                                {getFocusLabel(currentDailyPlan.track, currentDailyPlan.target)}
                              </p>
                            </div>
                          </div>

                          <div className="border-t pt-3 space-y-2">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Target</p>
                              <p className="text-sm text-text">{currentDailyPlan.target || '(not set)'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">First Step</p>
                              <p className="text-sm text-text">{currentDailyPlan.firstStep || '(not set)'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Success Check</p>
                              <p className="text-sm text-text">{currentDailyPlan.successCriteria || '(not set)'}</p>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                          <Button
                            type="button"
                            onClick={handleStartWork}
                            className="bg-brand text-brand-foreground hover:bg-brand/90"
                            disabled={externalWorkActionLoading || loadingExternalWork}
                          >
                            {externalWorkActionLoading ? 'Starting...' : 'Start Work'}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={handleEditPlan}
                          >
                            Edit Plan
                          </Button>
                        </div>

                        <div className="text-xs text-muted">
                          Starting work will begin tracking your time and help you stay focused on today's target.
                        </div>
                      </div>
                    </SectionCard>
                  </div>
                )}

                {goalCycle?.status !== 'not_checked_in' && (
                  <div className="mt-4 space-y-3">
                    <div className="text-sm text-gray-700">
                      Plan Status: <span className="font-semibold">{goalCycle?.status?.replace('_', ' ')}</span>
                    </div>
                    <div className="text-sm text-gray-700">Missing Work: {missingWorkDetail}</div>
                    <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-gray-700">
                      {currentDailyPlan.hasStructuredPlan ? (
                        <div className="grid gap-1">
                          <span><span className="font-semibold">Track:</span> {trackLabel(currentDailyPlan.track)}</span>
                          <span><span className="font-semibold">Target:</span> {currentDailyPlan.target || '(not set)'}</span>
                          <span><span className="font-semibold">First step:</span> {currentDailyPlan.firstStep || '(not set)'}</span>
                          <span><span className="font-semibold">Success Check:</span> {currentDailyPlan.successCriteria || '(not set)'}</span>
                        </div>
                      ) : (
                        <div>
                          <div><span className="font-semibold">Learning:</span> {currentDailyPlan.legacyLearningText || '(not set)'}</div>
                          <div><span className="font-semibold">Completion:</span> {currentDailyPlan.legacyCompletionText || '(not set)'}</div>
                        </div>
                      )}
                    </div>

                    {/* Start Work Transition Card - Non-Redesign UI */}
                    {!seniorPriorityWorkflow && showStartWorkTransition && goalCycle?.status === 'checked_in' && (
                      <div className="mt-4 rounded border border-indigo-200 bg-indigo-50 p-4 space-y-3">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Ready to Start Work?</h3>
                          <p className="text-xs text-gray-600">Here's your plan for today. Take a moment to review, then begin.</p>
                        </div>

                        {/* Plan Summary */}
                        <div className="bg-white rounded border border-gray-200 p-3 space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase">Work Type</p>
                              <p className="text-sm text-gray-900 capitalize">
                                {getWorkType(currentDailyPlan.track)?.replace(/_/g, ' ') || 'Daily Work'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase">Focus</p>
                              <p className="text-sm text-gray-900">
                                {getFocusLabel(currentDailyPlan.track, currentDailyPlan.target)}
                              </p>
                            </div>
                          </div>

                          <div className="border-t pt-2 space-y-1">
                            <div className="text-sm">
                              <span className="font-semibold">Target:</span> {currentDailyPlan.target || '(not set)'}
                            </div>
                            <div className="text-sm">
                              <span className="font-semibold">First step:</span> {currentDailyPlan.firstStep || '(not set)'}
                            </div>
                            <div className="text-sm">
                              <span className="font-semibold">Success Check:</span> {currentDailyPlan.successCriteria || '(not set)'}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleStartWork}
                            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            disabled={externalWorkActionLoading || loadingExternalWork}
                          >
                            {externalWorkActionLoading ? 'Starting...' : 'Start Work'}
                          </button>
                          <button
                            type="button"
                            onClick={handleEditPlan}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                          >
                            Edit Plan
                          </button>
                        </div>

                        <div className="text-xs text-gray-500">
                          Starting work will begin tracking your time and help you stay focused on today's target.
                        </div>
                      </div>
                    )}

                    {!seniorPriorityWorkflow && <><details className="rounded border border-dashed border-slate-300 bg-white p-3">
                      <summary className="cursor-pointer text-sm font-semibold text-gray-900">
                        Add Outside Work <span className="font-normal text-gray-500">(optional)</span>
                      </summary>
                      <p className="mt-2 text-xs text-gray-600">
                        Only use this if today&apos;s plan needs work from another app that is not already listed.
                      </p>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          className="border rounded px-3 py-2"
                          placeholder="What are you working on?"
                          value={externalTitle}
                          onChange={(event) => setExternalTitle(event.target.value)}
                        />
                        <input
                          className="border rounded px-3 py-2"
                          placeholder="Where are you working?"
                          value={externalSource}
                          onChange={(event) => setExternalSource(event.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="mt-3 px-3 py-2 border border-slate-300 text-slate-700 rounded text-sm"
                        onClick={createExternalTask}
                        disabled={externalCreating || !externalTitle.trim()}
                      >
                        {externalCreating ? 'Adding...' : 'Add Outside Work'}
                      </button>
                    </details>
                    {workStatus !== 'not_started' && (
                      <div className="rounded border border-indigo-200 bg-indigo-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Working now</div>
                            <div className="mt-1 text-sm font-semibold text-gray-900">
                              Focus: {planWorkSession.focus || 'Daily work'}
                            </div>
                            <div className="text-sm text-gray-700">
                              Target: {planWorkSession.title}
                            </div>
                          </div>
                          <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-indigo-700">
                            Status: {workStatus === 'completed' ? 'Completed' :
                              workStatus === 'paused' ? 'Paused' :
                              workStatus === 'in_progress' ? 'In Progress' : 'Not Started'}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="mt-2 rounded border border-indigo-200 bg-indigo-50/50 p-3 space-y-3">
                      <div className="flex flex-wrap items-center gap-2 justify-between">
                        <div>
                          <div className="text-sm font-semibold text-indigo-900">Work Time Today</div>
                          <div className="text-xs text-indigo-700">
                            Switches used today: {switchCountToday}
                          </div>
                        </div>
                        <div className="text-xs text-indigo-700">
                          {loadingExternalWork ? 'Syncing timeline...' : 'Timeline ready'}
                        </div>
                      </div>

                      {activeWorkBlock ? (
                        <div className="space-y-3 rounded border border-indigo-200 bg-white p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <div className="text-sm font-semibold text-gray-900">{activeWorkBlock.title}</div>
                              <div className="text-xs text-gray-500">
                                {activeWorkBlock.source_app || 'Outside work'} | Track: {activeWorkBlock.track_key}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs uppercase tracking-wide text-indigo-700">Live Timer</div>
                              <div className="text-lg font-semibold text-indigo-900">{formatDuration(activeWorkElapsedSeconds)}</div>
                              <div className="text-xs text-gray-500">Logged minutes: {activeWorkBlock.actual_minutes}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <textarea
                              className="border rounded px-3 py-2 text-sm"
                              placeholder="Evidence note..."
                              value={blockEvidenceNote}
                              onChange={(event) => setBlockEvidenceNote(event.target.value)}
                            />
                            <input
                              className="border rounded px-3 py-2 text-sm"
                              placeholder="Evidence link..."
                              value={blockEvidenceLink}
                              onChange={(event) => setBlockEvidenceLink(event.target.value)}
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <input
                              type="number"
                              min={1}
                              className="w-24 border rounded px-2 py-1 text-sm"
                              value={minutesDelta}
                              onChange={(event) => setMinutesDelta(Number(event.target.value))}
                            />
                            <button
                              type="button"
                              className="px-3 py-1 text-sm border rounded"
                              onClick={addExternalWorkMinutes}
                              disabled={externalWorkActionLoading}
                            >
                              Add Minutes
                            </button>
                            <button
                              type="button"
                              className="px-3 py-1 text-sm border rounded"
                              onClick={saveExternalWorkEvidence}
                              disabled={externalWorkActionLoading}
                            >
                              Save Evidence
                            </button>
                            <button
                              type="button"
                              className="px-3 py-1 text-sm border rounded border-amber-300 text-amber-700"
                              onClick={handlePauseWork}
                              disabled={externalWorkActionLoading}
                            >
                              Pause Session
                            </button>
                            <button
                              type="button"
                              className="px-3 py-1 text-sm bg-emerald-600 text-white rounded"
                              onClick={handleCompleteWork}
                              disabled={externalWorkActionLoading}
                            >
                              Complete Session
                            </button>
                          </div>
                          <div className="rounded border border-slate-200 p-2">
                            <div className="text-xs font-semibold text-gray-700 mb-2">Switch to Next Work Session</div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                              <select
                                className="border rounded px-2 py-1 text-sm"
                                value={switchTaskId}
                                onChange={(event) => setSwitchTaskId(event.target.value)}
                              >
                                <option value="">Type different work below</option>
                                {openExternalTasks.map((entry) => (
                                  <option key={entry.id} value={entry.id}>
                                    {entry.title} ({entry.source_app || 'Outside work'})
                                  </option>
                                ))}
                              </select>
                              <input
                                className="border rounded px-2 py-1 text-sm"
                                placeholder="What are you switching to?"
                                value={switchTitle}
                                onChange={(event) => setSwitchTitle(event.target.value)}
                              />
                              <input
                                className="border rounded px-2 py-1 text-sm"
                                placeholder="Where are you working?"
                                value={switchSource}
                                onChange={(event) => setSwitchSource(event.target.value)}
                              />
                              <input
                                type="number"
                                min={1}
                                className="border rounded px-2 py-1 text-sm"
                                value={switchPlannedMinutes}
                                onChange={(event) => setSwitchPlannedMinutes(Number(event.target.value))}
                              />
                            </div>
                            <button
                              type="button"
                              className="mt-2 px-3 py-1 text-sm bg-indigo-600 text-white rounded"
                              onClick={switchExternalWorkBlock}
                              disabled={externalWorkActionLoading}
                            >
                              Switch Session
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded border border-slate-200 bg-white p-3 space-y-2">
                          <div className="text-xs font-semibold text-gray-700">Start Work Session</div>
                          <div className="rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-gray-800">
                            <div className="font-semibold">{planWorkSession.title}</div>
                            <div className="text-xs text-gray-600">
                              {planWorkSession.sourceApp
                                ? `Where: ${planWorkSession.sourceApp}`
                                : `Focus: ${planWorkSession.focus}`}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="px-3 py-1 text-sm bg-indigo-600 text-white rounded"
                            onClick={handleStartWorkBlock}
                            disabled={externalWorkActionLoading || loadingExternalWork}
                          >
                            {externalWorkActionLoading ? 'Starting...' : 'Start Work Session'}
                          </button>
                          <details className="rounded border border-slate-200 p-2">
                            <summary className="cursor-pointer text-xs font-semibold text-gray-700">
                              Choose existing work instead
                            </summary>
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                              <select
                                className="border rounded px-2 py-1 text-sm"
                                value={startWorkTaskId}
                                onChange={(event) => setStartWorkTaskId(event.target.value)}
                              >
                                <option value="">Choose existing work</option>
                                {openExternalTasks.map((entry) => (
                                  <option key={entry.id} value={entry.id}>
                                    {entry.title} ({entry.source_app || 'Outside work'})
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                min={1}
                                className="border rounded px-2 py-1 text-sm"
                                value={startWorkPlannedMinutes}
                                onChange={(event) => setStartWorkPlannedMinutes(Number(event.target.value))}
                              />
                            </div>
                            <button
                              type="button"
                              className="mt-2 px-3 py-1 text-sm border rounded"
                              onClick={startExternalWorkBlock}
                              disabled={externalWorkActionLoading || loadingExternalWork || !startWorkTaskId}
                            >
                              Start Selected Work
                            </button>
                          </details>
                        </div>
                      )}

                      <div className="rounded border border-slate-200 bg-white p-3">
                        <div className="text-xs font-semibold text-gray-700 mb-2">Recent Work Events</div>
                        {externalWorkEvents.length === 0 ? (
                          <div className="text-xs text-gray-500">No work events yet.</div>
                        ) : (
                          <div className="space-y-1 max-h-36 overflow-auto">
                            {externalWorkEvents.map((event) => (
                              <div key={event.id} className="text-xs text-gray-600">
                                <span className="font-medium text-gray-800">{externalEventLabel(event)}</span>
                                {' '}• {formatDateTime(event.created_at)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {(goalCycle?.external_tasks || []).map((entry) => (
                        <div key={entry.id} className="border rounded p-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{entry.title}</div>
                            <div className="text-xs text-gray-500">{entry.source_app || 'Outside work'} | {entry.status}</div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className="px-2 py-1 text-xs border rounded"
                              onClick={() => updateExternalStatus(entry.id, 'in_progress')}
                            >
                              In Progress
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 text-xs border rounded border-indigo-300 text-indigo-700"
                              onClick={() => startExternalWorkFromTask(entry.id, entry.title, entry.source_app || '')}
                              disabled={externalWorkActionLoading || Boolean(activeWorkBlock)}
                            >
                              Start Session
                            </button>
                            <button
                              type="button"
                              className="px-2 py-1 text-xs bg-emerald-600 text-white rounded"
                              onClick={() => updateExternalStatus(entry.id, 'completed')}
                            >
                              Complete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div></>}
                  </div>
                )}

                {goalCycle?.status === 'checked_in' && (
                  <div className="mt-4 border-t pt-4">
                    <h3 className="text-md font-semibold text-gray-900">{seniorPriorityWorkflow ? 'Step 3: Required Daily Wrap Up' : 'End-of-Day Check-Out'}</h3>
                    {seniorPriorityWorkflow && <p className="mt-1 text-sm text-gray-600">Complete this before leaving class every day. Record the result and show evidence of your progress.</p>}
                    <div className="mt-3 rounded border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-gray-800">
                      <div><span className="font-semibold">{seniorPriorityWorkflow ? "Today's Priority:" : 'Target:'}</span> {currentDailyPlan.target || currentDailyPlan.legacyCompletionText || '(not set)'}</div>
                      <div className="mt-1"><span className="font-semibold">{seniorPriorityWorkflow ? 'End-of-class outcome:' : 'Success Check:'}</span> {currentDailyPlan.successCriteria || '(not set)'}</div>
                    </div>
                    {!seniorPriorityWorkflow && goalCycle?.primary_goal && dailyGoalAnchor && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-900">Did today help you move toward your long-term goal?</p>
                        <p className="mt-1 text-xs text-gray-500">Goal: {dailyGoalAnchor.title}</p>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {([
                            ['yes', 'Yes'],
                            ['no', 'No'],
                          ] as const).map(([value, label]) => (
                            <button
                              key={value}
                              type="button"
                              className={`border rounded px-3 py-2 text-sm font-semibold ${
                                movedTowardPrimaryGoal === value
                                  ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                                  : 'border-slate-200 bg-white text-slate-600'
                              }`}
                              onClick={() => setMovedTowardPrimaryGoal(value)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-3">
                      {seniorPriorityWorkflow ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">Main Goal Result</p>
                          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {([
                              ['met', 'Met'],
                              ['partially_met', 'Partially Met'],
                              ['blocked', 'Blocked'],
                              ['changed_authentic_work', 'Changed Because of Authentic Work'],
                              ['ready_for_review', 'Ready for Review'],
                            ] as const).map(([value, label]) => (
                              <button
                                key={value}
                                type="button"
                                className={`border rounded px-3 py-2 text-sm font-semibold ${priorityResult === value ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-white text-slate-600'}`}
                                onClick={() => setPriorityResult(value)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <label className="mt-3 block text-sm font-medium text-gray-800">
                            Evidence of Today&apos;s Progress
                            <span className="mt-1 block text-xs font-normal text-gray-600">In 2–4 sentences, explain what you completed or changed and where your teacher can see the proof. If blocked, explain what stopped you.</span>
                            <textarea className="mt-2 min-h-36 w-full rounded border px-3 py-2" placeholder="Describe what you completed, changed, or tried today." value={pathwayEvidenceText} onChange={(event) => setPathwayEvidenceText(event.target.value)} />
                          </label>
                          <label className="mt-3 block text-sm font-medium text-gray-800">
                            Link to Your Evidence (optional)
                            <span className="mt-1 block text-xs font-normal text-gray-600">Google Doc or Drive file, GitHub commit, Canva design, portfolio page, or submitted assignment. Leave blank when the proof is already visible to your teacher.</span>
                            <input className="mt-2 w-full rounded border px-3 py-2" type="url" placeholder="https://docs.google.com/... or another shareable link" value={pathwayEvidenceUrl} onChange={(event) => setPathwayEvidenceUrl(event.target.value)} />
                          </label>
                          {goalCycle?.primary_goal && <WorkEvidencePanel goalId={Number(goalCycle.primary_goal)} sourceKey={`daily:${goalCycle.id}:pathway`} />}
                          {personalProjectGoal && (
                            <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
                              <p className="text-sm font-semibold text-gray-900">Project Result</p>
                              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {([
                                  ['met', 'Met'], ['partially_met', 'Partially Met'], ['blocked', 'Blocked / No Progress'],
                                  ['changed_authentic_work', 'Changed Because of Authentic Work'], ['ready_for_review', 'Ready for Review'],
                                ] as const).map(([value, label]) => (
                                  <button key={value} type="button" className={`rounded border px-3 py-2 text-sm font-semibold ${projectResult === value ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-slate-200 bg-white text-slate-600'}`} onClick={() => setProjectResult(value)}>{label}</button>
                                ))}
                              </div>
                              <textarea className="mt-2 w-full rounded border px-3 py-2" placeholder="Project evidence or blocked explanation" value={projectEvidenceText} onChange={(event) => setProjectEvidenceText(event.target.value)} />
                              <input className="mt-2 w-full rounded border px-3 py-2" type="url" placeholder="Project evidence link (optional)" value={projectEvidenceUrl} onChange={(event) => setProjectEvidenceUrl(event.target.value)} />
                              {goalCycle && personalProjectGoal && <WorkEvidencePanel goalId={personalProjectGoal.id} sourceKey={`daily:${goalCycle.id}:project`} title="Project screenshots" />}
                              <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={projectPortfolioCandidate} onChange={(event) => setProjectPortfolioCandidate(event.target.checked)} /> Portfolio candidate</label>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                      <p className="text-sm font-medium text-gray-900">Did you meet your Target and Success Check?</p>
                      <div className="mt-2 grid grid-cols-3 gap-2">
                      {([
                        ['yes', 'Yes'],
                        ['partial', 'Partial'],
                        ['no', 'No'],
                      ] as const).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={`border rounded px-3 py-2 text-sm font-semibold ${
                            checkoutGoalStatus === value
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                              : 'border-slate-200 bg-white text-slate-600'
                          }`}
                          onClick={() => setCheckoutGoalStatus(value)}
                        >
                          {label}
                        </button>
                      ))}
                      </div>
                        </>
                      )}
                    </div>
                    {!seniorPriorityWorkflow && <textarea
                      className="mt-2 w-full border rounded px-3 py-2"
                      placeholder="Why? How did it go with your Target and Success Check?"
                      value={checkoutReflection}
                      onChange={(event) => setCheckoutReflection(event.target.value)}
                    />}
                    <input
                      className="mt-2 w-full border rounded px-3 py-2"
                      placeholder="What will you do next? (optional)"
                      value={checkoutNextStep}
                      onChange={(event) => setCheckoutNextStep(event.target.value)}
                    />
                    {!seniorPriorityWorkflow && <details className="mt-2 rounded border border-slate-200 bg-slate-50 p-3">
                      <summary className="cursor-pointer text-sm font-medium text-gray-800">Optional ratings</summary>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <label className="text-sm text-gray-700">
                          Completion Rating (1-5)
                          <input
                            type="number"
                            min={1}
                            max={5}
                            className="mt-1 w-full border rounded px-3 py-2"
                            value={completionRating}
                            onChange={(event) => setCompletionRating(Number(event.target.value))}
                          />
                        </label>
                        <label className="text-sm text-gray-700">
                          Confidence Rating (1-5)
                          <input
                            type="number"
                            min={1}
                            max={5}
                            className="mt-1 w-full border rounded px-3 py-2"
                            value={confidenceRating}
                            onChange={(event) => setConfidenceRating(Number(event.target.value))}
                          />
                        </label>
                      </div>
                    </details>}
                    <button
                      type="button"
                      className="mt-3 px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold"
                      onClick={submitCheckOut}
                      disabled={submittingCheckOut}
                    >
                      {submittingCheckOut ? 'Submitting...' : seniorPriorityWorkflow ? 'Finish Wrap Up' : 'Submit Check-Out'}
                    </button>
                  </div>
                )}

                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-md font-semibold text-gray-900">{seniorPriorityWorkflow ? 'Progress History' : 'Goal History'}</h3>
                    {activeSection && (
                      <button
                        type="button"
                        className="px-2 py-1 text-xs border border-slate-300 rounded text-slate-700"
                        onClick={() => loadGoalHistory(activeSection)}
                        disabled={loadingGoalHistory}
                      >
                        {loadingGoalHistory ? 'Refreshing...' : 'Refresh'}
                      </button>
                    )}
                  </div>
                  {loadingGoalHistory ? (
                    <div className="mt-2 text-sm text-gray-500">Loading history...</div>
                  ) : goalHistory.length === 0 ? (
                    <div className="mt-2 text-sm text-gray-500">{seniorPriorityWorkflow ? 'No progress history yet.' : 'No goal history yet.'}</div>
                  ) : (
                    <div className="mt-2 space-y-2 max-h-72 overflow-auto">
                      {goalHistory.map((cycle) => {
                        const historyPlan = structuredPlanFromCycle(cycle);
                        const learningGoal = (cycle.goals || []).find((goal) => goal.goal_type === 'learning');
                        const completionGoal = (cycle.goals || []).find((goal) => goal.goal_type === 'completion');
                        return (
                          <div key={cycle.id} className="border rounded p-2 bg-slate-50">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-medium text-gray-900">
                                {formatDate(cycle.local_school_date)}
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cycleStatusClass(cycle.status)}`}>
                                {String(cycle.status || 'not_checked_in').replace('_', ' ')}
                              </span>
                            </div>
                            {historyPlan.hasStructuredPlan ? (
                              <div className="mt-1 grid gap-1 text-xs text-gray-700">
                                <span><span className="font-semibold">Track:</span> {trackLabel(historyPlan.track)}</span>
                                <span><span className="font-semibold">Target:</span> {historyPlan.target || '(not set)'}</span>
                                <span><span className="font-semibold">First step:</span> {historyPlan.firstStep || '(not set)'}</span>
                                <span><span className="font-semibold">Success Check:</span> {historyPlan.successCriteria || '(not set)'}</span>
                                {cycle.reflection_status && (
                                  <span><span className="font-semibold">Outcome:</span> {String(cycle.reflection_status).replace(/_/g, ' ')}</span>
                                )}
                              </div>
                            ) : (
                              <>
                                <div className="mt-1 text-xs text-gray-700">
                                  <span className="font-semibold">Learning:</span>{' '}
                                  {learningGoal?.smart_text || '(not set)'}
                                </div>
                                <div className="mt-1 text-xs text-gray-700">
                                  <span className="font-semibold">Completion:</span>{' '}
                                  {completionGoal?.smart_text || '(not set)'}
                                </div>
                              </>
                            )}
                            {cycle.reflection_text && (
                              <div className="mt-1 text-xs text-gray-700">
                                <span className="font-semibold">Reflection:</span>{' '}
                                {cycle.reflection_text}
                              </div>
                            )}
                            {cycle.status === 'checked_out' && (
                              <div className="mt-1 text-[11px] text-gray-500">
                                Completion {cycle.completion_rating || '-'} / 5 | Confidence {cycle.confidence_rating || '-'} / 5
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )
      )}

    </>
  );

  return (
    <OptionalAppShell enabled={redesignEnabled} role="student" title="My Classroom" navItems={dashboardNavItems}>
      <div className={redesignEnabled ? 'space-y-4' : 'max-w-5xl mx-auto p-6'}>
        {redesignEnabled ? (
          <PageHeader
            eyebrow="Student Workspace"
            title="My Classroom"
            subtitle="See what matters today, continue your work, and track progress."
            actions={currentSection ? <Badge variant="muted">{currentSection.name}</Badge> : undefined}
          />
        ) : (
          <h1 className="text-2xl font-semibold text-gray-900">My Classroom</h1>
        )}

        {redesignEnabled && sectionSwitcher}
        {redesignEnabled && pendingReviewGoals.length > 0 && !reviewingGoal && (
          <section aria-labelledby="pending-five-week-reflection" className="rounded-2xl border border-warning/40 bg-warning/10 p-5 shadow-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Reflection due</p>
            <h2 id="pending-five-week-reflection" className="mt-2 text-lg font-semibold text-text">
              {pendingReviewGoals[0].instructional_cycle_detail?.title || 'Your previous five-week goal'} reflection is ready
            </h2>
            <p className="mt-2 text-sm text-muted">
              Your five-week period ended {formatDate(pendingReviewGoals[0].end_date)}. Reflect on the work you completed before continuing with the new cycle.
            </p>
            <Button type="button" className="mt-4" onClick={() => openFiveWeekReflection(pendingReviewGoals[0])}>
              Complete {pendingReviewGoals[0].instructional_cycle_detail?.title || 'five-week'} reflection
            </Button>
          </section>
        )}

        {redesignEnabled && activeTab === 'today' && (
          <div className="flex justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => setActiveTab('past')}>History</Button>
          </div>
        )}
        {redesignEnabled && activeTab === 'past' && (
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold">History</h2>
            <Button type="button" variant="secondary" onClick={() => setActiveTab('today')}>Back to Today</Button>
          </div>
        )}
        {redesignEnabled && activeTab === 'portfolio' && (
          <SectionCard title="Prepare evidence for Headrush" meta="Choose work that shows your learning.">
            <p className="mb-2 text-sm text-muted">Upload your selected evidence to Headrush separately. Headrush remains the official submission and assessment record.</p>
            <Link className="text-sm text-brand underline" to={`/v2/student/work-review${currentSection ? `?section=${currentSection.id}` : ''}`}>Review my submitted work</Link>
          </SectionCard>
        )}

        {redesignEnabled && activeTab === 'today' && currentSection && (!dailyWorkFirst || seniorGoalSetupRequired) && (
          <section aria-labelledby="student-next-step" className="rounded-2xl border border-brand/30 bg-brand/5 p-5 shadow-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Your next step · {currentSection.name}</p>
            <h2 id="student-next-step" className="mt-2 text-xl font-semibold text-text">
              {loadingGoals ? 'Loading your work…'
                : seniorGoalSetupRequired ? 'Create your five-week goal'
                  : gateRequired ? 'Complete your daily plan'
                    : goalCycle?.status === 'checked_in' && currentDailyPlan.target ? currentDailyPlan.target
                      : activeRun ? activeRun.lesson_plan_title || 'Live lesson available'
                        : goalCycle?.status === 'checked_out' ? 'Today’s plan is wrapped up'
                          : 'Choose your next task'}
            </h2>
            {!loadingGoals && (
              <>
                <p className="mt-2 text-sm text-text">
                  {seniorGoalSetupRequired ? 'Set up your Main Goal first, then choose today’s work.'
                    : gateRequired ? 'Save the required plan before opening work or a live lesson.'
                      : goalCycle?.status === 'checked_in' && currentDailyPlan.target
                        ? `Start here: ${currentDailyPlan.firstStep || 'Open your work controls to begin or resume your saved task.'}`
                        : activeRun ? 'Open the lesson to see your saved step and its instructions.'
                          : 'Open Assignments & Lessons to choose an activity, or ask your teacher what to work on next.'}
                </p>
                {!seniorGoalSetupRequired && !gateRequired && goalCycle?.status === 'checked_in' && currentDailyPlan.target && (
                  <p className="mt-2 text-sm text-text"><span className="font-semibold">Done when: </span>{currentDailyPlan.successCriteria || 'Check the task instructions or ask your teacher what to show.'}</p>
                )}
                <div className="mt-4 flex flex-wrap gap-3">
                  {seniorGoalSetupRequired || gateRequired || (goalsFeatureEnabled && goalCycle?.status === 'checked_in' && currentDailyPlan.target) ? (
                    <Button type="button" onClick={() => {
                      const target = document.getElementById(seniorGoalSetupRequired ? 'student-goal-setup' : 'student-daily-work');
                      target?.focus();
                      target?.scrollIntoView?.({ block: 'start' });
                    }}>{seniorGoalSetupRequired ? 'Go to goal setup' : gateRequired ? 'Go to daily plan' : 'Open current work'}</Button>
                  ) : activeRun ? (
                    <Link className="inline-flex items-center rounded-md bg-brand px-4 py-2 text-sm font-semibold text-brand-foreground" to={`/v2/run/${activeRun.id}`}>Open live lesson</Link>
                  ) : (
                    <Button type="button" onClick={() => setActiveTab('work')}>View assignments &amp; lessons</Button>
                  )}
                  <Button type="button" variant="secondary" onClick={() => setActiveTab('help')}>Get help</Button>
                </div>
              </>
            )}
          </section>
        )}


        {dailyWorkFirst && dailyWorkPanel}

        {(activeTab === 'today' || sections.length === 0) && <details
          className={redesignEnabled ? 'rounded-2xl border border-border bg-surface px-4 py-3 shadow-card' : 'mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3'}
          open={sections.length === 0 ? true : undefined}
        >
          <summary className="cursor-pointer text-sm font-semibold text-text">
            {sections.length ? 'Join another class' : 'Join a class'}
            {sections.length > 0 && <span className="ml-2 font-normal text-muted">Use a class code</span>}
          </summary>
        {redesignEnabled ? (
          <SectionCard
            title={sections.length ? 'Join Another Class' : 'Join a Class'}
            meta="Enter the class code from your teacher."
          >
            <form className="flex flex-col gap-3 sm:flex-row sm:items-start" onSubmit={handleJoinSectionByCode}>
              <div className="min-w-0 flex-1">
                <label htmlFor="student-join-code" className="sr-only">Class code</label>
                <Input
                  id="student-join-code"
                  value={joinCode}
                  onChange={(event) => {
                    setJoinCode(event.target.value.toUpperCase());
                    setJoinSectionError('');
                    setJoinSectionSuccess('');
                  }}
                  placeholder="ABC123"
                  maxLength={10}
                  className="font-mono uppercase"
                  disabled={joiningSection}
                />
                {joinGradeRequired && (
                  <div className="mt-3">
                    <label htmlFor="student-join-grade" className="mb-1 block text-sm font-medium text-text">
                      Current grade
                    </label>
                    <Select
                      id="student-join-grade"
                      value={joinGrade}
                      onChange={(event) => {
                        setJoinGrade(event.target.value);
                        setJoinSectionError('');
                      }}
                      disabled={joiningSection}
                    >
                      <option value="">Select your grade</option>
                      {['6th', '7th', '8th', '9th', '10th', '11th', '12th'].map((grade) => (
                        <option key={grade} value={grade}>{grade} Grade</option>
                      ))}
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">We use this to show the correct goals for your grade.</p>
                  </div>
                )}
                {joinSectionError && <p className="mt-2 text-sm text-danger">{joinSectionError}</p>}
                {joinSectionSuccess && <p className="mt-2 text-sm text-success">{joinSectionSuccess}</p>}
              </div>
              <Button type="submit" loading={joiningSection} disabled={!joinCode.trim() || (joinGradeRequired && !joinGrade)}>
                Join Class
              </Button>
            </form>
          </SectionCard>
        ) : (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-sm font-medium text-gray-900">
              {sections.length ? 'Join Another Class' : 'Join a Class'}
            </p>
            <p className="mt-1 text-sm text-gray-500">Enter the class code from your teacher.</p>
            <form className="mt-3 flex flex-col gap-3 sm:flex-row" onSubmit={handleJoinSectionByCode}>
              <input
                id="student-join-code"
                value={joinCode}
                onChange={(event) => {
                  setJoinCode(event.target.value.toUpperCase());
                  setJoinSectionError('');
                  setJoinSectionSuccess('');
                }}
                placeholder="ABC123"
                maxLength={10}
                className="w-full rounded border px-3 py-2 font-mono text-sm uppercase"
                disabled={joiningSection}
              />
              {joinGradeRequired && (
                <div className="min-w-48">
                  <label htmlFor="student-join-grade-legacy" className="mb-1 block text-sm font-medium text-gray-700">
                    Current grade
                  </label>
                  <select
                    id="student-join-grade-legacy"
                    value={joinGrade}
                    onChange={(event) => {
                      setJoinGrade(event.target.value);
                      setJoinSectionError('');
                    }}
                    className="w-full rounded border px-3 py-2 text-sm"
                    disabled={joiningSection}
                  >
                    <option value="">Select your grade</option>
                    {['6th', '7th', '8th', '9th', '10th', '11th', '12th'].map((grade) => (
                      <option key={grade} value={grade}>{grade} Grade</option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="submit"
                className="rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-60"
                disabled={joiningSection || !joinCode.trim() || (joinGradeRequired && !joinGrade)}
              >
                {joiningSection ? 'Joining...' : 'Join Class'}
              </button>
            </form>
            {joinSectionError && <p className="mt-2 text-sm text-red-600">{joinSectionError}</p>}
            {joinSectionSuccess && <p className="mt-2 text-sm text-green-700">{joinSectionSuccess}</p>}
          </div>
        )}
        </details>}

        {redesignEnabled && activeTab === 'today' && (
          <details className="space-y-3">
            <summary className="cursor-pointer text-sm font-semibold text-muted">Class summary</summary>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-border bg-surface px-4 py-3 text-sm shadow-card">
              <span><span className="font-semibold text-text">Current focus:</span> <span className="text-muted">{currentFocusLabel}</span></span>
              <span><span className="font-semibold text-text">Unfinished work:</span> <span className="text-muted">{activeAssignmentCount}</span></span>
              <span><span className="font-semibold text-text">Review Ready:</span> <span className="text-muted">{reviewRuns.length}</span></span>
              <span><span className="font-semibold text-text">Active Hooks:</span> <span className="text-muted">{activeHookCount}</span></span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setActiveTab('help')}
            >
              Stuck? {'\u2192'} Go to Help
            </Button>
          </details>
        )}
        {!redesignEnabled && sectionSwitcher}

      {activeTab === 'today' && seniorGoalSetupRequired && <div id="student-goal-setup" tabIndex={-1} aria-label="Goal setup" className="scroll-mt-28" />}
      {activeTab === 'today' && primaryGoalFeatureEnabled && seniorPriorityWorkflow && (
        seniorGoalSetupRequired ? (
          <SectionCard
            title="Step 1 of 2: Create your five-week goal"
            meta="Today's Work becomes available after you create your Main Goal. You only do this once for the five-week period."
            className="border-warning/30"
          >
            <div className="space-y-4">
              <div className="rounded-xl border border-warning/25 bg-warning/10 px-4 py-3 text-sm text-text">
                <p className="font-semibold">Why this comes first</p>
                <p className="mt-1 text-muted">Your five-week goal gives each daily plan a clear direction. After it is saved, you will return here to choose today's task and the proof you will show by the end of class.</p>
              </div>
              <div className="rounded-xl border border-border bg-surface-alt/50 px-4 py-3 text-sm text-text">
                <p><span className="font-semibold">Tip:</span> Describe what you want to be able to do or prove after five weeks—not every assignment you will complete.</p>
                <p className="mt-2 text-muted"><span className="font-semibold text-text">Example:</span> “Diagnose common computer hardware problems using a repeatable troubleshooting process, and prove it with completed repair records.”</p>
              </div>
              <Button type="button" onClick={() => setActiveTab('goals')}>Create My Five-Week Goal</Button>
            </div>
          </SectionCard>
        ) : requiredPathwayGoal ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm">
            <div>
              <span className="font-semibold text-text">Five-week goal:</span>{' '}
              <span className="text-muted">{requiredPathwayGoal.title}</span>
            </div>
            <Button type="button" size="sm" variant="ghost" onClick={() => setActiveTab('goals')}>View goals</Button>
          </div>
        ) : null
      )}

      {(activeTab === 'today' || activeTab === 'work') && primaryGoalFeatureEnabled && seniorPriorityWorkflow
        && requiredPathwayGoal?.is_active
        && requiredPathwayGoal.quality_workflow && !requiredPathwayGoal.quality_workflow.gate_satisfied && (
          <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm">
            <p className="font-semibold text-text">{requiredPathwayGoal.quality_workflow.state === 'needs_revision'
              ? 'Your teacher requested a revision to your five-week goal.'
              : 'Your five-week goal is waiting for teacher review.'}</p>
            <p className="mt-1 text-muted">You can save your daily plan and keep working today. Review your teacher’s feedback or ask for help.</p>
            <Button type="button" size="sm" variant="secondary" className="mt-3" onClick={() => {
              selectFiveWeekGoalTrack('required_pathway');
              setActiveTab('goals');
            }}>Review my five-week goal</Button>
          </div>
        )}

      {!redesignEnabled && activeTab === 'today' && primaryGoalFeatureEnabled && longerTermDirectionPanel}
      {activeTab === 'goals' && goalDraft.banner}
      {activeTab === 'goals' && primaryGoalFeatureEnabled && activeSection && <GoalConference sectionId={activeSection} onActivated={() => void loadGoals(activeSection)} />}
      {(activeTab === 'goals' || (!redesignEnabled && activeTab === 'today')) && primaryGoalFeatureEnabled && (primaryGoal || selectedGoalTrack === 'personal_project' || !activePerformanceCycle) && primaryGoalPanel}

      {activeTab === 'goals' && primaryGoalFeatureEnabled && (
        redesignEnabled && seniorPriorityWorkflow ? (
          <details className="rounded-xl border border-border bg-surface p-4" open={directionEditing || undefined}>
            <summary className="cursor-pointer text-sm font-medium text-muted">Longer-term direction — optional</summary>
            <div className="mt-3">{longerTermDirectionPanel}</div>
          </details>
        ) : longerTermDirectionPanel
      )}
      {redesignEnabled && activeTab === 'goals' && (
        <div className="text-sm text-muted">
          <Link className="text-brand underline" to={`/v2/student/coaching${currentSection ? `?section=${currentSection.id}` : ''}`}>View teacher feedback and check-ins</Link>
          {!showGoalTools && <p className="mt-2">Goal planning is not enabled for this class.</p>}
        </div>
      )}
      {!redesignEnabled && activeTab === 'today' && <details className="rounded-xl border border-border bg-surface p-4"><summary className="cursor-pointer font-semibold">Edit milestone plans and dates</summary>{milestonePanel}</details>}
      {activeTab === 'portfolio' && primaryGoal?.instructional_cycle && <WorkEvidencePanel key={primaryGoal.id} goalId={primaryGoal.id} portfolio={{
        goal: { title: primaryGoal.title, why: primaryGoal.why_text, target: primaryGoal.target_metric_text, start: primaryGoal.instructional_cycle_detail?.start_date || primaryGoal.start_date, end: primaryGoal.instructional_cycle_detail?.end_date || primaryGoal.end_date, editable: milestonesEditable, conferenceManaged: primaryGoal.conference_managed },
        milestones, refresh: () => refreshMilestones(primaryGoal.id), downloads: technicalEvidencePanel,
        onToday: () => setActiveTab('today'), todaySourceKey: goalCycle?.primary_goal === primaryGoal.id ? `daily:${goalCycle.id}:pathway` : undefined,
      }} />}
      {!redesignEnabled && activeTab === 'today' && <details className="rounded-xl border border-border bg-surface p-4"><summary className="cursor-pointer font-semibold">Assessment records and downloads</summary>{technicalEvidencePanel}</details>}
      {(redesignEnabled ? activeTab === 'portfolio' : (activeTab === 'goals' || activeTab === 'today')) && (redesignEnabled ? <details className="rounded-xl border border-border bg-surface p-4"><summary className="cursor-pointer font-semibold">Optional work habits reflection</summary><p className="mt-2 text-sm text-muted">Reflect on how you learn and work with others. This is separate from your five-week goal reflection.</p><div className="mt-4">{nsrPanel}</div></details> : nsrPanel)}
      {(activeTab === 'goals' || (!redesignEnabled && activeTab === 'today')) && instructionalCheckInPanel}

      {redesignEnabled && showGoalTools && activeTab === 'portfolio' && !primaryGoal?.instructional_cycle && (
        <SectionCard title="Portfolio" meta="Your Hook milestones, evidence, verification, and HeadRush export will appear here.">
          <p className="text-sm text-muted">Create or connect a five-week pathway goal to begin building your portfolio.</p>
        </SectionCard>
      )}

      {activeTab === 'today' && !seniorGoalSetupRequired && (!seniorPriorityWorkflow || goalCycle?.status !== 'checked_in') && dailyNudgePanel}

      {((redesignEnabled && activeTab === 'today' && weeklyCheckpointAvailability?.available === true) || (!redesignEnabled && activeTab === 'today')) && weeklyCheckpointFeatureEnabled && weeklyCheckpointPanel}

      {!dailyWorkFirst && dailyWorkPanel}

      {activeTab === 'help' && (
        redesignEnabled ? (
          <SectionCard
            title="Help"
            meta="Get help in one step."
          >
            <HelpTab
              runId={activeRun?.id ?? null}
              stepKey={`${activeRun?.id ?? 'none'}:${activeSection ?? 'none'}:${goalCycle?.id ?? 'none'}`}
              supportMode="fallback"
              onGoToLesson={() => {
                if (activeRun?.id) {
                  if (previewLocation()) { previewNotice('Opening live lessons is not available in the dashboard preview.'); return; }
                  window.location.assign(`/v2/run/${activeRun.id}`);
                  return;
                }
                setActiveTab('today');
              }}
            />
          </SectionCard>
        ) : (
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5">
            <HelpTab
              runId={activeRun?.id ?? null}
              stepKey={`${activeRun?.id ?? 'none'}:${activeSection ?? 'none'}:${goalCycle?.id ?? 'none'}`}
              supportMode="fallback"
              onGoToLesson={() => {
                if (activeRun?.id) {
                  if (previewLocation()) { previewNotice('Opening live lessons is not available in the dashboard preview.'); return; }
                  window.location.assign(`/v2/run/${activeRun.id}`);
                  return;
                }
                setActiveTab('today');
              }}
            />
          </div>
        )
      )}

      {activeTab === 'today' && (activeRun ? (
        <div className={cn(
          redesignEnabled ? 'rounded-2xl border border-brand/20 bg-[linear-gradient(135deg,rgba(219,234,254,0.75),rgba(255,255,255,0.98))] shadow-card p-6 flex flex-col gap-4' : 'mt-6 bg-white rounded-lg border border-indigo-100 shadow-sm p-5 flex flex-col gap-3',
          dailyGoalLocked && 'opacity-60 saturate-50',
        )}
        >
          <div>
            <p className={redesignEnabled ? 'text-xs uppercase tracking-[0.18em] text-brand' : 'text-xs uppercase tracking-wide text-indigo-500'}>Live Lesson</p>
            <h2 className={redesignEnabled ? 'mt-2 text-xl font-semibold text-text' : 'text-lg font-semibold text-gray-900'}>Join Live Lesson</h2>
            <p className={redesignEnabled ? 'mt-1 text-sm text-muted' : 'text-sm text-gray-600'}>This opens the live presentation your teacher is running.</p>
          </div>
          {fixQueuePreview && fixQueuePreview.unresolved > 0 ? (
            <div className={redesignEnabled ? 'rounded-xl border border-warning/25 bg-warning/10 p-4 text-sm text-text' : 'rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'}>
              <p className="font-semibold">
                Fix Queue: {fixQueuePreview.unresolved} item{fixQueuePreview.unresolved === 1 ? '' : 's'}
              </p>
              {fixQueuePreview.items.length > 0 ? (
                <ul className="mt-1 list-disc pl-5 space-y-1">
                  {fixQueuePreview.items.map((item) => (
                    <li key={`fix-preview-${item.id}`}>
                      {String(item.lesson_step_title || 'Lesson step')} {item.suggestions?.[0] ? `- ${item.suggestions[0]}` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {gateRequired ? (
            <div className={redesignEnabled ? 'rounded-xl border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-text' : 'text-sm text-amber-700'}>
              Complete Step 1 to unlock work and live lessons.
            </div>
          ) : (
            redesignEnabled ? (
              <Link
                to={`/v2/run/${activeRun.id}`}
                className="inline-flex h-10 w-fit items-center justify-center rounded-md bg-brand px-4 text-sm font-medium text-brand-foreground shadow-soft transition-colors hover:bg-brand/90"
              >
                Join Live Lesson
              </Link>
            ) : (
              <Link
                to={`/v2/run/${activeRun.id}`}
                className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold w-fit"
              >
                Join Live Lesson
              </Link>
            )
          )}
        </div>
      ) : (
        <div className={redesignEnabled ? 'rounded-2xl border border-border bg-surface-alt/50 px-5 py-4 text-sm text-muted' : 'mt-6 text-sm text-gray-500'}>No live lesson right now.</div>
      ))}

      {activeTab === 'work' && (
      <div className="space-y-4">
      {seniorPriorityWorkflow && (
        <div className={redesignEnabled ? 'rounded-2xl border border-brand/20 bg-brand/5 p-5 shadow-card' : 'mt-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4'}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Current Focus</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{currentFocusText || 'Individual Project'}</p>
              <p className="mt-2 text-sm text-gray-700"><span className="font-semibold">Today:</span> {currentDailyPlan.target}</p>
              <p className="mt-1 text-sm text-gray-700"><span className="font-semibold">Show by class end:</span> {currentDailyPlan.successCriteria}</p>
            </div>
            <Button type="button" size="sm" variant="secondary" onClick={() => setFocusEditing((current) => !current)}>
              {focusEditing ? 'Cancel' : 'Change Focus'}
            </Button>
          </div>
          {focusEditing && (
            <div className="mt-4 grid gap-3 rounded-xl border border-indigo-100 bg-white p-4 sm:grid-cols-[220px_minmax(0,1fr)_auto]">
              <Select value={focusKey} onChange={(event) => setFocusKey(event.target.value)}>
                <option value="comptia">CompTIA</option>
                <option value="individual_project">Individual Project</option>
                <option value="authentic_work">Authentic Work</option>
                <option value="other">Other</option>
              </Select>
              <Input
                aria-label="Current focus detail"
                placeholder="Module, project step, ticket, or task"
                value={focusTitle}
                onChange={(event) => setFocusTitle(event.target.value)}
              />
              <Button type="button" onClick={saveCurrentFocus} disabled={focusSaving || !focusTitle.trim()}>
                {focusSaving ? 'Saving...' : 'Save Focus'}
              </Button>
            </div>
          )}
        </div>
      )}
      {activeRun && (
        <div className={redesignEnabled ? 'rounded-2xl border border-brand/25 bg-surface p-5 shadow-card' : 'rounded-lg border border-indigo-200 bg-white p-4'}>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Live lesson is ready</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{activeRun.lesson_plan_title || 'Live Lesson'}</p>
          <p className="mt-1 text-sm text-gray-600">Your morning direction is saved. Joining the lesson does not start a timer or require another check-in.</p>
          <Link
            to={`/v2/run/${activeRun.id}`}
            className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Join Live Lesson
          </Link>
        </div>
      )}
      <div id="assignments-section" className={cn(
        redesignEnabled ? 'rounded-2xl border border-border bg-surface shadow-card p-6' : 'mt-6 bg-white rounded-lg border border-slate-200 shadow-sm p-5',
        dailyGoalLocked && 'opacity-70',
      )}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className={redesignEnabled ? 'text-xs uppercase tracking-[0.18em] text-muted' : 'text-xs uppercase tracking-wide text-slate-500'}>Independent Work</p>
            <h2 className={redesignEnabled ? 'mt-2 text-xl font-semibold text-text' : 'text-lg font-semibold text-gray-900'}>Assignments and assessments</h2>
            <Link className="mt-2 inline-block text-sm font-semibold text-brand underline" to="/v2/student/units">My next step and unit pathway</Link>
          </div>
        </div>
        {dailyGoalLocked && (
          <div className={redesignEnabled ? 'mt-4 rounded-xl border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-text' : 'mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'}>
            <span className="font-semibold">Step 1: Complete Your Daily Goal.</span> Complete Step 1 to unlock work and live lessons.
          </div>
        )}
        {assignments.length === 0 ? (
          <div className={redesignEnabled ? 'mt-4 rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted' : 'mt-4 text-sm text-gray-500'}>No independent assignments or assessments right now.</div>
        ) : (
          <div className={redesignEnabled ? 'mt-4 space-y-3' : 'mt-4 divide-y divide-slate-100'}>
            {assignments.map((assignment) => (
              gateRequired ? (
                <div
                  key={assignment.assignment_id}
                  className={redesignEnabled ? 'flex flex-col gap-3 rounded-xl border border-border bg-surface-alt/40 px-4 py-4 text-muted' : 'flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-3 text-gray-500 rounded-md px-3'}
                >
                  <div>
                    <div className={redesignEnabled ? 'font-semibold text-text' : 'font-semibold text-gray-700'}>{assignment.lesson_plan_title}</div>
                    <div className={redesignEnabled ? 'text-sm text-muted' : 'text-sm text-gray-500'}>{assignment.section_name}</div>
                    {assignment.purpose === 'assessment' && <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">{assignmentHookLevel(assignment) || ''} assessment</div>}
                    {assignment.purpose === 'makeup' && <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">{assignmentHookLevel(assignment) ? `${assignmentHookLevel(assignment)} · ` : ''}Make-up assignment</div>}
                    {assignment.purpose === 'practice' && <div className="mt-1 text-xs font-semibold text-indigo-700">Unit practice</div>}
                    {assignment.purpose === 'makeup' && assignment.makeup_review?.status === 'needs_revision' && <div className="mt-1 text-xs font-semibold text-amber-800">Revision requested — open for teacher feedback</div>}
                  </div>
                  <div className={redesignEnabled ? 'flex flex-wrap items-center gap-3 text-sm text-muted' : 'flex flex-wrap items-center gap-3 text-sm'}>
                    <span>Assigned {formatDate(assignment.assigned_at)}</span>
                    {assignment.due_at && <span>Due {formatDate(assignment.due_at)}</span>}
                    {redesignEnabled ? (
                      <Badge variant={assignmentStatusBadgeVariant(assignment.student_status)}>
                        {assignment.student_status.replace('_', ' ')}
                      </Badge>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[assignment.student_status]}`}>
                        {assignment.student_status.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <Link
                  key={assignment.assignment_id}
                  to={`/v2/assignments/${assignment.assignment_id}`}
                  className={redesignEnabled ? 'flex flex-col gap-3 rounded-xl border border-border bg-surface px-4 py-4 transition hover:border-brand/30 hover:bg-surface-alt/50' : 'flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-3 transition text-gray-900 hover:bg-slate-50 rounded-md px-3'}
                >
                  <div>
                    <div className={redesignEnabled ? 'font-semibold text-text' : 'font-semibold text-gray-900'}>{assignment.lesson_plan_title}</div>
                    <div className={redesignEnabled ? 'text-sm text-muted' : 'text-sm text-gray-500'}>{assignment.section_name}</div>
                    {assignment.purpose === 'assessment' && <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">{assignmentHookLevel(assignment) || ''} assessment</div>}
                    {assignment.purpose === 'makeup' && <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">{assignmentHookLevel(assignment) ? `${assignmentHookLevel(assignment)} · ` : ''}Make-up assignment</div>}
                    {assignment.purpose === 'practice' && <div className="mt-1 text-xs font-semibold text-indigo-700">Unit practice</div>}
                    {assignment.purpose === 'makeup' && assignment.makeup_review?.status === 'needs_revision' && <div className="mt-1 text-xs font-semibold text-amber-800">Revision requested — open for teacher feedback</div>}
                  </div>
                  <div className={redesignEnabled ? 'flex flex-wrap items-center gap-3 text-sm text-muted' : 'flex flex-wrap items-center gap-3 text-sm text-gray-500'}>
                    <span>Assigned {formatDate(assignment.assigned_at)}</span>
                    {assignment.due_at && <span>Due {formatDate(assignment.due_at)}</span>}
                    {redesignEnabled ? (
                      <Badge variant={assignmentStatusBadgeVariant(assignment.student_status)}>
                        {assignment.student_status.replace('_', ' ')}
                      </Badge>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[assignment.student_status]}`}>
                        {assignment.student_status.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </Link>
              )
            ))}
          </div>
        )}
      </div>
      </div>
      )}

      {activeTab === 'past' && (
      <div className="space-y-4">
      {weeklyCheckpointPastPanel}
      <div className={redesignEnabled ? 'rounded-2xl border border-border bg-surface shadow-card p-6' : 'mt-6 bg-white rounded-lg border border-slate-200 shadow-sm p-5'}>
        <div className="flex items-center justify-between">
          <div>
            <p className={redesignEnabled ? 'text-xs uppercase tracking-[0.18em] text-muted' : 'text-xs uppercase tracking-wide text-slate-500'}>Past Lessons</p>
            <h2 className={redesignEnabled ? 'mt-2 text-xl font-semibold text-text' : 'text-lg font-semibold text-gray-900'}>Review past lesson runs</h2>
          </div>
        </div>
        {reviewRuns.length === 0 ? (
          <div className={redesignEnabled ? 'mt-4 rounded-xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted' : 'mt-4 text-sm text-gray-500'}>No completed lesson runs available yet.</div>
        ) : (
          <div className={redesignEnabled ? 'mt-4 space-y-3' : 'mt-4 divide-y divide-slate-100'}>
            {reviewRuns.map((run) => (
              <Link
                key={run.id}
                to={`/v2/student/run/${run.id}/review`}
                className={redesignEnabled ? 'flex flex-col gap-3 rounded-xl border border-border bg-surface px-4 py-4 transition hover:border-brand/30 hover:bg-surface-alt/50' : 'flex flex-col md:flex-row md:items-center md:justify-between gap-2 py-3 hover:bg-slate-50 rounded px-2'}
              >
                <div>
                  <div className={redesignEnabled ? 'font-semibold text-text' : 'font-semibold text-gray-900'}>
                    {run.lesson_plan_title || `Lesson Run #${run.id}`}
                  </div>
                  <div className={redesignEnabled ? 'text-sm text-muted' : 'text-sm text-gray-500'}>
                    {run.section_name || `Section ${run.section}`} | Started {formatDate(run.started_at)}
                  </div>
                </div>
                {redesignEnabled ? (
                  <Badge variant={cycleStatusBadgeVariant(run.status)}>
                    {String(run.status || 'completed').replace('_', ' ')}
                  </Badge>
                ) : (
                  <div className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 capitalize w-fit">
                    {String(run.status || 'completed').replace('_', ' ')}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
      </div>
      )}

      {(activeTab === 'work' || activeTab === 'past' || (!redesignEnabled && activeTab === 'today')) && (
      <div className={redesignEnabled ? 'space-y-4' : 'mt-6'}>
        {redesignEnabled && (
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted">{activeTab === 'past' ? 'Archived Hooks' : 'Active Hooks'}</p>
            <h2 className="mt-2 text-xl font-semibold text-text">
              {activeTab === 'past' ? 'Past concepts and units' : 'Concepts and units available for current work'}
            </h2>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(activeTab === 'past' ? archivedHooks : activeHooks).map((hook) => (
          <div key={hook.id} className={redesignEnabled ? 'rounded-2xl border border-border bg-surface shadow-card p-5' : 'bg-white rounded shadow p-4'}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className={redesignEnabled ? 'text-lg font-semibold text-text' : 'text-lg font-semibold text-gray-900'}>{hook.title}</h2>
                <p className={redesignEnabled ? 'mt-2 text-sm text-muted' : 'text-sm text-gray-500'}>{hook.description}</p>
              </div>
              {redesignEnabled ? (
                <Badge variant={hook.is_archived ? 'muted' : 'default'}>
                  {hook.is_archived ? 'Archived' : 'Active'}
                </Badge>
              ) : null}
            </div>
          </div>
        ))}
        </div>
        {(activeTab === 'past' ? archivedHooks : activeHooks).length === 0 && (
          <div className="rounded-2xl border border-border bg-surface-alt/50 px-4 py-4 text-sm text-muted">
            {activeTab === 'past' ? 'No archived hooks yet.' : 'No active hooks are assigned right now.'}
          </div>
        )}
      </div>
      )}
      </div>
    </OptionalAppShell>
  );
};
